import { useState, useEffect, useRef, useMemo } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useParams, useNavigate } from "react-router-dom";
import {
  Menu, X, Phone, Mail, MapPin, ChevronRight, ChevronDown, ChevronLeft, ChevronsLeft, ChevronsRight,
  FlaskConical, Microscope, Cpu, Shield,
  FileCheck, Cog, BarChart3, Users, Award, Clock, CheckCircle2, ArrowRight, Linkedin, Twitter,
  Facebook, Pill, Syringe, HeartPulse, Dna, Atom, TestTube2, Stethoscope, Activity, Beaker,
  CircuitBoard, Tablets, Droplets, ShieldCheck, ClipboardCheck, FileSearch, Gauge, Settings2,
  Factory, Thermometer, Database, Server, Lock, Globe2, BadgeCheck, ScrollText, FileText,
  Target, TrendingUp, Building2, Rocket, Star, Quote, Play, ExternalLink, ArrowUpRight,
  Briefcase, GraduationCap, Handshake, Lightbulb, Layers, Network, Workflow, Zap,
  ChevronUp, Hexagon, Triangle, Circle, Square, MessageCircle, Send, Bot, Sparkles, Minimize2,
  Download, Package, Truck, BookOpen, PenTool, Settings, AlertTriangle, Eye, ArrowLeft, Calendar, User
} from "lucide-react";
import axios from 'axios';
import { toast } from './hooks/use-toast';
// Contexts
import { SiteSettingsProvider } from "./contexts/SiteSettingsContext";
import { Helmet } from 'react-helmet-async';
import { Toaster } from "./components/ui/toaster";
import { useSiteSettings } from './contexts/SiteSettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Premium Page Loader Component
const PageLoader = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(timer);
          setTimeout(() => setIsExiting(true), 300);
          setTimeout(() => onComplete(), 800);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 100);
    return () => clearInterval(timer);
  }, [onComplete]);

  return (
    <div className={`fixed inset-0 z-[9999] bg-[#030810] flex flex-col items-center justify-center transition-all duration-500 ${isExiting ? 'opacity-0 scale-110' : 'opacity-100 scale-100'}`}>
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#0369a1]/10 rounded-full blur-[150px] animate-pulse"></div>
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-[#047857]/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '0.5s' }}></div>
      </div>

      {/* Logo Animation - Text-based matching header */}
      <div className="relative mb-8">
        {/* Rotating rings */}
        <div className="absolute -inset-16 border-2 border-[#047857]/30 rounded-full animate-spin-slow" style={{ animationDuration: '4s' }}></div>
        <div className="absolute -inset-24 border border-[#0369a1]/20 rounded-full animate-spin-slow" style={{ animationDuration: '6s', animationDirection: 'reverse' }}></div>
        <div className="absolute -inset-32 border border-dashed border-[#047857]/15 rounded-full animate-spin-slow" style={{ animationDuration: '10s' }}></div>

        {/* Text Logo - Matching header style */}
        <div className="relative flex items-center gap-4">
          {/* UPT - Bold and vibrant */}
          <span
            className="font-black text-7xl animate-pulse"
            style={{
              background: 'linear-gradient(135deg, #0ea5e9 0%, #0369a1 50%, #047857 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textShadow: '0 0 60px rgba(14, 165, 233, 0.5)',
              letterSpacing: '0.05em'
            }}
          >
            UPT
          </span>

          {/* Vertical Divider - Animated gradient */}
          <div
            className="w-1 h-20 rounded-full animate-pulse"
            style={{
              background: 'linear-gradient(to bottom, #0ea5e9, #047857)',
              boxShadow: '0 0 20px rgba(4, 120, 87, 0.5)'
            }}
          />

          {/* United Pharma Technologies - Stacked */}
          <div className="flex flex-col leading-tight">
            <span
              className="font-bold text-xl uppercase tracking-widest text-white/90"
              style={{ textShadow: '0 0 20px rgba(255, 255, 255, 0.3)' }}
            >
              United
            </span>
            <span
              className="font-bold text-xl uppercase tracking-widest text-white/90"
              style={{ textShadow: '0 0 20px rgba(255, 255, 255, 0.3)' }}
            >
              Pharma
            </span>
            <span
              className="font-bold text-xl uppercase tracking-widest animate-pulse"
              style={{
                background: 'linear-gradient(90deg, #10b981, #34d399)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                textShadow: '0 0 30px rgba(16, 185, 129, 0.5)'
              }}
            >
              Technologies
            </span>
          </div>
        </div>
      </div>

      {/* Tagline */}
      <p className="text-gray-400 text-sm tracking-[0.3em] font-medium mb-8 uppercase">
        Compliance & Automation Excellence
      </p>

      {/* Progress Bar */}
      <div className="w-72 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: `${Math.min(progress, 100)}%`,
            background: 'linear-gradient(90deg, #0369a1, #047857, #10b981)'
          }}
        ></div>
      </div>
      <p className="text-gray-500 text-xs mt-4 tracking-[0.2em]">LOADING EXCELLENCE...</p>
    </div>
  );
};

// Custom Cursor Component
const CustomCursor = () => {
  const cursorRef = useRef(null);
  const cursorDotRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);

  useEffect(() => {
    const cursor = cursorRef.current;
    const cursorDot = cursorDotRef.current;

    const moveCursor = (e) => {
      if (cursor && cursorDot) {
        cursor.style.left = e.clientX + 'px';
        cursor.style.top = e.clientY + 'px';
        cursorDot.style.left = e.clientX + 'px';
        cursorDot.style.top = e.clientY + 'px';
      }
    };

    const handleMouseOver = (e) => {
      if (e.target.closest('a, button, [role="button"], .cursor-pointer')) {
        setIsHovering(true);
      } else {
        setIsHovering(false);
      }
    };

    const handleMouseDown = () => setIsClicking(true);
    const handleMouseUp = () => setIsClicking(false);

    window.addEventListener('mousemove', moveCursor);
    window.addEventListener('mouseover', handleMouseOver);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', moveCursor);
      window.removeEventListener('mouseover', handleMouseOver);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <>
      {/* Outer cursor ring */}
      <div
        ref={cursorRef}
        className={`fixed pointer-events-none z-[9998] -translate-x-1/2 -translate-y-1/2 transition-all duration-200 ease-out hidden lg:block ${isHovering ? 'w-12 h-12 border-[#2d9b83] bg-[#2d9b83]/10' : 'w-8 h-8 border-white/30 bg-transparent'
          } ${isClicking ? 'scale-75' : 'scale-100'} rounded-full border-2`}
        style={{ mixBlendMode: 'difference' }}
      ></div>
      {/* Inner cursor dot */}
      <div
        ref={cursorDotRef}
        className={`fixed pointer-events-none z-[9998] -translate-x-1/2 -translate-y-1/2 transition-all duration-75 hidden lg:block ${isHovering ? 'w-2 h-2 bg-[#2d9b83]' : 'w-1 h-1 bg-white'
          } rounded-full`}
      ></div>
    </>
  );
};

// Ripple Button Component
const RippleButton = ({ children, className, href, onClick, type = "button" }) => {
  const [ripples, setRipples] = useState([]);

  const createRipple = (e) => {
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const ripple = { x, y, id: Date.now() };
    setRipples(prev => [...prev, ripple]);
    setTimeout(() => setRipples(prev => prev.filter(r => r.id !== ripple.id)), 600);
  };

  const Component = href ? 'a' : 'button';
  const props = href ? { href } : { type, onClick };

  return (
    <Component
      {...props}
      className={`relative overflow-hidden ${className}`}
      onMouseDown={createRipple}
    >
      {ripples.map(ripple => (
        <span
          key={ripple.id}
          className="absolute bg-white/30 rounded-full animate-ripple pointer-events-none"
          style={{
            left: ripple.x,
            top: ripple.y,
            transform: 'translate(-50%, -50%)'
          }}
        />
      ))}
      {children}
    </Component>
  );
};

// Enterprise Logo Component - Text-based horizontal layout with divider
const EnterpriseLogo = ({ variant = "dark", size = "default", animated = false }) => {
  const sizeMap = {
    small: { upt: "text-4xl", sub: "text-[11px]", gap: "10px", dividerHeight: "44px" },
    default: { upt: "text-5xl", sub: "text-[13px]", gap: "12px", dividerHeight: "56px" },
    large: { upt: "text-6xl", sub: "text-[15px]", gap: "14px", dividerHeight: "68px" }
  };

  const { upt, sub, gap, dividerHeight } = sizeMap[size];

  // Color schemes based on variant
  const isLight = variant === "light";
  const uptColor = isLight ? '#ffffff' : '#0369a1';
  const textColor = isLight ? '#e5e7eb' : '#1f2937';
  const techColor = isLight ? '#10b981' : '#047857';

  return (
    <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: gap }}>
      {/* UPT - Bold and big on left */}
      <span
        className={`font-black ${upt}`}
        style={{
          color: uptColor,
          letterSpacing: '0.02em',
          lineHeight: 1,
          textShadow: isLight ? '0 2px 4px rgba(0,0,0,0.3)' : 'none'
        }}
      >
        UPT
      </span>

      {/* Vertical Divider */}
      <div
        style={{
          width: '2px',
          height: dividerHeight,
          background: isLight
            ? 'linear-gradient(to bottom, #ffffff, #10b981)'
            : 'linear-gradient(to bottom, #0369a1, #047857)',
          borderRadius: '1px'
        }}
      />

      {/* United Pharma Technologies - Stacked on right */}
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
        <span
          className={`font-bold uppercase ${sub}`}
          style={{
            color: textColor,
            letterSpacing: '0.12em'
          }}
        >
          United
        </span>
        <span
          className={`font-bold uppercase ${sub}`}
          style={{
            color: textColor,
            letterSpacing: '0.12em'
          }}
        >
          Pharma
        </span>
        <span
          className={`font-bold uppercase ${sub}`}
          style={{
            color: techColor,
            letterSpacing: '0.12em'
          }}
        >
          Technologies
        </span>
      </div>
    </div>
  );
};

/* SAVED HEXAGON LOGO CODE - DO NOT DELETE
const HexagonLogo = ({ size = "default" }) => {
  const sizeMap = {
    small: { width: 48, height: 48, hexSize: 28 },
    default: { width: 56, height: 56, hexSize: 32 },
    large: { width: 80, height: 80, hexSize: 44 }
  };
  const { width, height, hexSize } = sizeMap[size];
  const offset = hexSize * 0.5;
  return (
    <div style={{ position: 'relative', width: `${width}px`, height: `${height}px`, flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: '0px', left: '0px' }}>
        <div style={{ position: 'absolute', top: '-2px', left: '-2px', width: `${hexSize + 4}px`, height: `${hexSize + 4}px`, background: '#ffffff', clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)', zIndex: 0 }} />
        <div style={{ position: 'relative', width: `${hexSize}px`, height: `${hexSize}px`, background: '#0369a1', clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)', zIndex: 1 }} />
      </div>
      <div style={{ position: 'absolute', top: `${offset}px`, left: `${offset}px` }}>
        <div style={{ position: 'absolute', top: '-2px', left: '-2px', width: `${hexSize + 4}px`, height: `${hexSize + 4}px`, background: '#ffffff', clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)', zIndex: 2 }} />
        <div style={{ position: 'relative', width: `${hexSize}px`, height: `${hexSize}px`, background: '#047857', clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)', zIndex: 3 }} />
      </div>
    </div>
  );
};
END SAVED HEXAGON LOGO CODE */

// Helix AI Chatbot Component
const HelixChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm Helix, UPT's Compliance & Automation Intelligence. I can help you with questions about validation, automation, quality systems, and regulatory compliance for life sciences. How can I assist you today?"
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  // Listen for global event to open Helix
  useEffect(() => {
    const handleOpenHelix = () => setIsOpen(true);
    window.addEventListener('openHelix', handleOpenHelix);
    return () => window.removeEventListener('openHelix', handleOpenHelix);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/helix/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again or contact UPT directly at +1 732-769-8433."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-18 h-18 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110"
        style={{
          width: '72px',
          height: '72px',
          background: 'linear-gradient(135deg, #0369a1 0%, #047857 100%)',
          boxShadow: '0 8px 32px rgba(3, 105, 161, 0.4)'
        }}
      >
        {isOpen ? (
          <X size={32} className="text-white" />
        ) : (
          <div className="relative">
            <MessageCircle size={32} className="text-white" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full animate-pulse" />
          </div>
        )}
      </button>

      {/* Chat Modal */}
      {isOpen && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[450px] max-w-[calc(100vw-48px)] rounded-2xl shadow-2xl overflow-hidden"
          style={{
            background: '#ffffff',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            maxHeight: 'calc(100vh - 140px)'
          }}
        >
          {/* Header */}
          <div
            className="px-5 py-4 flex items-center justify-between"
            style={{ background: 'linear-gradient(135deg, #0369a1 0%, #047857 100%)' }}
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Sparkles size={24} className="text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-xl">Helix</h3>
                <p className="text-white/80 text-sm">Compliance & Automation Intelligence</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white transition-colors"
            >
              <Minimize2 size={22} />
            </button>
          </div>

          {/* Messages */}
          <div
            className="p-5 overflow-y-auto"
            style={{ height: '450px', background: '#f8fafc' }}
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`mb-4 flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-5 py-4 rounded-2xl ${msg.role === 'user'
                    ? 'bg-gradient-to-r from-[#0369a1] to-[#047857] text-white rounded-br-md'
                    : 'bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-100'
                    }`}
                >
                  {msg.role === 'assistant' && (
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
                      <Bot size={16} className="text-[#047857]" />
                      <span className="text-sm font-semibold text-[#047857]">Helix</span>
                    </div>
                  )}
                  <p className="text-base whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start mb-4">
                <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-[#0369a1] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-[#047857] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-[#0369a1] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-xs text-gray-500">Helix is thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-5 border-t border-gray-100 bg-white">
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask about validation, compliance..."
                className="flex-1 px-5 py-4 rounded-xl border border-gray-200 focus:outline-none focus:border-[#0369a1] focus:ring-2 focus:ring-[#0369a1]/20 text-base"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="w-14 h-14 rounded-xl flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: inputValue.trim() ? 'linear-gradient(135deg, #0369a1 0%, #047857 100%)' : '#e5e7eb'
                }}
              >
                <Send size={22} className={inputValue.trim() ? 'text-white' : 'text-gray-400'} />
              </button>
            </div>
            <p className="text-[10px] text-gray-400 mt-2 text-center">
              Helix supports decision-making. Execution remains with UPT experts.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

// Floating Text Elements - Compliance keywords
const FloatingElements = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[
      { text: "ISO 13485", x: 5, y: 15, delay: 0 },
      { text: "FDA 21 CFR", x: 88, y: 20, delay: 2 },
      { text: "GxP", x: 10, y: 75, delay: 1 },
      { text: "EU MDR", x: 85, y: 80, delay: 3 },
      { text: "GAMP 5", x: 92, y: 50, delay: 1.5 },
      { text: "CSV", x: 8, y: 45, delay: 2.5 },
    ].map((item, idx) => (
      <div
        key={idx}
        className="absolute animate-float-subtle text-white/10 font-semibold text-sm"
        style={{ left: `${item.x}%`, top: `${item.y}%`, animationDelay: `${item.delay}s` }}
      >
        {item.text}
      </div>
    ))}
  </div>
);

// Floating Icons - Amazing animations with wow factor
const FloatingIcons = () => {
  const icons = [
    { Icon: Pill, x: 2, y: 15, size: 38, delay: 0, duration: 8, rotate: true },
    { Icon: Dna, x: 96, y: 10, size: 52, delay: 1, duration: 10, pulse: true },
    { Icon: FlaskConical, x: 6, y: 50, size: 34, delay: 2, duration: 7, rotate: true },
    { Icon: Microscope, x: 92, y: 60, size: 44, delay: 0.5, duration: 9, pulse: true },
    { Icon: TestTube2, x: 12, y: 80, size: 32, delay: 1.5, duration: 6, rotate: true },
    { Icon: HeartPulse, x: 94, y: 35, size: 38, delay: 2.5, duration: 8, beat: true },
    { Icon: Atom, x: 3, y: 35, size: 48, delay: 3, duration: 12, spin: true },
    { Icon: Syringe, x: 89, y: 48, size: 34, delay: 1, duration: 7, rotate: true },
    { Icon: ShieldCheck, x: 8, y: 65, size: 30, delay: 2, duration: 6.5, pulse: true },
    { Icon: Beaker, x: 93, y: 78, size: 40, delay: 0.5, duration: 9, rotate: true },
    { Icon: Tablets, x: 4, y: 75, size: 32, delay: 3.5, duration: 7, pulse: true },
    { Icon: Cpu, x: 87, y: 20, size: 38, delay: 1.5, duration: 6, blink: true },
    { Icon: Activity, x: 14, y: 25, size: 36, delay: 2.5, duration: 8, wave: true },
    { Icon: Stethoscope, x: 84, y: 70, size: 40, delay: 0, duration: 9, rotate: true },
    { Icon: Droplets, x: 1, y: 45, size: 30, delay: 4, duration: 7, drip: true },
    { Icon: CircuitBoard, x: 96, y: 45, size: 34, delay: 2, duration: 6, blink: true },
    { Icon: Dna, x: 10, y: 40, size: 42, delay: 1.5, duration: 10, spin: true },
    { Icon: Server, x: 90, y: 88, size: 32, delay: 3, duration: 7, pulse: true },
    { Icon: Database, x: 5, y: 88, size: 28, delay: 2.5, duration: 8, blink: true },
    { Icon: Lock, x: 88, y: 8, size: 32, delay: 1, duration: 7, pulse: true },
    { Icon: Thermometer, x: 15, y: 58, size: 30, delay: 3.5, duration: 6, rise: true },
    { Icon: FileSearch, x: 82, y: 55, size: 34, delay: 0.5, duration: 8, rotate: true },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {icons.map((item, idx) => (
        <div
          key={idx}
          className={`absolute ${item.spin ? 'animate-spin-slow' : item.beat ? 'animate-heartbeat' : item.blink ? 'animate-blink' : item.wave ? 'animate-wave' : item.drip ? 'animate-drip' : item.rise ? 'animate-rise' : 'animate-float-icon'}`}
          style={{
            left: `${item.x}%`,
            top: `${item.y}%`,
            animationDelay: `${item.delay}s`,
            animationDuration: `${item.duration}s`,
          }}
        >
          <div className={`relative ${item.rotate ? 'animate-wiggle' : ''} ${item.pulse ? 'animate-glow-pulse' : ''}`}>
            {/* Multi-layer glow effect */}
            <div
              className="absolute inset-0 blur-2xl rounded-full animate-pulse"
              style={{
                backgroundColor: idx % 2 === 0 ? '#2d9b83' : '#1e5f74',
                opacity: 0.5,
                transform: 'scale(2.5)'
              }}
            ></div>
            <div
              className="absolute inset-0 blur-lg rounded-full"
              style={{
                backgroundColor: idx % 3 === 0 ? '#4db8a4' : '#2d9b83',
                opacity: 0.4,
                transform: 'scale(1.8)'
              }}
            ></div>
            <item.Icon
              size={item.size}
              className="relative drop-shadow-lg"
              style={{ color: idx % 3 === 0 ? 'rgba(45, 155, 131, 0.7)' : idx % 3 === 1 ? 'rgba(30, 95, 116, 0.7)' : 'rgba(77, 184, 164, 0.65)' }}
              strokeWidth={1.5}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// Top Bar
const TopBar = () => (
  <div className="bg-[#0d2137] text-white/80 py-2.5 text-sm border-b border-white/10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 flex justify-between items-center">
      <div className="flex items-center gap-2 sm:gap-0 sm:divide-x sm:divide-white/20">
        <a href="tel:+17327698433" className="flex items-center hover:text-white transition-colors sm:pr-4 whitespace-nowrap">
          <Phone size={14} className="mr-1.5 sm:mr-2 text-[#2d9b83] flex-shrink-0" />
          <span className="hidden xs:inline">+1 </span>732-769-8433
        </a>
        <a href="mailto:info@unitedpharmatechnologies.com" className="hidden sm:flex items-center hover:text-white transition-colors pl-4">
          <Mail size={14} className="mr-2 text-[#2d9b83]" />
          info@unitedpharmatechnologies.com
        </a>
      </div>
      <div className="hidden md:flex items-center gap-6">
        <a href="/news" className="hover:text-white transition-colors">News</a>
        <div className="flex items-center gap-3 pl-4 border-l border-white/20">
          <a href="#" className="hover:text-[#2d9b83] transition-colors"><Linkedin size={16} /></a>
          <a href="#" className="hover:text-[#2d9b83] transition-colors"><Twitter size={16} /></a>
        </div>
      </div>
      {/* Mobile social icons */}
      <div className="flex md:hidden items-center gap-3">
        <a href="mailto:info@unitedpharmatechnologies.com" className="hover:text-[#2d9b83] transition-colors">
          <Mail size={16} />
        </a>
        <a href="#" className="hover:text-[#2d9b83] transition-colors"><Linkedin size={16} /></a>
      </div>
    </div>
  </div>
);

// Enterprise Header
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
      const progress = Math.min((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100, 100);
      setScrollProgress(progress);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const menuItems = [
    {
      name: "Industries",
      href: "#industries",
      submenu: [
        { name: "Pharmaceuticals", desc: "Drug development & manufacturing support", icon: Pill },
        { name: "Biotechnology", desc: "Cell therapy & biologics services", icon: Dna },
        { name: "Medical Devices", desc: "Device lifecycle management", icon: Cpu },
        { name: "Diagnostics", desc: "IVD & laboratory solutions", icon: Microscope },
      ]
    },
    {
      name: "Services",
      href: "#services",
      submenu: [
        { name: "Validation Services", desc: "CQV, CSV, Process Validation", icon: ShieldCheck },
        { name: "Automation Solutions", desc: "Control systems & MES", icon: Settings2 },
        { name: "Quality Systems", desc: "QMS implementation & support", icon: ClipboardCheck },
        { name: "Regulatory Affairs", desc: "Global compliance support", icon: FileCheck },
        { name: "Digital Solutions", desc: "Industry 4.0 transformation", icon: Network },
      ]
    },
    {
      name: "Approach",
      href: "#solutions",
      submenu: [
        { name: "Discovery", desc: "Assessment & gap analysis", icon: Lightbulb },
        { name: "Strategy", desc: "Planning & roadmap", icon: Target },
        { name: "Execution", desc: "Implementation & delivery", icon: Rocket },
        { name: "Support", desc: "Ongoing partnership", icon: Handshake },
      ]
    },
    { name: "Case Studies", href: "#case-studies" },
    { name: "About", href: "#about" },
    {
      name: "Helix AI",
      href: "/helix",
      isSpecial: true,
      icon: Sparkles
    },
    {
      name: "Careers",
      href: "/careers",
      icon: Briefcase
    },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <>
      {!isScrolled && <TopBar />}
      <header className={`${isScrolled ? 'fixed top-0' : 'sticky top-0'} left-0 right-0 z-50 transition-all duration-500 ${isScrolled
        ? "bg-gradient-to-r from-white/95 via-white/98 to-white/95 backdrop-blur-2xl shadow-xl shadow-black/5 border-b border-gray-100/50"
        : "bg-white"
        }`}>
        {/* Animated gradient progress bar */}
        {isScrolled && (
          <div className="absolute bottom-0 left-0 h-[3px] overflow-hidden" style={{ width: `${scrollProgress}%` }}>
            <div className="h-full w-full bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#8b5cf6] animate-gradient-shift" style={{ backgroundSize: '200% 100%' }} />
          </div>
        )}

        <div className="max-w-7xl mx-auto px-6">
          <div className={`flex items-center justify-between transition-all duration-300 ${isScrolled ? 'h-16' : 'h-20'}`}>
            <a href="#home" className="relative group">
              <EnterpriseLogo variant="dark" size={isScrolled ? "small" : "default"} animated={!isScrolled} />
            </a>

            <nav className="hidden lg:flex items-center">
              {menuItems.map((item, idx) => (
                <div
                  key={idx}
                  className="relative"
                  onMouseEnter={() => !item.isSpecial && setActiveDropdown(idx)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  {item.isSpecial ? (
                    <a
                      href={item.href}
                      className="group flex items-center px-5 py-2.5 mx-2 text-white font-semibold rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl relative overflow-hidden"
                      style={{
                        background: 'linear-gradient(135deg, #0ea5e9 0%, #10b981 50%, #047857 100%)',
                        boxShadow: '0 4px 20px rgba(14, 165, 233, 0.3)'
                      }}
                    >
                      {/* Animated shine effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                      <item.icon size={16} className="mr-2 animate-pulse" />
                      {item.name}
                    </a>
                  ) : (
                    <>
                      <a
                        href={item.href}
                        className="flex items-center px-5 py-8 text-gray-700 font-medium hover:text-[#0ea5e9] transition-all duration-300 relative group"
                      >
                        {item.name}
                        {item.submenu && <ChevronDown size={14} className="ml-1 opacity-50 group-hover:rotate-180 transition-transform duration-300" />}
                        <span className="absolute bottom-6 left-5 right-5 h-[2px] bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#047857] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-full"></span>
                      </a>

                      {item.submenu && activeDropdown === idx && (
                        <div className="absolute top-full left-0 w-80 bg-[#0a1525] backdrop-blur-2xl rounded-2xl shadow-2xl shadow-black/30 border border-[#1a3a5c] p-3 animate-fadeIn">
                          {/* Gradient accent */}
                          <div className="absolute top-0 left-4 right-4 h-[2px] bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#047857] rounded-full" />
                          <div className="absolute -top-2 left-8 w-4 h-4 bg-[#0a1525] border-l border-t border-[#1a3a5c] transform rotate-45"></div>
                          {item.submenu.map((sub, subIdx) => (
                            <a
                              key={subIdx}
                              href={sub.href || item.href}
                              className="flex items-start p-3 rounded-xl hover:bg-white/10 transition-all duration-300 group/item"
                            >
                              <div className="w-11 h-11 bg-gradient-to-br from-[#0ea5e9]/20 to-[#10b981]/20 rounded-xl flex items-center justify-center mr-3 group-hover/item:from-[#0ea5e9]/30 group-hover/item:to-[#10b981]/30 transition-all duration-300 group-hover/item:scale-110 group-hover/item:shadow-lg">
                                <sub.icon className="text-[#0ea5e9] group-hover/item:text-[#10b981] transition-colors" size={20} />
                              </div>
                              <div>
                                <div className="font-semibold text-white group-hover/item:text-[#0ea5e9] transition-colors">{sub.name}</div>
                                <div className="text-sm text-gray-400">{sub.desc}</div>
                              </div>
                            </a>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </nav>

            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors">
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white/98 backdrop-blur-xl border-t border-gray-100 py-4 px-6">
            {menuItems.map((item, idx) => (
              item.isSpecial ? (
                <a
                  key={idx}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center py-3 my-2 text-white font-semibold rounded-xl"
                  style={{
                    background: 'linear-gradient(135deg, #0ea5e9 0%, #10b981 50%, #047857 100%)'
                  }}
                >
                  <item.icon size={16} className="mr-2" />
                  {item.name}
                </a>
              ) : (
                <a
                  key={idx}
                  href={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block py-3 text-gray-700 font-medium hover:text-[#0ea5e9] border-b border-gray-50 transition-colors"
                >
                  {item.name}
                </a>
              )
            ))}
          </div>
        )}
      </header>
    </>
  );
};

// Hero Section - Next-Level Enterprise with Dynamic Animations
const HeroSection = () => (
  <section id="home" className="relative min-h-[95vh] flex items-center overflow-hidden bg-[#0a1525]">
    {/* Sophisticated multi-layer gradient background */}
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a1525] via-[#0d2137] to-[#102a3f]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#0ea5e9]/10 via-transparent to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-[#10b981]/8 via-transparent to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-[#0a1525] to-transparent"></div>

      {/* Animated gradient orbs - more vibrant */}
      <div className="absolute top-10 right-1/4 w-[600px] h-[600px] bg-[#0ea5e9]/15 rounded-full blur-[150px] animate-morph-blob"></div>
      <div className="absolute bottom-10 left-1/4 w-[500px] h-[500px] bg-[#10b981]/12 rounded-full blur-[120px] animate-morph-blob" style={{ animationDelay: '-5s' }}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[#8b5cf6]/8 rounded-full blur-[180px] animate-pulse"></div>
      <div className="absolute top-1/3 right-10 w-[300px] h-[300px] bg-[#ec4899]/10 rounded-full blur-[100px] animate-morph-blob" style={{ animationDelay: '-3s' }}></div>
    </div>

    {/* Dynamic grid pattern with subtle animation */}
    <div className="absolute inset-0 cyber-grid" style={{
      backgroundImage: `linear-gradient(rgba(14, 165, 233, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(14, 165, 233, 0.03) 1px, transparent 1px)`,
      backgroundSize: '50px 50px'
    }}></div>

    {/* Animated horizontal light beams */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#0ea5e9]/40 to-transparent warp-line" style={{ animationDuration: '8s' }}></div>
      <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#10b981]/30 to-transparent warp-line" style={{ animationDelay: '-2s', animationDuration: '10s' }}></div>
      <div className="absolute top-3/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#8b5cf6]/25 to-transparent warp-line" style={{ animationDelay: '-4s', animationDuration: '12s' }}></div>
    </div>

    {/* Floating quantum particles */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(30)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 rounded-full quantum-float"
          style={{
            backgroundColor: ['#0ea5e9', '#10b981', '#8b5cf6', '#ec4899', '#f59e0b'][i % 5],
            left: `${(i * 7) % 100}%`,
            top: `${(i * 11) % 100}%`,
            animationDelay: `${i * 0.2}s`,
            opacity: 0.6
          }}
        />
      ))}
    </div>

    <FloatingElements />
    <FloatingIcons />

    <div className="relative max-w-7xl mx-auto px-6 py-24 z-10 w-full">
      <div className="flex items-center justify-center gap-8">
        {/* Center Content */}
        <div className="text-center max-w-3xl">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.05] mb-6">
            Engineering
            <span className="block mt-2 bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#8b5cf6] bg-clip-text text-transparent animate-gradient-shift" style={{ backgroundSize: '200% auto' }}>
              Compliance Excellence
            </span>
          </h1>

          <p className="text-lg text-gray-400 mb-10 leading-relaxed max-w-2xl mx-auto">
            Empowering pharmaceutical, biotechnology, and medical device enterprises with end-to-end validation,
            quality assurance, and regulatory compliance solutions. From GMP validation and CQV to FDA compliance
            consulting—we architect operational excellence for the world's most regulated industries.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-10 justify-center">
            <button
              onClick={() => window.dispatchEvent(new Event('openHelix'))}
              className="group relative inline-flex items-center justify-center px-8 py-4 font-bold rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl"
              style={{
                background: 'linear-gradient(135deg, #0ea5e9 0%, #10b981 50%, #047857 100%)',
                boxShadow: '0 10px 40px rgba(14, 165, 233, 0.3)'
              }}
            >
              {/* Animated shine */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <Sparkles className="mr-2 text-white" size={20} />
              <span className="text-white">Ask Helix AI</span>
              <ArrowRight className="ml-2 text-white group-hover:translate-x-1 transition-transform" size={20} />
            </button>
            <a href="#case-studies" className="group inline-flex items-center justify-center px-8 py-4 bg-white/5 border-2 border-white/20 text-white font-bold rounded-xl hover:bg-white/10 hover:border-[#0ea5e9]/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              View Our Work
              <ExternalLink className="ml-2 opacity-50 group-hover:opacity-100 transition-opacity" size={18} />
            </a>
          </div>

          {/* Trust indicators with icons */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-gray-300">
            {[
              { icon: Shield, text: "FDA Compliance USA", color: '#0ea5e9' },
              { icon: Award, text: "GMP Validation USA", color: '#10b981' },
              { icon: Globe2, text: "Nationwide Coverage", color: '#8b5cf6' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2 group cursor-pointer">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: `${item.color}20` }}
                >
                  <item.icon size={16} style={{ color: item.color }} />
                </div>
                <span className="text-sm font-medium group-hover:text-white transition-colors">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side - Compact Experience Badge */}
        <div className="hidden xl:block">
          <div className="relative p-6 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl w-52">
            {/* Animated gradient border */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#8b5cf6] rounded-2xl opacity-20 blur animate-pulse"></div>

            <div className="relative text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-[#0ea5e9] to-[#10b981] mb-3 shadow-lg shadow-[#10b981]/30">
                <Award size={24} className="text-white" />
              </div>
              <div className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] to-[#10b981] mb-1">
                30+
              </div>
              <div className="text-sm text-white font-bold leading-tight">Years of Combined</div>
              <div className="text-sm text-white font-bold leading-tight">Industry Experience</div>
              <p className="text-gray-400 text-xs mt-2 leading-tight">
                Since 2014, delivering excellence in life sciences compliance
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Stats - Single Badge */}
      <div className="xl:hidden mt-12 flex justify-center">
        <div className="p-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl text-center">
          <div className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] to-[#10b981] mb-1">30+ Years</div>
          <div className="text-gray-400 text-xs">Combined Industry Experience Since 2014</div>
        </div>
      </div>
    </div>
  </section>
);

// Clients Section - Premium Infinite Scroll Marquee
const ClientsSection = () => {
  const clients = [
    { name: "Novartis", logo: "https://customer-assets.emergentagent.com/job_upt-enterprise/artifacts/2lzhzxw1_Novartis-Logo.svg.png" },
    { name: "Roche", logo: "https://customer-assets.emergentagent.com/job_upt-enterprise/artifacts/tb7eahuq_Hoffmann-La_Roche_logo.svg.png" },
    { name: "AstraZeneca", logo: "https://customer-assets.emergentagent.com/job_upt-enterprise/artifacts/ac2g23g9_AstraZeneca_logo.svg.png" },
    { name: "GSK", logo: "https://customer-assets.emergentagent.com/job_upt-enterprise/artifacts/dt1o6ml0_GSK_logo_2014.svg.png" },
    { name: "Sanofi", logo: "https://customer-assets.emergentagent.com/job_upt-enterprise/artifacts/fymjbga3_Sanofi_logo.svg.png" },
  ];

  // Double the array for seamless infinite scroll
  const duplicatedClients = [...clients, ...clients];

  return (
    <section className="py-20 bg-gradient-to-b from-[#0a1525] to-[#0d2137] relative overflow-hidden">
      {/* Subtle background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#1a936f]/5 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#1e5f74]/5 rounded-full blur-[100px]"></div>
      </div>

      <div className="relative z-10">
        {/* Header */}
        <div className="text-center mb-16 px-6">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#2d9b83]"></div>
            <p className="text-[#2d9b83] text-sm font-semibold tracking-[0.3em]">TRUSTED BY INDUSTRY LEADERS</p>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#2d9b83]"></div>
          </div>
          <h3 className="text-2xl md:text-3xl font-bold text-white">
            Partnering with <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2d9b83] to-[#4db8a4]">Global Innovators</span>
          </h3>
        </div>

        {/* Infinite Scroll Marquee */}
        <div className="relative">
          {/* Gradient Fade Edges */}
          <div className="absolute left-0 top-0 bottom-0 w-32 md:w-48 bg-gradient-to-r from-[#0a1525] via-[#0a1525]/80 to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-32 md:w-48 bg-gradient-to-l from-[#0d2137] via-[#0d2137]/80 to-transparent z-10 pointer-events-none"></div>

          {/* Marquee Track */}
          <div className="flex animate-marquee hover:pause-animation">
            {duplicatedClients.map((client, idx) => (
              <div
                key={idx}
                className="flex-shrink-0 mx-6 md:mx-10"
              >
                <div className="group relative">
                  {/* Glassmorphism Card */}
                  <div className="relative px-10 py-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl transition-all duration-500 hover:bg-white/10 hover:border-[#2d9b83]/30 hover:scale-105 hover:shadow-2xl hover:shadow-[#1a936f]/20">
                    {/* Hover glow */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#1a936f]/0 to-[#1e5f74]/0 group-hover:from-[#1a936f]/10 group-hover:to-[#1e5f74]/10 transition-all duration-500"></div>

                    {/* Logo */}
                    <img
                      src={client.logo}
                      alt={client.name}
                      loading="lazy"
                      decoding="async"
                      className="relative h-10 md:h-12 w-auto object-contain filter brightness-0 invert opacity-60 group-hover:opacity-100 transition-all duration-500"
                    />

                    {/* Bottom accent line */}
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-[#1a936f] to-[#2d9b83] group-hover:w-3/4 transition-all duration-500 rounded-full"></div>
                  </div>

                  {/* Company name tooltip */}
                  <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                    <span className="text-[#2d9b83] text-sm font-medium whitespace-nowrap">{client.name}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Decorative Line */}
        <div className="flex justify-center mt-16 px-6">
          <div className="flex items-center gap-4">
            <div className="h-px w-16 md:w-24 bg-gradient-to-r from-transparent to-[#1a936f]/50"></div>
            <div className="flex gap-2">
              {[...Array(5)].map((_, idx) => (
                <div
                  key={idx}
                  className="w-1.5 h-1.5 rounded-full bg-[#2d9b83]/50 animate-pulse"
                  style={{ animationDelay: `${idx * 200}ms` }}
                ></div>
              ))}
            </div>
            <div className="h-px w-16 md:w-24 bg-gradient-to-l from-transparent to-[#1a936f]/50"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Industries Section
// NEXT-LEVEL UPT End-to-End Lifecycle Coverage Component
const IndustryLifecycleWorkflow = ({ industry }) => {
  const [activeStage, setActiveStage] = useState(0);
  const [activeTab, setActiveTab] = useState('activities');
  const [activeSubPhase, setActiveSubPhase] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStage(prev => (prev + 1) % 10);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Complete 10 Lifecycle Stages from UPT with expanded content
  const lifecycleStages = [
    {
      id: 'research',
      num: '01',
      title: 'Research & Discovery',
      shortTitle: 'R&D',
      icon: FlaskConical,
      color: '#0ea5e9',
      gradient: 'from-sky-400 via-sky-500 to-blue-600',
      description: 'UPT enables early-stage readiness while building compliance foundations that scale.',
      subPhases: [
        {
          name: 'Lab Setup',
          items: ['Laboratory design assessments', 'GxP impact evaluations', 'Utility qualification planning']
        },
        {
          name: 'Instrumentation',
          items: ['Instrument selection criteria', 'Installation qualification', 'Initial calibration programs']
        },
        {
          name: 'Data Systems',
          items: ['LIMS implementation readiness', 'Data integrity controls', 'Electronic notebook validation']
        }
      ],
      activities: [
        'Laboratory design and readiness assessments',
        'Instrument selection, installation, and calibration',
        'GxP impact assessments for R&D environments',
        'Early automation and data integrity controls',
        'Method development documentation',
        'Reference standard management'
      ],
      value: [
        'Prevents rework during later validation phases',
        'Establishes traceability from day one',
        'Enables smooth transition into clinical and manufacturing',
        'Reduces compliance gaps before IND submission'
      ],
      deliverables: ['Lab Readiness Assessment Report', 'GxP Impact Analysis', 'Calibration Program Design', 'Data Integrity Framework'],
      skills: ['Lab Design', 'Calibration', 'GxP Assessment', 'Data Integrity', 'Method Development'],
      domains: ['Pharma', 'Biotech', 'Med Device']
    },
    {
      id: 'preclinical',
      num: '02',
      title: 'Preclinical & Translational',
      shortTitle: 'Preclinical',
      icon: Dna,
      color: '#8b5cf6',
      gradient: 'from-violet-400 via-violet-500 to-purple-600',
      description: 'UPT supports controlled transition from discovery into regulated development.',
      subPhases: [
        {
          name: 'Method Dev',
          items: ['Analytical method development', 'Bioanalytical assay design', 'Stability indicating methods']
        },
        {
          name: 'Equipment',
          items: ['Equipment qualification', 'Performance verification', 'Maintenance protocols']
        },
        {
          name: 'Compliance',
          items: ['GLP documentation', 'Risk-based frameworks', 'Audit readiness preparation']
        }
      ],
      activities: [
        'Method development and documentation',
        'Equipment qualification and calibration',
        'Data governance and integrity controls',
        'Risk-based compliance frameworks',
        'Protocol development for toxicology studies',
        'Bioanalytical method validation'
      ],
      value: [
        'Maintains scientific flexibility while preparing for regulatory scrutiny',
        'Reduces compliance gaps before clinical entry',
        'Streamlines IND/IMPD preparation',
        'Establishes robust analytical foundations'
      ],
      deliverables: ['Method Validation Protocols', 'Equipment Qualification Reports', 'Risk Assessment Documents', 'GLP Compliance Package'],
      skills: ['Method Development', 'Documentation', 'Risk Assessment', 'Compliance', 'GLP'],
      domains: ['Pharma', 'Biotech', 'CRO']
    },
    {
      id: 'clinical',
      num: '03',
      title: 'Clinical Lifecycle',
      shortTitle: 'Clinical',
      icon: Stethoscope,
      color: '#ec4899',
      gradient: 'from-pink-400 via-pink-500 to-rose-600',
      description: 'UPT ensures clinical operations are compliant, auditable, and scalable.',
      subPhases: [
        {
          name: 'Planning',
          items: ['Clinical strategy development', 'Regulatory pathway alignment', 'System selection criteria']
        },
        {
          name: 'Setup',
          items: ['EDC/CTMS/eTMF validation', 'Site qualification', 'Training deployment']
        },
        {
          name: 'Execution',
          items: ['Data verification', 'Audit trail review', 'Inspection readiness']
        }
      ],
      activities: [
        'Clinical strategy and system selection',
        'Validation of EDC, CTMS, eTMF systems',
        'Site qualification and readiness',
        'Data verification and cleaning',
        'Audit trail review and traceability',
        'Clinical study reports and archival'
      ],
      value: [
        'GCP and ICH alignment throughout trials',
        'Inspection readiness at all times',
        'Data integrity maintenance',
        'Accelerated submission timelines'
      ],
      deliverables: ['Clinical System Validation Reports', 'Site Readiness Packages', 'Data Management Plans', 'CSR Documentation'],
      skills: ['EDC Validation', 'GCP', 'Site Qualification', 'Data Management', 'eTMF', 'CSV'],
      domains: ['Pharma', 'Biotech', 'CRO', 'Clinical Sites']
    },
    {
      id: 'manufacturing',
      num: '04',
      title: 'Manufacturing & Scale-Up',
      shortTitle: 'Manufacturing',
      icon: Factory,
      color: '#10b981',
      gradient: 'from-emerald-400 via-emerald-500 to-green-600',
      description: 'UPT enables rapid scale-up without compromising compliance.',
      subPhases: [
        {
          name: 'Commissioning',
          items: ['Facility commissioning', 'Utility qualification', 'HVAC/cleanroom verification']
        },
        {
          name: 'Equipment',
          items: ['IQ/OQ/PQ execution', 'FAT/SAT support', 'Process equipment validation']
        },
        {
          name: 'Process',
          items: ['Process validation', 'Tech transfer protocols', 'Scale-up studies']
        }
      ],
      activities: [
        'Facility and utility commissioning',
        'Equipment qualification (IQ/OQ/PQ)',
        'Process automation and validation',
        'Technology transfer support',
        'Cleaning validation programs',
        'Environmental monitoring setup'
      ],
      value: [
        'Faster time to commercial readiness',
        'Reduced deviations during ramp-up',
        'Consistent product quality',
        'Optimized manufacturing efficiency'
      ],
      deliverables: ['Commissioning Reports', 'IQ/OQ/PQ Protocols & Reports', 'Process Validation Packages', 'Tech Transfer Dossiers'],
      skills: ['CQV', 'Process Validation', 'Tech Transfer', 'Automation', 'Cleaning Validation'],
      domains: ['Pharma', 'Biotech', 'CDMO', 'Med Device']
    },
    {
      id: 'qc',
      num: '05',
      title: 'Quality Control & Labs',
      shortTitle: 'QC Labs',
      icon: Microscope,
      color: '#f59e0b',
      gradient: 'from-amber-400 via-amber-500 to-orange-600',
      description: 'UPT builds robust QC operations that withstand regulatory scrutiny.',
      subPhases: [
        {
          name: 'Lab Setup',
          items: ['QC lab design', 'Instrument qualification', 'Environmental controls']
        },
        {
          name: 'Methods',
          items: ['Method validation', 'Method transfer', 'Stability programs']
        },
        {
          name: 'Operations',
          items: ['Release testing', 'OOS/OOT investigations', 'CAPA management']
        }
      ],
      activities: [
        'QC lab design and readiness',
        'Instrument qualification and calibration',
        'Method validation and documentation',
        'In-process and release testing',
        'LIMS implementation and integration',
        'OOS/OOT investigations'
      ],
      value: [
        'Stability and environmental monitoring excellence',
        'CAPA and change control optimization',
        'Continuous improvement and trending',
        'Regulatory inspection confidence'
      ],
      deliverables: ['Lab Qualification Reports', 'Method Validation Packages', 'LIMS Validation Documents', 'Investigation Reports'],
      skills: ['Method Validation', 'LIMS', 'Stability Studies', 'Lab Operations', 'OOS Investigation'],
      domains: ['Pharma', 'Biotech', 'Contract Labs', 'Med Device']
    },
    {
      id: 'calibration',
      num: '06',
      title: 'Calibration & Asset Management',
      shortTitle: 'Calibration',
      icon: Gauge,
      color: '#06b6d4',
      gradient: 'from-cyan-400 via-cyan-500 to-teal-600',
      description: 'UPT treats calibration as a continuous compliance system.',
      subPhases: [
        {
          name: 'Governance',
          items: ['Asset inventory', 'Classification systems', 'Criticality assessment']
        },
        {
          name: 'Execution',
          items: ['Scheduled calibration', 'Event-based calibration', 'Traceable standards']
        },
        {
          name: 'Sustainment',
          items: ['Trend analysis', 'Optimization programs', 'CMMS integration']
        }
      ],
      activities: [
        'Asset inventory and classification',
        'Criticality and risk assessment',
        'Scheduled and event-based calibration',
        'Traceable standards and documentation',
        'Deviation and impact assessment',
        'Preventive maintenance optimization'
      ],
      value: [
        'Trend analysis and optimization',
        'Inspection readiness at all times',
        'Integration with CQV and QMS',
        'Reduced equipment downtime'
      ],
      deliverables: ['Asset Criticality Reports', 'Calibration Procedures', 'PM Programs', 'Trending Analysis Reports'],
      skills: ['Asset Management', 'Calibration', 'Risk Assessment', 'Compliance', 'CMMS'],
      domains: ['All Industries']
    },
    {
      id: 'cqv',
      num: '07',
      title: 'CQV Lifecycle',
      shortTitle: 'CQV',
      icon: ShieldCheck,
      color: '#a855f7',
      gradient: 'from-purple-400 via-purple-500 to-indigo-600',
      description: 'UPT applies risk-based, lifecycle-driven CQV strategies.',
      subPhases: [
        {
          name: 'Planning',
          items: ['Validation master plans', 'Risk-based strategies', 'Requirements traceability']
        },
        {
          name: 'Execution',
          items: ['Commissioning testing', 'IQ/OQ/PQ protocols', 'FAT/SAT coordination']
        },
        {
          name: 'Verification',
          items: ['Periodic review', 'Requalification', 'Change management']
        }
      ],
      activities: [
        'Validation master planning',
        'Risk-based testing strategies',
        'Commissioning and functional testing',
        'IQ/OQ/PQ execution',
        'Process, cleaning, and software validation',
        'Periodic review and requalification'
      ],
      value: [
        'Continuous process verification',
        'Change management excellence',
        'Regulatory inspection readiness',
        'Reduced validation cycle times'
      ],
      deliverables: ['Validation Master Plans', 'Risk Assessment Reports', 'IQ/OQ/PQ Documentation', 'Periodic Review Reports'],
      skills: ['IQ/OQ/PQ', 'CSV', 'Risk Management', 'Process Validation', 'Cleaning Validation'],
      domains: ['Pharma', 'Biotech', 'CDMO', 'Med Device']
    },
    {
      id: 'automation',
      num: '08',
      title: 'Automation & Digital Systems',
      shortTitle: 'Automation',
      icon: CircuitBoard,
      color: '#3b82f6',
      gradient: 'from-blue-400 via-blue-500 to-indigo-600',
      description: 'UPT enables compliant digital transformation.',
      subPhases: [
        {
          name: 'Design',
          items: ['PLC/SCADA/DCS architecture', 'MES/LIMS integration', 'Cybersecurity framework']
        },
        {
          name: 'Validation',
          items: ['CSV execution', '21 CFR Part 11', 'Electronic records/signatures']
        },
        {
          name: 'Intelligence',
          items: ['Digital CQV workflows', 'Automated testing', 'Real-time compliance']
        }
      ],
      activities: [
        'PLC, SCADA, DCS architecture design',
        'Data integrity and cybersecurity',
        'CSV and Part 11 compliance',
        'Electronic records and signatures',
        'Digital CQV workflows',
        'Automated testing and reporting'
      ],
      value: [
        'Real-time compliance visibility',
        'Intelligent automation deployment',
        'Reduced manual intervention and errors',
        'Accelerated validation cycles'
      ],
      deliverables: ['System Architecture Documents', 'CSV Reports', 'Part 11 Compliance Packages', 'Digital Workflow Documentation'],
      skills: ['PLC/SCADA/DCS', 'MES', 'CSV', '21 CFR Part 11', 'Cybersecurity', 'GAMP5'],
      domains: ['All Industries']
    },
    {
      id: 'regulatory',
      num: '09',
      title: 'Regulatory & Compliance',
      shortTitle: 'Regulatory',
      icon: FileCheck,
      color: '#ef4444',
      gradient: 'from-red-400 via-red-500 to-rose-600',
      description: 'UPT supports continuous regulatory readiness.',
      subPhases: [
        {
          name: 'Strategy',
          items: ['Regulatory pathway assessment', 'Submission planning', 'Gap analysis']
        },
        {
          name: 'Execution',
          items: ['Dossier preparation', 'Agency interactions', 'Inspection support']
        },
        {
          name: 'Remediation',
          items: ['483 responses', 'Warning letter remediation', 'Consent decree support']
        }
      ],
      activities: [
        'FDA, EMA, ISO alignment assessments',
        'Inspection readiness programs',
        'Remediation management',
        'Regulatory documentation support',
        'Gap analysis and resolution',
        'Mock inspection programs'
      ],
      value: [
        'Reduced inspection risk',
        'Faster response to regulatory changes',
        'Sustained compliance excellence',
        'Proactive issue identification'
      ],
      deliverables: ['Gap Analysis Reports', 'Inspection Readiness Packages', 'Remediation Plans', 'Regulatory Submissions'],
      skills: ['FDA', 'EMA', 'ISO 13485', 'Audit Support', 'Gap Analysis', 'Remediation'],
      domains: ['Pharma', 'Biotech', 'Med Device', 'IVD']
    },
    {
      id: 'commercial',
      num: '10',
      title: 'Commercialization & Post-Market',
      shortTitle: 'Commercial',
      icon: TrendingUp,
      color: '#14b8a6',
      gradient: 'from-teal-400 via-teal-500 to-emerald-600',
      description: 'UPT sustains compliance beyond product launch.',
      subPhases: [
        {
          name: 'Sustainment',
          items: ['Ongoing validation', 'Calibration programs', 'Asset lifecycle management']
        },
        {
          name: 'Surveillance',
          items: ['Post-market monitoring', 'Complaint handling', 'Adverse event management']
        },
        {
          name: 'Optimization',
          items: ['Change management', 'Continuous improvement', 'Process optimization']
        }
      ],
      activities: [
        'Ongoing calibration and validation',
        'Post-market surveillance systems',
        'Complaint handling and CAPA',
        'Change management and optimization',
        'Continuous improvement programs',
        'Annual product reviews'
      ],
      value: [
        'Long-term operational stability',
        'Continuous compliance assurance',
        'Market expansion support',
        'Product lifecycle optimization'
      ],
      deliverables: ['Annual Review Reports', 'CAPA Documentation', 'Change Control Records', 'Continuous Improvement Plans'],
      skills: ['Post-Market', 'CAPA', 'Change Control', 'Continuous Improvement', 'APR'],
      domains: ['Pharma', 'Biotech', 'Med Device', 'IVD']
    }
  ];

  // Skills/Domains matrix data
  const allSkills = [...new Set(lifecycleStages.flatMap(s => s.skills))];
  const allDomains = ['Pharma', 'Biotech', 'Med Device', 'IVD', 'CDMO', 'CRO'];

  return (
    <div className="mt-8 relative perspective-container">
      {/* Animated Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Floating quantum particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1.5 h-1.5 rounded-full quantum-float"
            style={{
              backgroundColor: lifecycleStages[i % 10].color,
              left: `${(i * 11) % 100}%`,
              top: `${(i * 13) % 90}%`,
              animationDelay: `${i * 0.3}s`,
              opacity: 0.5
            }}
          />
        ))}

        {/* Connection lines */}
        <svg className="absolute inset-0 w-full h-full opacity-10">
          {[...Array(5)].map((_, i) => (
            <line
              key={i}
              x1={`${i * 25}%`}
              y1="0%"
              x2={`${(i * 25) + 50}%`}
              y2="100%"
              stroke={lifecycleStages[i * 2].color}
              strokeWidth="1"
              className="circuit-animate"
              style={{ animationDelay: `${i * 0.5}s` }}
            />
          ))}
        </svg>
      </div>

      {/* Main Lifecycle Timeline - Horizontal Scrollable */}
      <div className="relative mb-8">
        {/* Timeline track with gradient */}
        <div className="absolute top-16 left-0 right-0 h-1.5 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-1000 relative"
            style={{
              width: `${((activeStage + 1) / 10) * 100}%`,
              background: `linear-gradient(90deg, ${lifecycleStages[0].color}, ${lifecycleStages[activeStage].color})`
            }}
          >
            {/* Animated shimmer */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-shimmer" />

            {/* Moving energy particles */}
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full warp-line"
                style={{
                  background: `radial-gradient(circle, white 0%, ${lifecycleStages[activeStage].color} 100%)`,
                  animationDelay: `${i * 0.4}s`,
                  boxShadow: `0 0 10px ${lifecycleStages[activeStage].color}`
                }}
              />
            ))}
          </div>
        </div>

        {/* Stage nodes */}
        <div className="flex justify-between items-start relative z-10 px-2">
          {lifecycleStages.map((stage, idx) => (
            <div
              key={stage.id}
              className={`flex flex-col items-center cursor-pointer transition-all duration-500 group ${idx === activeStage ? 'scale-110 z-20' : idx < activeStage ? 'opacity-90' : 'opacity-50 hover:opacity-90'
                }`}
              onClick={() => setActiveStage(idx)}
              style={{ width: '10%' }}
            >
              {/* Glow effect for active */}
              {idx === activeStage && (
                <>
                  <div
                    className="absolute w-20 h-20 rounded-full energy-burst opacity-30"
                    style={{ backgroundColor: stage.color }}
                  />
                  <div
                    className="absolute w-16 h-16 rounded-full animate-pulse opacity-40"
                    style={{ backgroundColor: stage.color }}
                  />
                </>
              )}

              {/* Node */}
              <div
                className={`relative w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 shadow-lg ${idx === activeStage ? 'electric-glow' : 'group-hover:scale-110'
                  }`}
                style={{
                  background: idx <= activeStage
                    ? `linear-gradient(135deg, ${stage.color}, ${stage.color}cc)`
                    : '#e5e7eb',
                  transform: idx === activeStage ? 'rotateY(0deg)' : 'rotateY(0deg)'
                }}
              >
                <stage.icon
                  size={26}
                  className={`${idx <= activeStage ? 'text-white' : 'text-gray-400'} ${idx === activeStage ? 'breathing-icon' : ''}`}
                />

                {/* Stage number */}
                <div
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white flex items-center justify-center text-[10px] font-black shadow-md"
                  style={{ color: stage.color }}
                >
                  {idx + 1}
                </div>
              </div>

              {/* Label */}
              <span className={`mt-2 text-[10px] font-bold text-center leading-tight transition-all ${idx === activeStage ? 'text-gray-900 scale-105' : 'text-gray-500'
                }`}>
                {stage.shortTitle}
              </span>

              {/* Connector line to content */}
              {idx === activeStage && (
                <div
                  className="w-0.5 h-6 mt-1 rounded-full animate-expand-contract"
                  style={{ backgroundColor: stage.color }}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Active Stage Detail Card */}
      <div
        className="relative bg-white rounded-3xl shadow-2xl overflow-hidden ultra-card"
        style={{
          borderTop: `4px solid ${lifecycleStages[activeStage].color}`,
          boxShadow: `0 25px 50px -12px ${lifecycleStages[activeStage].color}20`
        }}
      >
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-[0.03] cyber-grid" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, ${lifecycleStages[activeStage].color} 1px, transparent 0)`,
          backgroundSize: '24px 24px'
        }} />

        <div className="p-8 relative">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-start gap-6 mb-8">
            {/* Animated Icon */}
            <div className="relative">
              {/* Orbital rings */}
              <div className="absolute inset-0 w-24 h-24">
                <div
                  className="absolute inset-2 rounded-full border-2 border-dashed radar-sweep opacity-30"
                  style={{ borderColor: lifecycleStages[activeStage].color }}
                />
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-2 h-2 rounded-full animate-orbit-path"
                    style={{
                      backgroundColor: lifecycleStages[activeStage].color,
                      opacity: 0.6,
                      animationDelay: `${i * 4}s`,
                      animationDuration: '12s'
                    }}
                  />
                ))}
              </div>

              <div
                className={`relative w-24 h-24 rounded-3xl flex items-center justify-center shadow-2xl liquid-morph`}
                style={{
                  background: `linear-gradient(135deg, ${lifecycleStages[activeStage].color}, ${lifecycleStages[activeStage].color}aa)`,
                  boxShadow: `0 20px 40px ${lifecycleStages[activeStage].color}40`
                }}
              >
                {(() => {
                  const StageIcon = lifecycleStages[activeStage].icon;
                  return <StageIcon size={48} className="text-white" />;
                })()}
              </div>
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span
                  className="px-4 py-1.5 rounded-full text-sm font-black text-white neon-pulse"
                  style={{ backgroundColor: lifecycleStages[activeStage].color }}
                >
                  STAGE {lifecycleStages[activeStage].num}
                </span>
                <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">
                  {lifecycleStages[activeStage].domains.join(' • ')}
                </span>
              </div>
              <h3 className="text-3xl lg:text-4xl font-black text-gray-900 mb-2">
                {lifecycleStages[activeStage].title}
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                {lifecycleStages[activeStage].description}
              </p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap gap-2 mb-6 p-1.5 bg-gray-100 rounded-xl">
            {[
              { id: 'phases', label: 'Workflow Phases', icon: Workflow },
              { id: 'activities', label: 'Key Activities', icon: ClipboardCheck },
              { id: 'deliverables', label: 'Deliverables', icon: FileText },
              { id: 'value', label: 'UPT Value', icon: Sparkles },
              { id: 'skills', label: 'Skills & Domains', icon: Layers }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 ${activeTab === tab.id
                  ? 'bg-white shadow-md text-gray-900'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                <tab.icon size={16} style={{ color: activeTab === tab.id ? lifecycleStages[activeStage].color : undefined }} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="min-h-[220px]">
            {/* Workflow Phases Tab - NEW */}
            {activeTab === 'phases' && lifecycleStages[activeStage].subPhases && (
              <div className="space-y-4">
                <div className="flex gap-4 mb-4">
                  {lifecycleStages[activeStage].subPhases.map((phase, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveSubPhase(idx)}
                      className={`flex-1 p-4 rounded-xl border-2 transition-all duration-300 ${activeSubPhase === idx
                        ? 'border-transparent shadow-lg'
                        : 'border-gray-100 hover:border-gray-200'
                        }`}
                      style={{
                        background: activeSubPhase === idx
                          ? `linear-gradient(135deg, ${lifecycleStages[activeStage].color}15, ${lifecycleStages[activeStage].color}05)`
                          : 'white'
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm transition-all ${activeSubPhase === idx ? 'text-white' : ''
                            }`}
                          style={{
                            backgroundColor: activeSubPhase === idx ? lifecycleStages[activeStage].color : `${lifecycleStages[activeStage].color}20`,
                            color: activeSubPhase !== idx ? lifecycleStages[activeStage].color : undefined
                          }}
                        >
                          {idx + 1}
                        </div>
                        <span className={`font-semibold ${activeSubPhase === idx ? 'text-gray-900' : 'text-gray-600'}`}>
                          {phase.name}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Sub-phase content */}
                <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: lifecycleStages[activeStage].color }}
                    >
                      <Workflow size={24} className="text-white" />
                    </div>
                    <div>
                      <h5 className="font-bold text-gray-900">{lifecycleStages[activeStage].subPhases[activeSubPhase].name}</h5>
                      <p className="text-sm text-gray-500">Phase {activeSubPhase + 1} of {lifecycleStages[activeStage].subPhases.length}</p>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-3 gap-3">
                    {lifecycleStages[activeStage].subPhases[activeSubPhase].items.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100 hover:shadow-md transition-all"
                      >
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${lifecycleStages[activeStage].color}15` }}
                        >
                          <CheckCircle2 size={14} style={{ color: lifecycleStages[activeStage].color }} />
                        </div>
                        <span className="text-sm text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Phase flow indicator */}
                <div className="flex items-center justify-center gap-2 mt-4">
                  {lifecycleStages[activeStage].subPhases.map((_, idx) => (
                    <div key={idx} className="flex items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${idx <= activeSubPhase ? 'text-white' : 'text-gray-400 bg-gray-100'
                          }`}
                        style={{
                          backgroundColor: idx <= activeSubPhase ? lifecycleStages[activeStage].color : undefined
                        }}
                      >
                        {idx + 1}
                      </div>
                      {idx < lifecycleStages[activeStage].subPhases.length - 1 && (
                        <div
                          className="w-12 h-1 rounded-full mx-1"
                          style={{
                            backgroundColor: idx < activeSubPhase ? lifecycleStages[activeStage].color : '#e5e7eb'
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Activities Tab */}
            {activeTab === 'activities' && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
                {lifecycleStages[activeStage].activities.map((activity, idx) => (
                  <div
                    key={idx}
                    className="group p-4 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100 hover:border-transparent hover:shadow-xl transition-all duration-300 cursor-pointer ultra-card"
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform heartbeat-scale"
                        style={{ backgroundColor: `${lifecycleStages[activeStage].color}15` }}
                      >
                        <CheckCircle2 size={16} style={{ color: lifecycleStages[activeStage].color }} />
                      </div>
                      <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{activity}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Value Tab */}
            {activeTab === 'value' && (
              <div className="space-y-4 stagger-children">
                {lifecycleStages[activeStage].value.map((val, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-4 p-5 rounded-2xl border-2 border-gray-100 hover:border-transparent transition-all duration-300 ultra-card"
                    style={{
                      background: `linear-gradient(135deg, ${lifecycleStages[activeStage].color}05, white)`,
                    }}
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: lifecycleStages[activeStage].color }}
                    >
                      <Sparkles size={24} className="text-white" />
                    </div>
                    <span className="text-base font-semibold text-gray-800">{val}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Deliverables Tab - NEW */}
            {activeTab === 'deliverables' && lifecycleStages[activeStage].deliverables && (
              <div className="grid sm:grid-cols-2 gap-4">
                {lifecycleStages[activeStage].deliverables.map((deliverable, idx) => (
                  <div
                    key={idx}
                    className="group flex items-center gap-4 p-5 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:border-transparent hover:shadow-lg transition-all duration-300"
                  >
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                      style={{
                        background: `linear-gradient(135deg, ${lifecycleStages[activeStage].color}, ${lifecycleStages[activeStage].color}cc)`
                      }}
                    >
                      <FileText size={24} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <span className="font-semibold text-gray-900 block">{deliverable}</span>
                      <span className="text-xs text-gray-500">Documentation Package</span>
                    </div>
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 group-hover:bg-white transition-colors"
                    >
                      <Download size={14} className="text-gray-400 group-hover:text-gray-700" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Skills & Domains Tab */}
            {activeTab === 'skills' && (
              <div className="space-y-6">
                {/* Skills */}
                <div>
                  <h5 className="text-sm font-bold text-gray-500 mb-3 flex items-center gap-2">
                    <Zap size={14} style={{ color: lifecycleStages[activeStage].color }} />
                    CORE COMPETENCIES
                  </h5>
                  <div className="flex flex-wrap gap-2">
                    {lifecycleStages[activeStage].skills.map((skill, idx) => (
                      <span
                        key={idx}
                        className="px-4 py-2 rounded-full text-sm font-semibold text-white magnetic-hover cursor-pointer"
                        style={{
                          background: `linear-gradient(135deg, ${lifecycleStages[activeStage].color}, ${lifecycleStages[activeStage].color}cc)`,
                          boxShadow: `0 4px 15px ${lifecycleStages[activeStage].color}30`
                        }}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Domain Coverage */}
                <div>
                  <h5 className="text-sm font-bold text-gray-500 mb-3 flex items-center gap-2">
                    <Globe2 size={14} style={{ color: lifecycleStages[activeStage].color }} />
                    INDUSTRY COVERAGE
                  </h5>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                    {allDomains.map((domain, idx) => {
                      const isActive = lifecycleStages[activeStage].domains.includes(domain) || lifecycleStages[activeStage].domains.includes('All Industries');
                      return (
                        <div
                          key={idx}
                          className={`p-3 rounded-xl text-center text-xs font-semibold transition-all duration-300 ${isActive
                            ? 'bg-gradient-to-br text-white shadow-lg scale-105'
                            : 'bg-gray-100 text-gray-400'
                            }`}
                          style={{
                            background: isActive ? `linear-gradient(135deg, ${lifecycleStages[activeStage].color}, ${lifecycleStages[activeStage].color}cc)` : undefined
                          }}
                        >
                          {domain}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setActiveStage(prev => prev > 0 ? prev - 1 : 9)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-semibold"
              >
                <ChevronRight size={16} className="rotate-180" />
                Previous
              </button>
              <button
                onClick={() => setActiveStage(prev => (prev + 1) % 10)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all text-sm font-semibold text-white magnetic-hover"
                style={{ backgroundColor: lifecycleStages[activeStage].color }}
              >
                Next Stage
                <ChevronRight size={16} />
              </button>
            </div>

            {/* Progress dots */}
            <div className="flex items-center gap-1.5">
              {lifecycleStages.map((stage, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveStage(idx)}
                  className={`h-2 rounded-full transition-all duration-300 ${idx === activeStage ? 'w-6' : 'w-2 hover:scale-125'
                    }`}
                  style={{
                    backgroundColor: idx <= activeStage ? stage.color : '#e5e7eb'
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Lifecycle Integration Banner */}
      <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-[#0a1525] via-[#0d2137] to-[#0a1525] text-white relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-20">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="absolute w-32 h-32 rounded-full blur-3xl animate-pulse"
              style={{
                backgroundColor: lifecycleStages[i].color,
                left: `${i * 10}%`,
                top: `${(i % 2) * 50}%`,
                animationDelay: `${i * 0.3}s`
              }}
            />
          ))}
        </div>

        <div className="relative z-10 text-center">
          <h4 className="text-xl font-black mb-2 animate-text-shimmer">How the Lifecycle Connects</h4>
          <p className="text-gray-300 text-sm mb-4">
            Calibration underpins data accuracy • CQV ensures system reliability • Automation enforces consistency • QC confirms product quality • Clinical data proves safety and efficacy
          </p>
          <div className="flex justify-center gap-3 flex-wrap">
            {lifecycleStages.slice(0, 5).map((stage, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{ backgroundColor: `${stage.color}30`, color: stage.color }}
              >
                <stage.icon size={12} />
                {stage.shortTitle}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced Industries Section with Lifecycle Workflow
const IndustriesSection = () => {
  const [selectedIndustry, setSelectedIndustry] = useState(0);
  const [activeStage, setActiveStage] = useState(2);
  const [isHovered, setIsHovered] = useState(false);

  // Auto-cycle through industries
  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setSelectedIndustry((prev) => (prev + 1) % 4);
        setActiveStage(2); // Reset to stage 3 on industry change
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [isHovered]);

  // Auto-cycle through lifecycle stages
  useEffect(() => {
    const stageInterval = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % 10);
    }, 2500);
    return () => clearInterval(stageInterval);
  }, [selectedIndustry]);

  // Industry-specific lifecycles
  const industryLifecycles = {
    // Pharmaceuticals Lifecycle
    0: [
      { num: "01", phase: "R&D", icon: FlaskConical, color: "#0ea5e9" },
      { num: "02", phase: "Preclinical", icon: Dna, color: "#8b5cf6" },
      { num: "03", phase: "Clinical", icon: Stethoscope, color: "#ec4899" },
      { num: "04", phase: "Manufacturing", icon: Factory, color: "#f59e0b" },
      { num: "05", phase: "QC Labs", icon: Microscope, color: "#10b981" },
      { num: "06", phase: "Calibration", icon: Gauge, color: "#06b6d4" },
      { num: "07", phase: "CQV", icon: ShieldCheck, color: "#0ea5e9" },
      { num: "08", phase: "Automation", icon: Cpu, color: "#8b5cf6" },
      { num: "09", phase: "Regulatory", icon: FileCheck, color: "#10b981" },
      { num: "10", phase: "Commercial", icon: TrendingUp, color: "#ec4899" }
    ],
    // Biotechnology Lifecycle
    1: [
      { num: "01", phase: "Discovery", icon: Lightbulb, color: "#0ea5e9" },
      { num: "02", phase: "Cell Line", icon: Dna, color: "#10b981" },
      { num: "03", phase: "Process Dev", icon: Beaker, color: "#8b5cf6" },
      { num: "04", phase: "Scale-Up", icon: TrendingUp, color: "#f59e0b" },
      { num: "05", phase: "Tech Transfer", icon: Truck, color: "#06b6d4" },
      { num: "06", phase: "Validation", icon: ShieldCheck, color: "#ec4899" },
      { num: "07", phase: "Manufacturing", icon: Factory, color: "#10b981" },
      { num: "08", phase: "QC Testing", icon: Microscope, color: "#0ea5e9" },
      { num: "09", phase: "Release", icon: CheckCircle2, color: "#8b5cf6" },
      { num: "10", phase: "Commercial", icon: Rocket, color: "#ec4899" }
    ],
    // Medical Devices & IVD Lifecycle
    2: [
      { num: "01", phase: "Concept", icon: Lightbulb, color: "#8b5cf6" },
      { num: "02", phase: "Design", icon: PenTool, color: "#0ea5e9" },
      { num: "03", phase: "Development", icon: Settings, color: "#10b981" },
      { num: "04", phase: "Verification", icon: CheckCircle2, color: "#f59e0b" },
      { num: "05", phase: "Validation", icon: ShieldCheck, color: "#ec4899" },
      { num: "06", phase: "Manufacturing", icon: Factory, color: "#06b6d4" },
      { num: "07", phase: "QMS", icon: ClipboardCheck, color: "#8b5cf6" },
      { num: "08", phase: "Regulatory", icon: FileCheck, color: "#10b981" },
      { num: "09", phase: "Launch", icon: Rocket, color: "#0ea5e9" },
      { num: "10", phase: "Post-Market", icon: Eye, color: "#ec4899" }
    ],
    // Diagnostics Lifecycle
    3: [
      { num: "01", phase: "R&D", icon: FlaskConical, color: "#0ea5e9" },
      { num: "02", phase: "Preclinical", icon: Dna, color: "#8b5cf6" },
      { num: "03", phase: "Clinical", icon: Stethoscope, color: "#ec4899" },
      { num: "04", phase: "Manufacturing", icon: Factory, color: "#f59e0b" },
      { num: "05", phase: "QC Labs", icon: Microscope, color: "#10b981" },
      { num: "06", phase: "Calibration", icon: Gauge, color: "#06b6d4" },
      { num: "07", phase: "CQV", icon: ShieldCheck, color: "#dc2626" },
      { num: "08", phase: "Automation", icon: Cpu, color: "#8b5cf6" },
      { num: "09", phase: "Regulatory", icon: FileCheck, color: "#10b981" },
      { num: "10", phase: "Commercial", icon: TrendingUp, color: "#ec4899" }
    ]
  };

  // Get current lifecycle based on selected industry
  const lifecycleStages = industryLifecycles[selectedIndustry];

  const industries = [
    {
      icon: Pill,
      title: "Pharmaceuticals",
      description: "Comprehensive support across the drug development lifecycle, from clinical trials to commercial operations.",
      services: ["Process Validation", "Equipment Qualification", "CSV Implementation", "Regulatory Support"],
      metric: "500+ Projects",
      color: "#0369a1"
    },
    {
      icon: Dna,
      title: "Biotechnology",
      description: "Specialized expertise in cell & gene therapy, biologics, and advanced therapeutic platforms.",
      services: ["Cell Therapy Support", "Biologics Validation", "Aseptic Processing", "Tech Transfer"],
      metric: "200+ Projects",
      color: "#047857"
    },
    {
      icon: Cpu,
      title: "Medical Devices & IVD",
      description: "End-to-end device lifecycle services from design controls through post-market compliance.",
      services: ["Design Controls", "Process Validation", "EU MDR/IVDR", "Software Validation"],
      metric: "350+ Projects",
      color: "#7c3aed"
    },
    {
      icon: Microscope,
      title: "Diagnostics",
      description: "Expert validation and compliance services for IVD and laboratory equipment manufacturers.",
      services: ["IVD Validation", "Lab Equipment QC", "Method Validation", "Data Integrity"],
      metric: "150+ Projects",
      color: "#dc2626"
    }
  ];

  return (
    <section id="industries" className="py-28 bg-white relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, #0ea5e9 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }}></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#0ea5e9]/10 to-[#10b981]/10 rounded-full mb-6">
            <Globe2 size={18} className="text-[#10b981]" />
            <span className="text-sm font-bold text-[#10b981] tracking-wider">INDUSTRIES WE SERVE</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6">
            Deep Domain{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#8b5cf6]">
              Expertise
            </span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Specialized knowledge across highly regulated life sciences sectors
          </p>
        </div>

        {/* All Industries Grid - Auto-cycling */}
        <div
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {industries.map((industry, idx) => {
            const isActive = selectedIndustry === idx;
            return (
              <div
                key={idx}
                onClick={() => setSelectedIndustry(idx)}
                className={`group relative bg-white rounded-2xl border-2 p-6 transition-all duration-500 cursor-pointer ${isActive
                  ? 'shadow-2xl scale-[1.02] border-transparent z-10'
                  : 'border-gray-100 hover:shadow-xl hover:border-transparent'
                  }`}
              >
                {/* Glow effect when active or hovered */}
                <div
                  className={`absolute -inset-1 rounded-2xl blur-xl transition-opacity -z-10 ${isActive ? 'opacity-25' : 'opacity-0 group-hover:opacity-20'
                    }`}
                  style={{ backgroundColor: industry.color }}
                />

                {/* Animated ping effect when active */}
                {isActive && (
                  <div
                    className="absolute -inset-1 rounded-2xl animate-ping opacity-10 -z-20"
                    style={{ backgroundColor: industry.color, animationDuration: '2s' }}
                  />
                )}

                {/* Top accent bar - glows when active */}
                <div
                  className={`absolute top-0 left-0 right-0 h-1.5 rounded-t-2xl transition-all duration-500 ${isActive ? 'h-2' : ''
                    }`}
                  style={{ background: `linear-gradient(90deg, ${industry.color}, ${industry.color}80)` }}
                />

                {/* Icon */}
                <div
                  className={`w-16 h-16 rounded-xl flex items-center justify-center mb-4 transition-all duration-500 ${isActive ? 'scale-110 shadow-lg' : 'group-hover:scale-110'
                    }`}
                  style={{
                    backgroundColor: isActive ? industry.color : `${industry.color}15`,
                  }}
                >
                  <industry.icon size={32} style={{ color: isActive ? 'white' : industry.color }} />
                </div>

                {/* Title */}
                <h3 className={`text-xl font-bold mb-3 transition-colors duration-300 ${isActive ? '' : 'text-gray-900 group-hover:text-[#10b981]'
                  }`} style={{ color: isActive ? industry.color : undefined }}>
                  {industry.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                  {industry.description}
                </p>

                {/* Services List */}
                <div className="space-y-2">
                  {industry.services.map((service, sidx) => (
                    <div key={sidx} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle2 size={14} style={{ color: industry.color }} />
                      <span>{service}</span>
                    </div>
                  ))}
                </div>

                {/* Bottom accent - shows when active */}
                <div
                  className={`absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl transition-transform duration-500 origin-left ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                    }`}
                  style={{ background: `linear-gradient(90deg, ${industry.color}, ${industry.color}60)` }}
                />
              </div>
            );
          })}
        </div>

        {/* UPT Industry Lifecycle Coverage - 10 Stage Timeline */}
        <div className="mt-16 relative">
          <div className="relative bg-white rounded-3xl border-2 border-gray-100 p-8 lg:p-10 shadow-xl overflow-hidden">
            {/* Background glow for active stage */}
            <div
              className="absolute top-0 left-0 right-0 bottom-0 opacity-5 transition-all duration-700"
              style={{
                background: `radial-gradient(circle at ${(activeStage + 0.5) * 10}% 50%, ${lifecycleStages[activeStage].color} 0%, transparent 50%)`
              }}
            />

            {/* Header */}
            <div className="text-center mb-10 relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#0ea5e9]/10 to-[#ec4899]/10 rounded-full mb-4">
                <Workflow size={16} className="text-[#ec4899]" />
                <span className="text-xs font-bold text-[#ec4899] tracking-wider">UPT INDUSTRY LIFECYCLE COVERAGE</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                {industries[selectedIndustry].title}{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] via-[#ec4899] to-[#8b5cf6]">
                  Lifecycle
                </span>
              </h3>
              <p className="text-gray-500 text-sm mt-2">Complete lifecycle support from R&D to Commercialization</p>
            </div>

            {/* 10-Stage Horizontal Timeline */}
            <div className="relative px-4">
              {/* Main Connection Line with Glow */}
              <div className="absolute top-10 left-8 right-8 h-1 bg-gray-200 rounded-full">
                {/* Animated gradient line */}
                <div
                  className="absolute h-full rounded-full transition-all duration-500"
                  style={{
                    left: 0,
                    width: `${((activeStage + 1) / 10) * 100}%`,
                    background: `linear-gradient(90deg, #0ea5e9, ${lifecycleStages[activeStage].color})`
                  }}
                />
                {/* Glowing dot that moves */}
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full transition-all duration-500"
                  style={{
                    left: `${((activeStage + 0.5) / 10) * 100}%`,
                    backgroundColor: lifecycleStages[activeStage].color,
                    boxShadow: `0 0 20px ${lifecycleStages[activeStage].color}, 0 0 40px ${lifecycleStages[activeStage].color}`
                  }}
                />
              </div>

              {/* Lifecycle Stages */}
              <div className="relative flex justify-between">
                {lifecycleStages.map((stage, idx) => {
                  const StageIcon = stage.icon;
                  const isActive = activeStage === idx;
                  const isPast = idx < activeStage;

                  return (
                    <div
                      key={idx}
                      className="flex flex-col items-center cursor-pointer group"
                      onClick={() => setActiveStage(idx)}
                    >
                      {/* Icon Circle with Glow */}
                      <div className="relative">
                        {/* Outer glow rings for active */}
                        {isActive && (
                          <>
                            <div
                              className="absolute -inset-4 rounded-full animate-ping opacity-20"
                              style={{ backgroundColor: stage.color }}
                            />
                            <div
                              className="absolute -inset-6 rounded-full opacity-10"
                              style={{ backgroundColor: stage.color, filter: 'blur(8px)' }}
                            />
                          </>
                        )}

                        <div
                          className={`relative w-14 h-14 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center transition-all duration-500 z-10 ${isActive
                            ? 'scale-110 shadow-2xl'
                            : isPast
                              ? 'bg-white shadow-md'
                              : 'bg-gray-50 shadow-sm'
                            }`}
                          style={{
                            backgroundColor: isActive ? stage.color : isPast ? 'white' : undefined,
                            borderWidth: '2px',
                            borderColor: isActive ? stage.color : isPast ? stage.color : '#e5e7eb',
                            boxShadow: isActive ? `0 10px 40px ${stage.color}50` : undefined
                          }}
                        >
                          <StageIcon
                            size={22}
                            style={{ color: isActive ? 'white' : stage.color }}
                            className="transition-all duration-300"
                          />
                        </div>
                      </div>

                      {/* Stage Number */}
                      <div
                        className={`mt-3 text-[10px] font-bold transition-all duration-300 ${isActive ? 'opacity-100' : 'opacity-50'
                          }`}
                        style={{ color: isActive ? stage.color : '#9ca3af' }}
                      >
                        {stage.num}
                      </div>

                      {/* Phase Label */}
                      <div
                        className={`text-xs font-semibold transition-all duration-300 ${isActive ? 'text-gray-900' : 'text-gray-400'
                          }`}
                      >
                        {stage.phase}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Active Stage Details */}
            <div
              className="mt-10 p-6 rounded-2xl transition-all duration-500 relative overflow-hidden"
              style={{ backgroundColor: `${lifecycleStages[activeStage].color}10` }}
            >
              {/* Decorative gradient */}
              <div
                className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-30"
                style={{ backgroundColor: lifecycleStages[activeStage].color }}
              />

              <div className="relative z-10 flex items-center gap-6">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
                  style={{ backgroundColor: lifecycleStages[activeStage].color }}
                >
                  {(() => {
                    const Icon = lifecycleStages[activeStage].icon;
                    return <Icon size={28} className="text-white" />;
                  })()}
                </div>
                <div>
                  <div
                    className="text-xs font-bold tracking-wider mb-1"
                    style={{ color: lifecycleStages[activeStage].color }}
                  >
                    STAGE {lifecycleStages[activeStage].num}
                  </div>
                  <h4 className="text-xl font-bold text-gray-900">
                    {lifecycleStages[activeStage].phase} Lifecycle
                  </h4>
                  <p className="text-gray-600 text-sm mt-1">
                    Comprehensive {lifecycleStages[activeStage].phase.toLowerCase()} support for {industries[selectedIndustry].title.toLowerCase()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-10 max-w-xl mx-auto h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700 relative overflow-hidden"
            style={{
              width: `${((selectedIndustry + 1) / industries.length) * 100}%`,
              background: `linear-gradient(90deg, ${industries[0].color}, ${industries[selectedIndustry].color})`
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
          </div>
        </div>

        {/* Navigation Dots */}
        <div className="flex justify-center items-center mt-6 gap-2">
          {industries.map((industry, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedIndustry(idx)}
              className={`h-2.5 rounded-full transition-all duration-500 ${selectedIndustry === idx ? 'w-10' : 'w-2.5 hover:scale-125'
                }`}
              style={{ backgroundColor: selectedIndustry === idx ? industry.color : '#e5e7eb' }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Service Approach Section - Vertical Timeline with Wow Effects
const ApproachSection = () => {
  const [activeStep, setActiveStep] = useState(0);

  const phases = [
    {
      num: "01",
      title: "Discover",
      subtitle: "Assessment & Analysis",
      desc: "Comprehensive evaluation of your current state, identifying gaps and opportunities.",
      items: ["Gap Analysis", "Risk Assessment", "Requirements", "Alignment"],
      icon: Lightbulb,
      color: '#0ea5e9'
    },
    {
      num: "02",
      title: "Develop",
      subtitle: "Strategy & Planning",
      desc: "Tailored solutions and detailed implementation roadmaps aligned with your objectives.",
      items: ["Solution Design", "Resource Planning", "Timeline", "Risk Mitigation"],
      icon: Target,
      color: '#10b981'
    },
    {
      num: "03",
      title: "Deliver",
      subtitle: "Implementation",
      desc: "Expert delivery with dedicated project management ensuring quality outcomes.",
      items: ["Expert Teams", "Project Management", "Quality Assurance", "Tracking"],
      icon: Rocket,
      color: '#8b5cf6'
    },
    {
      num: "04",
      title: "Lead",
      subtitle: "Continuous Growth",
      desc: "Ongoing partnership for sustained compliance excellence and improvement.",
      items: ["Training", "Audit Support", "Improvement", "Knowledge Transfer"],
      icon: Award,
      color: '#ec4899'
    }
  ];

  // Auto-cycle through phases
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % phases.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [phases.length]);

  return (
    <section id="solutions" className="py-24 bg-white relative overflow-hidden">
      {/* Subtle background */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, #10b981 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }}></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#0ea5e9]/10 to-[#10b981]/10 rounded-full mb-6">
            <Workflow size={18} className="text-[#10b981]" />
            <span className="text-sm font-bold text-[#10b981] tracking-wider">OUR APPROACH</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
            Proven{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#8b5cf6]">
              Methodology
            </span>
          </h2>
          <p className="text-lg text-gray-600">
            A structured approach ensuring successful outcomes from assessment to ongoing partnership.
          </p>
        </div>

        {/* Horizontal Timeline with Auto-Cycling */}
        <div className="relative">
          {/* Progress Line */}
          <div className="hidden md:block absolute top-20 left-0 right-0 h-1 bg-gray-100 rounded-full">
            <div
              className="h-full bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#8b5cf6] rounded-full transition-all duration-700 ease-out"
              style={{ width: `${((activeStep + 1) / phases.length) * 100}%` }}
            />
            {/* Animated glow */}
            <div
              className="absolute top-0 h-full bg-gradient-to-r from-[#0ea5e9] to-[#10b981] rounded-full blur-sm opacity-50 transition-all duration-700"
              style={{ width: `${((activeStep + 1) / phases.length) * 100}%` }}
            />
          </div>

          {/* Horizontal Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {phases.map((phase, idx) => {
              const isActive = activeStep === idx;
              const isPast = idx < activeStep;
              const PhaseIcon = phase.icon;

              return (
                <div
                  key={idx}
                  onClick={() => setActiveStep(idx)}
                  className={`relative cursor-pointer transition-all duration-500 ${isActive ? 'transform scale-105 z-10' : ''}`}
                >
                  {/* Step Number Circle */}
                  <div className="flex justify-center mb-6">
                    <div
                      className={`relative w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg transition-all duration-500 ${isActive
                        ? 'text-white shadow-lg scale-125'
                        : isPast
                          ? 'bg-gray-200 text-gray-600'
                          : 'bg-gray-100 text-gray-400'
                        }`}
                      style={{
                        backgroundColor: isActive ? phase.color : undefined,
                        boxShadow: isActive ? `0 10px 30px ${phase.color}40` : undefined
                      }}
                    >
                      {isActive && (
                        <div className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ backgroundColor: phase.color }} />
                      )}
                      {phase.num}
                    </div>
                  </div>

                  {/* Card */}
                  <div
                    className={`relative bg-white rounded-2xl p-6 border-2 transition-all duration-500 overflow-hidden ${isActive
                      ? 'shadow-2xl border-transparent'
                      : 'border-gray-100 hover:border-gray-200 hover:shadow-lg'
                      }`}
                    style={{ borderColor: isActive ? `${phase.color}40` : undefined }}
                  >
                    {/* Active glow */}
                    {isActive && (
                      <div className="absolute -inset-1 rounded-2xl blur-xl opacity-20 -z-10" style={{ backgroundColor: phase.color }} />
                    )}

                    {/* Top accent */}
                    <div
                      className={`absolute top-0 left-0 right-0 h-1 transition-all duration-500 ${isActive ? 'opacity-100' : 'opacity-30'}`}
                      style={{ background: `linear-gradient(90deg, ${phase.color}, ${phase.color}80)` }}
                    />

                    {/* Icon */}
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 ${isActive ? 'scale-110' : ''}`}
                      style={{ backgroundColor: `${phase.color}15` }}
                    >
                      <PhaseIcon size={24} style={{ color: phase.color }} />
                    </div>

                    {/* Title */}
                    <h3 className={`text-xl font-bold mb-1 transition-colors duration-300 ${isActive ? '' : 'text-gray-900'}`} style={{ color: isActive ? phase.color : undefined }}>
                      {phase.title}
                    </h3>
                    <p className="text-sm text-gray-500 mb-3">{phase.subtitle}</p>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-4">{phase.desc}</p>

                    {/* Items */}
                    <div className="space-y-2">
                      {phase.items.map((item, iidx) => (
                        <div key={iidx} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle2 size={14} style={{ color: phase.color }} />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Progress Dots */}
        <div className="flex justify-center items-center mt-10 gap-2">
          {phases.map((phase, idx) => (
            <button
              key={idx}
              onClick={() => setActiveStep(idx)}
              className={`h-2.5 rounded-full transition-all duration-500 ${activeStep === idx ? 'w-10' : 'w-2.5 hover:scale-125'
                }`}
              style={{ backgroundColor: activeStep === idx ? phase.color : '#e5e7eb' }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// How UPT Delivers - Capabilities Section
const CapabilitiesSection = () => {
  const [activeCapability, setActiveCapability] = useState(0);
  const [hoveredItem, setHoveredItem] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveCapability(prev => (prev + 1) % 4);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const capabilities = [
    {
      id: 'lifecycle',
      title: 'Lifecycle Ownership',
      subtitle: 'End-to-End Accountability',
      icon: Workflow,
      color: '#0ea5e9',
      gradient: 'from-sky-500 to-blue-600',
      description: 'We take full ownership of your compliance journey—from initial assessment through sustained excellence.',
      keyPoints: [
        {
          title: 'Project Inception to Closeout',
          desc: 'Complete project management from kickoff through final deliverables and knowledge transfer',
          icon: Rocket
        },
        {
          title: 'Validation Lifecycle Stewardship',
          desc: 'Continuous oversight of validation activities ensuring ongoing compliance and optimization',
          icon: Shield
        },
        {
          title: 'Documentation Governance',
          desc: 'Rigorous document control maintaining audit-ready records throughout the engagement',
          icon: FileText
        },
        {
          title: 'Handover & Knowledge Transfer',
          desc: 'Structured transition ensuring your team is equipped to maintain compliance independently',
          icon: Handshake
        }
      ],
      stats: { value: '2800+', label: 'Projects Managed' }
    },
    {
      id: 'digital',
      title: 'Digital Enablement',
      subtitle: 'Technology-Driven Excellence',
      icon: CircuitBoard,
      color: '#8b5cf6',
      gradient: 'from-violet-500 to-purple-600',
      description: 'We leverage cutting-edge technology to deliver faster, more accurate, and more cost-effective solutions.',
      keyPoints: [
        {
          title: 'AI-Assisted Protocol Development',
          desc: 'Accelerated document creation with intelligent templates and automated content generation',
          icon: Sparkles
        },
        {
          title: 'Digital Twin Simulations',
          desc: 'Virtual modeling of systems and processes to optimize design before implementation',
          icon: Cpu
        },
        {
          title: 'Real-Time Dashboards & Analytics',
          desc: 'Live project visibility with predictive insights for proactive decision-making',
          icon: BarChart3
        },
        {
          title: 'Automated Compliance Monitoring',
          desc: 'Continuous system surveillance with intelligent alerts for deviation detection',
          icon: Activity
        }
      ],
      stats: { value: '40%', label: 'Faster Delivery' }
    },
    {
      id: 'quality',
      title: 'Quality & Risk Management',
      subtitle: 'Proactive Excellence',
      icon: Shield,
      color: '#10b981',
      gradient: 'from-emerald-500 to-green-600',
      description: 'Our embedded quality framework ensures risk-based approaches and continuous compliance assurance.',
      keyPoints: [
        {
          title: 'ICH Q9/Q10 Risk Frameworks',
          desc: 'Industry-standard risk management methodologies integrated into every engagement',
          icon: Target
        },
        {
          title: 'Proactive Issue Identification',
          desc: 'Early detection systems preventing costly deviations and regulatory findings',
          icon: Lightbulb
        },
        {
          title: 'CAPA Excellence Programs',
          desc: 'Systematic corrective action processes ensuring root cause elimination',
          icon: ClipboardCheck
        },
        {
          title: 'Audit Defense Preparation',
          desc: 'Comprehensive inspection readiness ensuring successful regulatory outcomes',
          icon: BadgeCheck
        }
      ],
      stats: { value: '99.8%', label: 'Success Rate' }
    },
    {
      id: 'enterprise',
      title: 'Enterprise Delivery Model',
      subtitle: 'Scalable Partnership',
      icon: Building2,
      color: '#f59e0b',
      gradient: 'from-amber-500 to-orange-600',
      description: 'Our flexible engagement models scale to meet your needs—from single projects to enterprise-wide transformations.',
      keyPoints: [
        {
          title: 'Dedicated Resource Teams',
          desc: 'Embedded experts functioning as extensions of your organization',
          icon: Users
        },
        {
          title: 'Project-Based Engagements',
          desc: 'Defined scope delivery with clear milestones and fixed outcomes',
          icon: Target
        },
        {
          title: 'Managed Services Programs',
          desc: 'Ongoing operational support with SLA-backed performance guarantees',
          icon: Settings2
        },
        {
          title: 'Global Delivery Capability',
          desc: '12+ locations providing 24/7 support across time zones',
          icon: Globe2
        }
      ],
      stats: { value: '98%', label: 'Client Retention' }
    }
  ];

  return (
    <section id="capabilities" className="py-28 bg-gradient-to-b from-gray-50 via-white to-gray-50 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        {/* Gradient orbs */}
        <div className="absolute top-20 left-0 w-96 h-96 bg-[#0ea5e9]/5 rounded-full blur-[120px] animate-morph-blob" />
        <div className="absolute bottom-20 right-0 w-80 h-80 bg-[#10b981]/5 rounded-full blur-[100px] animate-morph-blob" style={{ animationDelay: '7s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#8b5cf6]/3 rounded-full blur-[150px]" />

        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #000 1px, transparent 0)`,
          backgroundSize: '50px 50px'
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#0369a1]/10 to-[#047857]/10 rounded-full mb-6">
            <Zap size={18} className="text-[#047857]" />
            <span className="text-sm font-bold text-[#047857] tracking-wider">HOW UPT DELIVERS</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6">
            Our Execution{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0369a1] via-[#047857] to-[#10b981] animate-gradient-shift" style={{ backgroundSize: '200% 200%' }}>
              Model
            </span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            A proven delivery framework combining deep domain expertise, cutting-edge technology, and unwavering commitment to quality.
          </p>
        </div>

        {/* Capability Navigation - Horizontal Flow */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-4 lg:gap-6">
            {capabilities.map((cap, idx) => (
              <button
                key={cap.id}
                onClick={() => setActiveCapability(idx)}
                className={`group relative flex items-center gap-3 px-6 py-4 rounded-2xl font-semibold transition-all duration-500 ${activeCapability === idx
                  ? 'bg-white shadow-2xl scale-105 border-2'
                  : 'bg-white/50 hover:bg-white hover:shadow-lg border border-gray-100'
                  }`}
                style={{ borderColor: activeCapability === idx ? cap.color : undefined }}
              >
                {/* Active indicator glow */}
                {activeCapability === idx && (
                  <div
                    className="absolute -inset-1 rounded-2xl blur-xl opacity-20 animate-pulse"
                    style={{ backgroundColor: cap.color }}
                  />
                )}

                <div
                  className={`relative w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-500 ${activeCapability === idx ? '' : 'group-hover:scale-110'
                    }`}
                  style={{
                    backgroundColor: activeCapability === idx ? cap.color : `${cap.color}15`,
                  }}
                >
                  <cap.icon
                    size={24}
                    className={activeCapability === idx ? 'text-white' : ''}
                    style={{ color: activeCapability !== idx ? cap.color : undefined }}
                  />
                </div>

                <div className="text-left">
                  <span className={`block font-bold transition-colors ${activeCapability === idx ? 'text-gray-900' : 'text-gray-700'
                    }`}>
                    {cap.title}
                  </span>
                  <span className="text-xs text-gray-500">{cap.subtitle}</span>
                </div>

                {/* Connection line to next */}
                {idx < capabilities.length - 1 && (
                  <div className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 w-8">
                    <div className={`h-0.5 rounded-full transition-all duration-500 ${activeCapability > idx ? 'bg-gradient-to-r' : 'bg-gray-200'
                      }`} style={{
                        background: activeCapability > idx ? `linear-gradient(90deg, ${cap.color}, ${capabilities[idx + 1].color})` : undefined
                      }}>
                      {activeCapability === idx && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent animate-shimmer" />
                      )}
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Progress bar */}
          <div className="mt-8 max-w-3xl mx-auto h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700 relative overflow-hidden"
              style={{
                width: `${((activeCapability + 1) / capabilities.length) * 100}%`,
                background: `linear-gradient(90deg, ${capabilities[0].color}, ${capabilities[activeCapability].color})`
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
            </div>
          </div>
        </div>

        {/* Active Capability Detail */}
        <div className="relative">
          {/* Glow background */}
          <div
            className="absolute -inset-4 rounded-3xl blur-3xl opacity-10 transition-colors duration-700"
            style={{ backgroundColor: capabilities[activeCapability].color }}
          />

          <div
            className="relative bg-white rounded-3xl border-2 shadow-2xl overflow-hidden transition-all duration-500"
            style={{ borderColor: `${capabilities[activeCapability].color}30` }}
          >
            {/* Top accent bar with animation */}
            <div
              className="h-2 w-full relative overflow-hidden"
              style={{ background: `linear-gradient(90deg, ${capabilities[activeCapability].color}, ${capabilities[(activeCapability + 1) % 4].color})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
            </div>

            <div className="p-8 lg:p-12">
              {/* Header */}
              <div className="flex flex-col lg:flex-row lg:items-start gap-8 mb-12">
                {/* Icon with orbital animation */}
                <div className="relative">
                  <div className="absolute inset-0 w-28 h-28">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="absolute w-3 h-3 rounded-full animate-orbit-path"
                        style={{
                          backgroundColor: capabilities[activeCapability].color,
                          opacity: 0.4,
                          animationDelay: `${i * 3.3}s`,
                          animationDuration: '12s'
                        }}
                      />
                    ))}
                  </div>

                  <div
                    className="relative w-28 h-28 rounded-3xl flex items-center justify-center shadow-2xl animate-float-card"
                    style={{
                      background: `linear-gradient(135deg, ${capabilities[activeCapability].color}, ${capabilities[activeCapability].color}cc)`,
                      boxShadow: `0 25px 50px ${capabilities[activeCapability].color}40`
                    }}
                  >
                    {(() => {
                      const CapIcon = capabilities[activeCapability].icon;
                      return <CapIcon size={56} className="text-white" />;
                    })()}
                  </div>
                </div>

                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-3">
                    <span
                      className="px-4 py-1.5 rounded-full text-sm font-bold text-white"
                      style={{ backgroundColor: capabilities[activeCapability].color }}
                    >
                      Capability {activeCapability + 1} of {capabilities.length}
                    </span>
                    <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-gray-100 text-gray-600">
                      {capabilities[activeCapability].stats.value} {capabilities[activeCapability].stats.label}
                    </span>
                  </div>
                  <h3 className="text-4xl lg:text-5xl font-black text-gray-900 mb-3">
                    {capabilities[activeCapability].title}
                  </h3>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {capabilities[activeCapability].description}
                  </p>
                </div>
              </div>

              {/* Key Points Grid - Vertical Layout with Horizontal Animation */}
              <div className="grid md:grid-cols-2 gap-6">
                {capabilities[activeCapability].keyPoints.map((point, idx) => (
                  <div
                    key={idx}
                    className={`group relative p-6 rounded-2xl border-2 transition-all duration-500 cursor-pointer overflow-hidden animate-cascade-slide ${hoveredItem === idx
                      ? 'bg-gray-50 shadow-xl scale-[1.02]'
                      : 'bg-white hover:bg-gray-50 border-gray-100 hover:shadow-lg'
                      }`}
                    style={{
                      animationDelay: `${idx * 0.12}s`,
                      borderColor: hoveredItem === idx ? `${capabilities[activeCapability].color}40` : undefined
                    }}
                    onMouseEnter={() => setHoveredItem(idx)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    {/* Hover gradient overlay */}
                    <div
                      className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                      style={{
                        background: `linear-gradient(135deg, ${capabilities[activeCapability].color}08, transparent)`
                      }}
                    />

                    {/* Corner accent */}
                    <div
                      className="absolute top-0 right-0 w-24 h-24 opacity-10 group-hover:opacity-20 transition-opacity"
                      style={{
                        background: `radial-gradient(circle at top right, ${capabilities[activeCapability].color}, transparent)`
                      }}
                    />

                    <div className="relative flex items-start gap-5">
                      <div
                        className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-500 ${hoveredItem === idx ? 'scale-110 animate-icon-bounce' : 'group-hover:scale-105'
                          }`}
                        style={{
                          backgroundColor: hoveredItem === idx ? capabilities[activeCapability].color : `${capabilities[activeCapability].color}15`,
                        }}
                      >
                        <point.icon
                          size={28}
                          className={hoveredItem === idx ? 'text-white' : ''}
                          style={{ color: hoveredItem !== idx ? capabilities[activeCapability].color : undefined }}
                        />
                      </div>

                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-gray-800">
                          {point.title}
                        </h4>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {point.desc}
                        </p>

                        {/* Animated underline on hover */}
                        <div
                          className="mt-4 h-1 rounded-full transition-all duration-500"
                          style={{
                            width: hoveredItem === idx ? '100%' : '0%',
                            backgroundColor: capabilities[activeCapability].color
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom Navigation */}
              <div className="mt-12 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setActiveCapability(prev => prev > 0 ? prev - 1 : capabilities.length - 1)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors text-sm font-semibold"
                  >
                    <ChevronRight size={18} className="rotate-180" />
                    Previous
                  </button>
                  <button
                    onClick={() => setActiveCapability(prev => (prev + 1) % capabilities.length)}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl transition-colors text-sm font-semibold text-white"
                    style={{ backgroundColor: capabilities[activeCapability].color }}
                  >
                    Next Capability
                    <ChevronRight size={18} />
                  </button>
                </div>

                {/* Dot indicators */}
                <div className="flex items-center gap-3">
                  {capabilities.map((cap, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveCapability(idx)}
                      className={`h-3 rounded-full transition-all duration-500 ${idx === activeCapability ? 'w-10' : 'w-3 hover:scale-125'
                        }`}
                      style={{
                        backgroundColor: idx <= activeCapability ? cap.color : '#e5e7eb'
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom value proposition */}
        <div className="mt-16 text-center">
          <div className="inline-flex flex-wrap justify-center items-center gap-8 lg:gap-12 p-8 bg-gradient-to-r from-[#0369a1]/5 via-[#047857]/5 to-[#10b981]/5 rounded-3xl border border-gray-100">
            {[
              { icon: Award, label: '30+ Years Experience' },
              { icon: Users, label: '500+ Expert Engineers' },
              { icon: Globe2, label: '12+ Global Locations' },
              { icon: CheckCircle2, label: '99.8% Success Rate' }
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-3 group">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0369a1] to-[#047857] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <item.icon size={24} className="text-white" />
                </div>
                <span className="font-bold text-gray-800">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Technology Ecosystem Section - Premium Enterprise Design
// Animated Compliance Transformation Flowchart - Creative Design
const ComplianceTransformationSection = () => {
  const [activePhase, setActivePhase] = useState(0);
  const [particles, setParticles] = useState([]);

  // Animate through phases
  useEffect(() => {
    const interval = setInterval(() => {
      setActivePhase(prev => (prev + 1) % 6);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Generate floating particles
  useEffect(() => {
    const newParticles = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 3 + 1,
      duration: Math.random() * 10 + 15,
      delay: Math.random() * 5,
    }));
    setParticles(newParticles);
  }, []);

  // How we leverage AI in our services
  const aiServices = [
    {
      id: 'validation',
      label: 'Validation Services',
      sublabel: 'AI-Enhanced Delivery',
      description: 'We leverage AI to accelerate protocol development, streamline test execution, and ensure comprehensive compliance coverage for our clients.',
      icon: ShieldCheck,
      color: '#0ea5e9',
      aiFeatures: ['Faster Protocols', 'Efficient Testing', 'Better Coverage']
    },
    {
      id: 'automation',
      label: 'Automation Solutions',
      sublabel: 'Smarter Implementation',
      description: 'Our team uses AI-assisted analysis to optimize batch processes, predict maintenance needs, and deliver more efficient control system implementations.',
      icon: Cpu,
      color: '#8b5cf6',
      aiFeatures: ['Process Analysis', 'Performance Insights', 'Efficient Delivery']
    },
    {
      id: 'quality',
      label: 'Quality Systems',
      sublabel: 'Data-Driven Excellence',
      description: 'We apply AI analytics to identify deviation patterns, recommend effective CAPAs, and provide clients with actionable quality insights.',
      icon: ClipboardCheck,
      color: '#10b981',
      aiFeatures: ['Pattern Recognition', 'Smart Recommendations', 'Quality Insights']
    },
    {
      id: 'regulatory',
      label: 'Regulatory Affairs',
      sublabel: 'Intelligent Support',
      description: 'Our regulatory experts use AI to review submissions faster, stay current with global regulations, and identify compliance gaps efficiently.',
      icon: FileCheck,
      color: '#f59e0b',
      aiFeatures: ['Faster Reviews', 'Regulatory Updates', 'Gap Identification']
    },
    {
      id: 'engineering',
      label: 'Engineering Services',
      sublabel: 'Enhanced Design',
      description: 'We utilize AI-powered analysis to optimize process designs, simulate outcomes, and deliver commissioning projects with greater precision.',
      icon: Cog,
      color: '#ec4899',
      aiFeatures: ['Design Optimization', 'Outcome Simulation', 'Precise Delivery']
    },
    {
      id: 'data',
      label: 'Data Integrity',
      sublabel: 'AI-Assisted Assurance',
      description: 'Our specialists use AI to verify ALCOA+ compliance, detect data anomalies, and provide thorough audit trail analysis for clients.',
      icon: Database,
      color: '#06b6d4',
      aiFeatures: ['Compliance Checks', 'Anomaly Detection', 'Audit Analysis']
    },
  ];

  return (
    <section className="py-20 lg:py-28 bg-white relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, #10b981 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }}></div>

      {/* Animated background blobs */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-[#0ea5e9]/5 rounded-full blur-[150px] animate-morph-blob"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#10b981]/5 rounded-full blur-[120px] animate-morph-blob" style={{ animationDelay: '5s' }}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#8b5cf6]/3 rounded-full blur-[180px]"></div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full animate-float-particle"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: particle.id % 3 === 0 ? '#0ea5e9' : particle.id % 3 === 1 ? '#10b981' : '#a855f7',
              animationDuration: `${particle.duration}s`,
              animationDelay: `${particle.delay}s`,
              opacity: 0.3,
            }}
          ></div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header Section */}
        <div className="text-center mb-16 lg:mb-20">
          {/* Badge */}
          <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-gradient-to-r from-[#0ea5e9]/10 to-[#10b981]/10 rounded-full mb-8">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Bot size={18} className="text-[#10b981]" />
                <div className="absolute inset-0 animate-ping opacity-50">
                  <Bot size={18} className="text-[#10b981]" />
                </div>
              </div>
              <span className="text-[#10b981] font-semibold tracking-widest text-xs sm:text-sm">AI-ENHANCED SERVICES</span>
            </div>
            <div className="flex gap-1 ml-2">
              {[0, 1, 2].map(i => (
                <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#10b981] animate-pulse" style={{ animationDelay: `${i * 0.3}s` }}></div>
              ))}
            </div>
          </div>

          {/* Main Title */}
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight">
            How We Leverage{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#8b5cf6]">
              AI in Our Services
            </span>
          </h2>

          {/* Subtitle */}
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We use artificial intelligence to deliver faster, smarter, and more efficient solutions for our life sciences clients
          </p>
        </div>

        {/* Main Visualization - AI Services Grid */}
        <div className="relative mb-16">
          {/* Central Hub */}
          <div className="flex justify-center mb-12">
            <div className="relative">
              {/* Outer glow rings */}
              <div className="absolute -inset-8 rounded-full border border-[#10b981]/20 animate-pulse"></div>
              <div className="absolute -inset-16 rounded-full border border-dashed border-[#0ea5e9]/10 animate-spin-slow" style={{ animationDuration: '30s' }}></div>

              {/* Main Hub - UPT AI */}
              <div className="relative w-36 h-36 sm:w-44 sm:h-44 rounded-full bg-gradient-to-br from-[#0ea5e9] to-[#10b981] border-2 border-white flex items-center justify-center overflow-hidden group cursor-pointer hover:scale-105 transition-transform shadow-2xl">
                {/* Rotating gradient border */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#8b5cf6] opacity-30 animate-spin-slow" style={{ animationDuration: '8s' }}></div>
                <div className="absolute inset-[3px] rounded-full bg-gradient-to-br from-[#0ea5e9] to-[#10b981]"></div>

                {/* Inner glow */}
                <div className="absolute inset-6 rounded-full bg-white/20 animate-pulse"></div>

                {/* UPT AI Content */}
                <div className="relative z-10 text-center">
                  <div className="relative">
                    <Bot size={32} className="text-white mx-auto mb-1" />
                    <Sparkles size={12} className="absolute top-0 right-0 text-white animate-pulse" />
                  </div>
                  <span className="text-2xl sm:text-3xl font-black text-white">
                    UPT
                  </span>
                  <p className="text-[9px] text-white/80 font-semibold tracking-wider">AI POWERED</p>
                </div>
              </div>
            </div>
          </div>

          {/* AI Services Cards - 2x3 Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {aiServices.map((service, idx) => {
              const isActive = activePhase === idx;

              return (
                <div
                  key={service.id}
                  className={`relative group transition-all duration-500 ${isActive ? 'scale-[1.02] z-20' : 'scale-100'}`}
                >
                  {/* Glow effect when active */}
                  {isActive && (
                    <div className="absolute -inset-1 rounded-2xl blur-xl opacity-20 -z-10" style={{ backgroundColor: service.color }}></div>
                  )}

                  {/* Card */}
                  <div className={`relative p-5 sm:p-6 rounded-2xl border-2 transition-all duration-500 h-full bg-white ${isActive
                    ? 'shadow-2xl'
                    : 'border-gray-100 hover:shadow-xl hover:border-transparent'
                    }`}
                    style={{ borderColor: isActive ? `${service.color}40` : undefined }}
                  >
                    {/* Header Row */}
                    <div className="flex items-start gap-4 mb-4 relative">
                      {/* Icon */}
                      <div
                        className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${isActive ? 'scale-110' : 'group-hover:scale-110'}`}
                        style={{ backgroundColor: `${service.color}15` }}
                      >
                        <service.icon size={28} style={{ color: service.color }} />
                        {isActive && (
                          <div className="absolute inset-0 rounded-xl border-2 animate-ping opacity-20" style={{ borderColor: service.color }}></div>
                        )}
                      </div>

                      {/* Title & Sublabel */}
                      <div className="flex-1">
                        <h3 className="font-bold text-lg mb-1 text-gray-900">
                          {service.label}
                        </h3>
                        <p className="text-sm" style={{ color: service.color }}>
                          {service.sublabel}
                        </p>
                      </div>

                      {/* AI Badge */}
                      <div className="px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1"
                        style={{ backgroundColor: `${service.color}15`, color: service.color }}
                      >
                        <Sparkles size={10} />
                        AI
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-sm mb-4 leading-relaxed text-gray-600">
                      {service.description}
                    </p>

                    {/* AI Features Tags */}
                    <div className="flex flex-wrap gap-2">
                      {service.aiFeatures.map((feature, fIdx) => (
                        <span
                          key={fIdx}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-50 text-gray-700 border border-gray-100"
                          style={isActive ? { borderLeft: `2px solid ${service.color}`, backgroundColor: `${service.color}10` } : {}}
                        >
                          {feature}
                        </span>
                      ))}
                    </div>

                    {/* Active indicator */}
                    {isActive && (
                      <div className="absolute bottom-3 right-3 flex gap-1">
                        {[0, 1, 2].map(i => (
                          <span key={i} className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ backgroundColor: service.color, animationDelay: `${i * 100}ms` }}></span>
                        ))}
                      </div>
                    )}

                    {/* Bottom accent */}
                    <div
                      className={`absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl transform ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'} transition-transform duration-500 origin-left`}
                      style={{ background: `linear-gradient(90deg, ${service.color}, ${service.color}60)` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Progress Timeline */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#8b5cf6] rounded-full transition-all duration-700"
              style={{ width: `${((activePhase + 1) / aiServices.length) * 100}%` }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12">
          {[
            { value: '10x', label: 'Faster', sublabel: 'Delivery', icon: Zap, color: '#0ea5e9' },
            { value: '99.9%', label: 'Accuracy', sublabel: 'Rate', icon: Target, color: '#10b981' },
            { value: '24/7', label: 'AI', sublabel: 'Monitoring', icon: Activity, color: '#8b5cf6' },
            { value: '100%', label: 'Audit', sublabel: 'Ready', icon: ShieldCheck, color: '#ec4899' },
          ].map((stat, idx) => (
            <div
              key={idx}
              className="group relative p-4 sm:p-5 rounded-xl bg-white border-2 border-gray-100 hover:border-transparent hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              {/* Glow on hover */}
              <div
                className="absolute -inset-1 rounded-xl blur-lg opacity-0 group-hover:opacity-15 transition-opacity -z-10"
                style={{ backgroundColor: stat.color }}
              />

              {/* Top color bar */}
              <div
                className="absolute top-0 left-0 right-0 h-1 rounded-t-xl"
                style={{ background: `linear-gradient(90deg, ${stat.color}, ${stat.color}60)` }}
              />

              <div className="flex items-start gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: `${stat.color}15` }}
                >
                  <stat.icon size={20} style={{ color: stat.color }} />
                </div>
                <div>
                  <p className="text-2xl sm:text-3xl font-black" style={{ color: stat.color }}>{stat.value}</p>
                  <p className="text-gray-900 text-sm font-medium">{stat.label}</p>
                  <p className="text-gray-500 text-xs">{stat.sublabel}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center">
          <button
            onClick={() => window.dispatchEvent(new Event('openHelix'))}
            className="group relative inline-flex items-center gap-3 px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-[#0ea5e9] to-[#10b981] text-white font-bold text-base sm:text-lg rounded-xl overflow-hidden transition-all hover:shadow-2xl hover:shadow-[#10b981]/30 hover:-translate-y-1"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#10b981] to-[#8b5cf6] opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <Sparkles size={22} className="relative z-10" />
            <span className="relative z-10">Know More About UPT via Helix</span>
            <ArrowRight size={22} className="relative z-10 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

const EcosystemSection = () => {
  const [activeCard, setActiveCard] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const capabilities = [
    {
      id: 1,
      title: "Engineering & Systems",
      subtitle: "PROCESS EXCELLENCE",
      icon: Cog,
      stats: "500+",
      statsLabel: "Systems Deployed",
      features: ["Process Design", "Equipment Qualification", "Facility Commissioning", "Tech Transfer"],
      gradient: "from-[#1e5f74] to-[#2d9b83]",
      accentColor: "#1e5f74"
    },
    {
      id: 2,
      title: "AI & Intelligent Systems",
      subtitle: "SMART SOLUTIONS",
      icon: Cpu,
      stats: "50+",
      statsLabel: "AI Models Deployed",
      features: ["Predictive Analytics", "Machine Learning", "Smart Automation", "Data Intelligence"],
      gradient: "from-[#2d9b83] to-[#4db8a4]",
      accentColor: "#2d9b83"
    },
    {
      id: 3,
      title: "Validation & Compliance",
      subtitle: "REGULATORY EXCELLENCE",
      icon: ShieldCheck,
      stats: "2800+",
      statsLabel: "Validations Complete",
      features: ["CSV Implementation", "Process Validation", "Equipment Qualification", "Risk Assessment"],
      gradient: "from-[#1a936f] to-[#1e5f74]",
      accentColor: "#1a936f"
    },
    {
      id: 4,
      title: "Digital Transformation",
      subtitle: "INDUSTRY 4.0",
      icon: Network,
      stats: "100+",
      statsLabel: "Digital Projects",
      features: ["MES Systems", "IoT Integration", "Paperless Operations", "Digital Twin"],
      gradient: "from-[#4db8a4] to-[#1a936f]",
      accentColor: "#4db8a4"
    }
  ];

  // Auto-rotate cards
  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setActiveCard((prev) => (prev + 1) % capabilities.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isHovered, capabilities.length]);

  return (
    <section className="py-32 bg-white relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, #10b981 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }}></div>

      {/* Animated background blobs */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-[#0ea5e9]/5 rounded-full blur-[150px] animate-morph-blob"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#10b981]/5 rounded-full blur-[120px] animate-morph-blob" style={{ animationDelay: '5s' }}></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#8b5cf6]/3 rounded-full blur-[180px]"></div>

      {/* Floating orbs */}
      <div className="absolute top-20 left-20 w-3 h-3 bg-[#10b981] rounded-full blur-sm animate-float-icon opacity-20"></div>
      <div className="absolute top-40 right-32 w-2 h-2 bg-[#0ea5e9] rounded-full blur-sm animate-float-icon opacity-30" style={{ animationDelay: '1s' }}></div>
      <div className="absolute bottom-32 left-1/4 w-4 h-4 bg-[#8b5cf6] rounded-full blur-sm animate-float-icon opacity-15" style={{ animationDelay: '2s' }}></div>
      <div className="absolute bottom-20 right-20 w-3 h-3 bg-[#ec4899] rounded-full blur-sm animate-float-icon opacity-20" style={{ animationDelay: '3s' }}></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Premium Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#0ea5e9]/10 to-[#10b981]/10 rounded-full mb-8">
            <div className="w-2 h-2 bg-[#10b981] rounded-full animate-pulse"></div>
            <span className="text-[#10b981] text-sm font-semibold tracking-[0.2em]">TECHNOLOGY ECOSYSTEM</span>
            <div className="w-2 h-2 bg-[#10b981] rounded-full animate-pulse"></div>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight">
            Integrated Engineering
            <br />
            <span className="relative inline-block mt-2">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#8b5cf6]">
                Solutions Platform
              </span>
              <svg className="absolute -bottom-2 left-0 w-full" height="8" viewBox="0 0 300 8" fill="none">
                <path d="M0 4C50 4 50 1 100 1C150 1 150 7 200 7C250 7 250 4 300 4" stroke="url(#underlineGrad)" strokeWidth="2" strokeLinecap="round" />
                <defs>
                  <linearGradient id="underlineGrad" x1="0" y1="0" x2="300" y2="0">
                    <stop stopColor="#0ea5e9" />
                    <stop offset="0.5" stopColor="#10b981" />
                    <stop offset="1" stopColor="#8b5cf6" />
                  </linearGradient>
                </defs>
              </svg>
            </span>
          </h2>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            A comprehensive suite of interconnected capabilities powering innovation across the life sciences industry
          </p>
        </div>

        {/* Main Content - Bento Grid Layout */}
        <div
          className="grid grid-cols-1 lg:grid-cols-12 gap-6"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Left - Featured Card (Large) */}
          <div className="lg:col-span-5 lg:row-span-2">
            <div
              className={`relative h-full min-h-[500px] bg-white border-2 rounded-[2rem] p-8 overflow-hidden transition-all duration-700 group cursor-pointer ${activeCard === 0 ? 'shadow-2xl' : 'border-gray-100 hover:shadow-xl hover:border-transparent'
                }`}
              style={{ borderColor: activeCard === 0 ? `${capabilities[0].accentColor}40` : undefined }}
              onClick={() => setActiveCard(0)}
            >
              {/* Glow effect when active */}
              {activeCard === 0 && (
                <div className="absolute -inset-1 rounded-[2rem] blur-xl opacity-15 -z-10" style={{ backgroundColor: capabilities[0].accentColor }}></div>
              )}

              {/* Background gradient animation */}
              <div className={`absolute inset-0 bg-gradient-to-br ${capabilities[0].gradient} opacity-0 ${activeCard === 0 ? 'opacity-5' : 'group-hover:opacity-3'} transition-opacity duration-700`}></div>

              {/* Large background icon */}
              <div className="absolute -right-10 -bottom-10 opacity-[0.03] group-hover:opacity-[0.06] transition-opacity duration-700">
                <Cog size={300} strokeWidth={0.5} className="text-gray-400" />
              </div>

              {/* Content */}
              <div className="relative z-10 h-full flex flex-col">
                {/* Badge */}
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${capabilities[0].gradient} bg-opacity-20 w-fit mb-6`}>
                  <Cog size={16} className="text-white" />
                  <span className="text-white text-xs font-semibold tracking-wider">{capabilities[0].subtitle}</span>
                </div>

                {/* Title */}
                <h3 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4 group-hover:text-[#10b981] transition-colors duration-500">
                  {capabilities[0].title}
                </h3>

                {/* Features */}
                <div className="space-y-3 mb-auto">
                  {capabilities[0].features.map((feature, fidx) => (
                    <div
                      key={fidx}
                      className="flex items-center gap-3 text-gray-600 group-hover:text-gray-700 transition-colors"
                      style={{ transitionDelay: `${fidx * 100}ms` }}
                    >
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${capabilities[0].gradient} flex items-center justify-center`}>
                        <CheckCircle2 size={14} className="text-white" />
                      </div>
                      <span className="font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Stats */}
                <div className="pt-6 mt-6 border-t border-gray-100">
                  <div className="flex items-end justify-between">
                    <div>
                      <div className={`text-5xl font-black bg-gradient-to-r ${capabilities[0].gradient} bg-clip-text text-transparent`}>
                        {capabilities[0].stats}
                      </div>
                      <div className="text-gray-500 text-sm mt-1">{capabilities[0].statsLabel}</div>
                    </div>
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${capabilities[0].gradient} flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
                      <ArrowRight size={24} className="text-white" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom accent line */}
              <div className={`absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r ${capabilities[0].gradient} transform ${activeCard === 0 ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'} transition-transform duration-500 origin-left rounded-b-[2rem]`}></div>
            </div>
          </div>

          {/* Right - Smaller Cards Grid */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {capabilities.slice(1).map((cap, idx) => (
              <div
                key={cap.id}
                className={`relative bg-white border-2 rounded-[1.5rem] p-6 overflow-hidden transition-all duration-500 cursor-pointer group ${activeCard === idx + 1
                  ? 'shadow-xl scale-[1.02]'
                  : 'border-gray-100 hover:shadow-lg hover:border-transparent hover:scale-[1.01]'
                  }`}
                style={{ borderColor: activeCard === idx + 1 ? `${cap.accentColor}40` : undefined }}
                onClick={() => setActiveCard(idx + 1)}
              >
                {/* Glow effect when active */}
                {activeCard === idx + 1 && (
                  <div className="absolute -inset-1 rounded-[1.5rem] blur-xl opacity-15 -z-10" style={{ backgroundColor: cap.accentColor }}></div>
                )}

                {/* Background gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${cap.gradient} opacity-0 ${activeCard === idx + 1 ? 'opacity-5' : 'group-hover:opacity-3'} transition-opacity duration-500`}></div>

                {/* Background icon */}
                <div className="absolute -right-6 -bottom-6 opacity-[0.03] group-hover:opacity-[0.06] transition-opacity">
                  <cap.icon size={150} strokeWidth={0.5} className="text-gray-400" />
                </div>

                {/* Content */}
                <div className="relative z-10">
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${cap.gradient} flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg`}>
                    <cap.icon size={22} className="text-white" />
                  </div>

                  {/* Subtitle */}
                  <p className="text-[#10b981] text-[10px] font-bold tracking-[0.15em] mb-1">{cap.subtitle}</p>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#10b981] transition-colors">
                    {cap.title}
                  </h3>

                  {/* Features (show 2) */}
                  <div className="space-y-1.5 mb-4">
                    {cap.features.slice(0, 2).map((feature, fidx) => (
                      <div key={fidx} className="flex items-center gap-2 text-sm text-gray-600">
                        <div className={`w-1 h-1 rounded-full bg-gradient-to-r ${cap.gradient}`}></div>
                        {feature}
                      </div>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div>
                      <div className={`text-2xl font-black bg-gradient-to-r ${cap.gradient} bg-clip-text text-transparent`}>
                        {cap.stats}
                      </div>
                      <div className="text-gray-500 text-[10px]">{cap.statsLabel}</div>
                    </div>
                    <ChevronRight size={20} className="text-gray-400 group-hover:text-[#10b981] group-hover:translate-x-1 transition-all" />
                  </div>
                </div>

                {/* Bottom accent line */}
                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${cap.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-b-[1.5rem]`}></div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Stats Strip */}
        <div className="mt-16 relative">
          <div className="relative grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { value: "30+", label: "Years of Excellence", icon: Award, suffix: "", color: '#0ea5e9' },
              { value: "500", label: "Expert Engineers", icon: Users, suffix: "+", color: '#10b981' },
              { value: "98", label: "Client Satisfaction", icon: TrendingUp, suffix: "%", color: '#8b5cf6' },
              { value: "24/7", label: "Global Support", icon: Globe2, suffix: "", color: '#ec4899' }
            ].map((stat, idx) => (
              <div
                key={idx}
                className="group relative bg-white border-2 border-gray-100 rounded-2xl p-6 text-center hover:shadow-lg hover:border-transparent transition-all duration-300 cursor-pointer"
              >
                {/* Glow on hover */}
                <div
                  className="absolute -inset-1 rounded-2xl blur-lg opacity-0 group-hover:opacity-15 transition-opacity -z-10"
                  style={{ backgroundColor: stat.color }}
                />

                {/* Top color bar */}
                <div
                  className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
                  style={{ background: `linear-gradient(90deg, ${stat.color}, ${stat.color}60)` }}
                />

                <stat.icon size={20} style={{ color: stat.color }} className="mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <div className="text-3xl font-black text-gray-900 group-hover:scale-105 transition-transform">
                  {stat.value}<span style={{ color: stat.color }}>{stat.suffix}</span>
                </div>
                <div className="text-gray-500 text-xs mt-1 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Dots */}
        <div className="flex justify-center mt-10 gap-2">
          {capabilities.map((cap, idx) => (
            <button
              key={idx}
              onClick={() => setActiveCard(idx)}
              className={`h-2 rounded-full transition-all duration-500 ${activeCard === idx
                ? `w-8 bg-gradient-to-r ${cap.gradient}`
                : 'w-2 bg-gray-200 hover:bg-gray-300'
                }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section - Enhanced with Detailed Workflow Layers
const ServicesSection = () => {
  const [activeService, setActiveService] = useState(0);
  const [expandedLayer, setExpandedLayer] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  // Auto-cycle through services
  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setActiveService((prev) => (prev + 1) % 6);
        setExpandedLayer(null); // Reset expanded layer on cycle
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isHovered]);

  const services = [
    {
      icon: ShieldCheck,
      title: "Validation Services",
      desc: "IQ/OQ/PQ, Process Validation, Cleaning Validation, CSV, Thermal Mapping",
      color: '#0ea5e9',
      gradient: 'from-sky-500 to-blue-600',
      workflowLayers: [
        {
          title: 'Strategy & Planning',
          icon: Target,
          items: ['Risk-based validation approach', 'Master validation planning', 'Protocol development lifecycle', 'Resource and timeline optimization']
        },
        {
          title: 'Execution Excellence',
          icon: Rocket,
          items: ['IQ/OQ/PQ execution and documentation', 'Deviation management and resolution', 'Real-time progress tracking', 'Cross-functional coordination']
        },
        {
          title: 'Audit Defense',
          icon: Shield,
          items: ['Regulatory-ready documentation', 'Inspection preparation support', 'CAPA and remediation expertise', '21 CFR Part 11 compliance']
        }
      ]
    },
    {
      icon: Settings2,
      title: "Automation Solutions",
      desc: "PLC/DCS/SCADA, MES Implementation, 21 CFR Part 11, System Integration",
      color: '#10b981',
      gradient: 'from-emerald-500 to-green-600',
      workflowLayers: [
        {
          title: 'System Architecture',
          icon: Network,
          items: ['Control system design and specification', 'Integration architecture planning', 'Cybersecurity framework implementation', 'Scalability and redundancy design']
        },
        {
          title: 'Implementation & Integration',
          icon: CircuitBoard,
          items: ['PLC/DCS/SCADA programming and configuration', 'MES deployment and optimization', 'Historian and reporting systems', 'Third-party system integration']
        },
        {
          title: 'Validation & Compliance',
          icon: FileCheck,
          items: ['CSV for automated systems', 'Data integrity assurance', '21 CFR Part 11 compliance', 'Change management procedures']
        }
      ]
    },
    {
      icon: ClipboardCheck,
      title: "Quality Systems",
      desc: "QMS Implementation, CAPA Management, Change Control, Risk Management",
      color: '#8b5cf6',
      gradient: 'from-violet-500 to-purple-600',
      workflowLayers: [
        {
          title: 'QMS Framework',
          icon: Layers,
          items: ['Quality management system design', 'SOP development and harmonization', 'Document control implementation', 'Training program establishment']
        },
        {
          title: 'Process Excellence',
          icon: Workflow,
          items: ['CAPA lifecycle management', 'Change control optimization', 'Deviation and OOS handling', 'Supplier quality management']
        },
        {
          title: 'Risk & Compliance',
          icon: Shield,
          items: ['ICH Q9/Q10 risk management', 'Internal audit programs', 'Metrics and KPI dashboards', 'Continuous improvement initiatives']
        }
      ]
    },
    {
      icon: FileCheck,
      title: "Regulatory Affairs",
      desc: "FDA/EMA Submissions, EU MDR/IVDR, Global Registration, Audit Support",
      color: '#f59e0b',
      gradient: 'from-amber-500 to-orange-600',
      workflowLayers: [
        {
          title: 'Submission Strategy',
          icon: FileText,
          items: ['Regulatory pathway assessment', 'Submission timeline planning', 'Gap analysis and remediation', 'Pre-submission meeting support']
        },
        {
          title: 'Dossier Excellence',
          icon: ScrollText,
          items: ['eCTD compilation and publishing', 'Technical writing and review', 'Global registration management', 'Post-market commitments tracking']
        },
        {
          title: 'Inspection Readiness',
          icon: BadgeCheck,
          items: ['Mock inspection programs', 'Regulatory intelligence monitoring', 'Health authority response support', 'Audit remediation management']
        }
      ]
    },
    {
      icon: Network,
      title: "Digital Transformation",
      desc: "Digital Twin, IoT Solutions, Data Analytics, Paperless Systems",
      color: '#ec4899',
      gradient: 'from-pink-500 to-rose-600',
      workflowLayers: [
        {
          title: 'Digital Strategy',
          icon: Lightbulb,
          items: ['Digital maturity assessment', 'Technology roadmap development', 'ROI and business case analysis', 'Vendor evaluation and selection']
        },
        {
          title: 'Implementation',
          icon: Cpu,
          items: ['Digital twin modeling', 'IoT sensor deployment', 'Cloud infrastructure setup', 'Paperless manufacturing systems']
        },
        {
          title: 'Data Excellence',
          icon: Database,
          items: ['Advanced analytics and AI/ML', 'Real-time dashboards and reporting', 'Data lake architecture', 'Predictive maintenance models']
        }
      ]
    },
    {
      icon: GraduationCap,
      title: "Training & Development",
      desc: "GxP Training, Validation Courses, Custom Programs, E-Learning",
      color: '#06b6d4',
      gradient: 'from-cyan-500 to-teal-600',
      workflowLayers: [
        {
          title: 'Program Design',
          icon: Target,
          items: ['Training needs assessment', 'Curriculum development', 'Competency mapping', 'Learning path creation']
        },
        {
          title: 'Delivery Excellence',
          icon: Users,
          items: ['Instructor-led training (ILT)', 'Virtual and hybrid delivery', 'Hands-on workshops', 'On-the-job coaching']
        },
        {
          title: 'Learning Management',
          icon: BarChart3,
          items: ['LMS implementation and support', 'Training effectiveness metrics', 'Compliance tracking', 'Knowledge retention programs']
        }
      ]
    },
  ];

  return (
    <section id="services" className="py-28 bg-white relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, #10b981 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }}></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#0ea5e9]/10 to-[#10b981]/10 rounded-full mb-6">
            <Layers size={18} className="text-[#10b981]" />
            <span className="text-sm font-bold text-[#10b981] tracking-wider">SERVICE EXCELLENCE</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6">
            Comprehensive{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#8b5cf6]">
              Solutions
            </span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Deep execution expertise with structured workflow layers ensuring regulatory compliance and operational excellence at every stage.
          </p>
        </div>

        {/* Service Navigation Pills - Enhanced with glowing effect */}
        <div
          className="flex flex-wrap justify-center gap-4 mb-12"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {services.map((service, idx) => (
            <button
              key={idx}
              onClick={() => {
                setActiveService(idx);
                setExpandedLayer(null);
              }}
              className={`group relative flex items-center gap-3 px-5 py-3 rounded-xl font-semibold transition-all duration-500 ${activeService === idx
                ? 'bg-white shadow-2xl scale-105 border-2'
                : 'bg-gray-50 hover:bg-white hover:shadow-lg border-2 border-transparent'
                }`}
              style={{
                borderColor: activeService === idx ? service.color : 'transparent',
                boxShadow: activeService === idx ? `0 20px 40px ${service.color}25` : undefined
              }}
            >
              {/* Glow effect */}
              {activeService === idx && (
                <div
                  className="absolute -inset-1 rounded-xl blur-xl opacity-30 animate-pulse -z-10"
                  style={{ backgroundColor: service.color }}
                />
              )}

              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ${activeService === idx ? '' : 'group-hover:scale-110'
                  }`}
                style={{
                  backgroundColor: activeService === idx ? service.color : `${service.color}15`
                }}
              >
                <service.icon
                  size={20}
                  className={activeService === idx ? 'text-white' : ''}
                  style={{ color: activeService !== idx ? service.color : undefined }}
                />
              </div>

              <span className={`hidden sm:inline font-bold ${activeService === idx ? 'text-gray-900' : 'text-gray-600'}`}>
                {service.title}
              </span>
            </button>
          ))}
        </div>

        {/* Active Service Detail Card */}
        <div className="relative">
          {/* Glow effect */}
          <div
            className="absolute -inset-4 rounded-3xl blur-3xl opacity-10 transition-colors duration-700"
            style={{ backgroundColor: services[activeService].color }}
          />

          <div
            className="relative bg-white rounded-3xl border-2 shadow-2xl overflow-hidden transition-all duration-500"
            style={{ borderColor: `${services[activeService].color}30` }}
          >
            {/* Top gradient bar */}
            <div
              className="h-2 w-full relative overflow-hidden"
              style={{
                background: `linear-gradient(90deg, ${services[activeService].color}, ${services[(activeService + 1) % services.length].color})`
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" />
            </div>

            <div className="p-8 lg:p-12">
              {/* Service Header */}
              <div className="flex flex-col lg:flex-row lg:items-start gap-6 mb-10">
                <div className="relative">
                  {/* Glow effect */}
                  <div
                    className="absolute inset-0 rounded-2xl blur-xl opacity-40 animate-pulse"
                    style={{ backgroundColor: services[activeService].color }}
                  />
                  <div
                    className="relative w-24 h-24 rounded-2xl flex items-center justify-center shadow-2xl"
                    style={{
                      background: `linear-gradient(135deg, ${services[activeService].color}, ${services[activeService].color}cc)`,
                      boxShadow: `0 20px 40px ${services[activeService].color}40`
                    }}
                  >
                    {(() => {
                      const ServiceIcon = services[activeService].icon;
                      return <ServiceIcon size={48} className="text-white" />;
                    })()}
                  </div>
                </div>

                <div className="flex-1">
                  <h3 className="text-3xl lg:text-4xl font-black text-gray-900 mb-3">{services[activeService].title}</h3>
                  <p className="text-lg text-gray-600 mb-4">{services[activeService].desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {services[activeService].workflowLayers.map((layer, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1.5 rounded-full text-xs font-semibold"
                        style={{ backgroundColor: `${services[activeService].color}15`, color: services[activeService].color }}
                      >
                        {layer.title}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Workflow Layers - Vertical Accordion */}
              <div className="space-y-4">
                <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-6">
                  <Workflow size={20} style={{ color: services[activeService].color }} />
                  Execution Workflow Layers
                </h4>

                {services[activeService].workflowLayers.map((layer, idx) => (
                  <div
                    key={idx}
                    className={`group relative bg-gray-50 rounded-2xl border-2 transition-all duration-500 overflow-hidden ${expandedLayer === idx
                      ? 'shadow-xl'
                      : 'border-gray-100 hover:border-gray-200 hover:shadow-lg'
                      }`}
                    style={{ borderColor: expandedLayer === idx ? `${services[activeService].color}40` : undefined }}
                  >
                    {/* Glow effect when expanded */}
                    {expandedLayer === idx && (
                      <div
                        className="absolute -inset-1 rounded-2xl blur-xl opacity-10 -z-10"
                        style={{ backgroundColor: services[activeService].color }}
                      />
                    )}

                    {/* Layer Header */}
                    <button
                      onClick={() => setExpandedLayer(expandedLayer === idx ? null : idx)}
                      className="w-full p-6 flex items-center justify-between text-left bg-white rounded-t-2xl"
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-500 ${expandedLayer === idx ? 'animate-icon-bounce' : 'group-hover:scale-110'
                            }`}
                          style={{
                            backgroundColor: expandedLayer === idx ? services[activeService].color : `${services[activeService].color}15`,
                          }}
                        >
                          <layer.icon size={28} className={expandedLayer === idx ? 'text-white' : ''} style={{ color: expandedLayer !== idx ? services[activeService].color : undefined }} />
                        </div>
                        <div>
                          <h5 className="text-xl font-bold text-gray-900 mb-1">{layer.title}</h5>
                          <p className="text-sm text-gray-500">{layer.items.length} key capabilities</p>
                        </div>
                      </div>

                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${expandedLayer === idx ? 'rotate-180' : ''
                          }`}
                        style={{ backgroundColor: `${services[activeService].color}15` }}
                      >
                        <ChevronDown size={20} style={{ color: services[activeService].color }} />
                      </div>
                    </button>

                    {/* Expanded Content */}
                    <div className={`overflow-hidden transition-all duration-500 ${expandedLayer === idx ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                      <div className="px-6 pb-6 bg-white">
                        <div className="pt-4 border-t border-gray-100">
                          <div className="grid sm:grid-cols-2 gap-4">
                            {layer.items.map((item, itemIdx) => (
                              <div
                                key={itemIdx}
                                className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl group/item hover:bg-white hover:shadow-md transition-all duration-300 cursor-default animate-cascade-slide border border-gray-100"
                                style={{ animationDelay: `${itemIdx * 0.1}s` }}
                              >
                                <div
                                  className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 group-hover/item:scale-110 transition-transform"
                                  style={{ backgroundColor: `${services[activeService].color}15` }}
                                >
                                  <CheckCircle2 size={16} style={{ color: services[activeService].color }} />
                                </div>
                                <span className="text-sm text-gray-700 leading-relaxed group-hover/item:text-gray-900 transition-colors">{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom progress indicator when expanded */}
                    {expandedLayer === idx && (
                      <div
                        className="h-1 animate-stage-progress"
                        style={{ background: `linear-gradient(90deg, ${services[activeService].color}, ${services[activeService].color}60)` }}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Bottom CTA */}
              <div className="mt-10 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="flex -space-x-3">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-bold shadow-md"
                        style={{
                          background: `linear-gradient(135deg, ${services[(activeService + i) % services.length].color}, ${services[(activeService + i) % services.length].color}bb)`,
                          zIndex: 4 - i
                        }}
                      >
                        {['J', 'S', 'M', 'A'][i]}
                      </div>
                    ))}
                  </div>
                  <div>
                    <p className="text-gray-900 font-semibold">Expert Team</p>
                    <p className="text-gray-500 text-sm">500+ specialists ready</p>
                  </div>
                </div>

                <button
                  onClick={() => window.dispatchEvent(new Event('openHelix'))}
                  className="enterprise-btn flex items-center gap-3 px-6 py-3 rounded-xl font-semibold text-white"
                  style={{ background: `linear-gradient(135deg, ${services[activeService].color}, ${services[activeService].color}cc)` }}
                >
                  <Sparkles size={18} />
                  Discuss Your Requirements
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Service Stats Bar */}
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Projects Delivered', value: '2,800+', icon: CheckCircle2 },
            { label: 'Expert Engineers', value: '500+', icon: Users },
            { label: 'Client Retention', value: '98%', icon: Award },
            { label: 'Global Locations', value: '12+', icon: Globe2 },
          ].map((stat, idx) => (
            <div
              key={idx}
              className="group p-6 bg-gray-50 rounded-2xl border border-gray-100 hover:border-gray-200 hover:bg-white hover:shadow-lg transition-all duration-500"
            >
              <stat.icon size={24} className="text-[#2d9b83] mb-3 group-hover:scale-110 transition-transform" />
              <div className="text-3xl font-black text-gray-900 mb-1">{stat.value}</div>
              <div className="text-sm text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Progress Bar */}
        <div className="mt-10 max-w-2xl mx-auto h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700 relative overflow-hidden"
            style={{
              width: `${((activeService + 1) / services.length) * 100}%`,
              background: `linear-gradient(90deg, ${services[0].color}, ${services[activeService].color})`
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer" />
          </div>
        </div>

        {/* Navigation Dots */}
        <div className="flex justify-center items-center mt-6 gap-2">
          {services.map((service, idx) => (
            <button
              key={idx}
              onClick={() => { setActiveService(idx); setExpandedLayer(null); }}
              className={`h-2.5 rounded-full transition-all duration-500 ${activeService === idx ? 'w-10' : 'w-2.5 hover:scale-125'
                }`}
              style={{ backgroundColor: activeService === idx ? service.color : '#e5e7eb' }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Case Studies Section
const CaseStudiesSection = () => {
  const [activeIndustry, setActiveIndustry] = useState('Pharmaceutical');
  const [activeSubCase, setActiveSubCase] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  // Industry groups with their case studies
  const industryGroups = {
    Pharmaceutical: {
      icon: Pill,
      color: '#0ea5e9',
      description: '3 Success Stories',
      cases: [
        {
          title: "Global Facility Validation Program",
          client: "Top 10 Pharmaceutical Company",
          result: "Zero Critical Observations",
          tags: ["CQV", "FDA", "EMA", "Multi-Site"],
          icon: ShieldCheck,
          color: '#0ea5e9',
          highlight: "Validation",
          challenge: "Validate 12 manufacturing facilities across 4 continents within 18 months while maintaining production schedules.",
          solution: "Deployed 50+ validation engineers using standardized protocols and real-time project tracking dashboards.",
          metrics: [
            { label: "Facilities Validated", value: "12" },
            { label: "Protocols Executed", value: "2,400+" },
            { label: "Time Saved", value: "35%" },
            { label: "FDA Observations", value: "0" }
          ]
        },
        {
          title: "Data Integrity Remediation",
          client: "Injectable Drug Manufacturer",
          result: "FDA Warning Letter Lifted",
          tags: ["Data Integrity", "ALCOA+", "Remediation", "21 CFR Part 11"],
          icon: Database,
          color: '#f59e0b',
          highlight: "Data Integrity",
          challenge: "Address FDA warning letter with multiple data integrity observations within consent decree timeline.",
          solution: "Comprehensive ALCOA+ assessment, system upgrades, and culture transformation program.",
          metrics: [
            { label: "Systems Remediated", value: "45+" },
            { label: "SOPs Updated", value: "200+" },
            { label: "Staff Trained", value: "800+" },
            { label: "Remediation Time", value: "11 mo" }
          ]
        },
        {
          title: "Cleanroom Validation Program",
          client: "Sterile Manufacturing Facility",
          result: "EU Annex 1 Compliance",
          tags: ["Cleanroom", "Environmental Monitoring", "Annex 1", "Aseptic"],
          icon: Thermometer,
          color: '#06b6d4',
          highlight: "Cleanroom",
          challenge: "Upgrade cleanroom facilities and validation programs to meet new EU Annex 1 requirements.",
          solution: "Complete contamination control strategy redesign with enhanced monitoring and qualification protocols.",
          metrics: [
            { label: "Cleanrooms Qualified", value: "24" },
            { label: "Monitoring Points", value: "350+" },
            { label: "Excursions Reduced", value: "78%" },
            { label: "Audit Readiness", value: "100%" }
          ]
        }
      ]
    },
    Biotechnology: {
      icon: Dna,
      color: '#10b981',
      description: '3 Success Stories',
      cases: [
        {
          title: "Cell Therapy Manufacturing Support",
          client: "Leading Biotech Innovator",
          result: "FDA BLA Approval Achieved",
          tags: ["Biotech", "Cell Therapy", "GMP", "ATMP"],
          icon: Dna,
          color: '#10b981',
          highlight: "Cell Therapy",
          challenge: "Support first-time BLA submission for CAR-T cell therapy with complex manufacturing process.",
          solution: "Provided end-to-end validation support including process validation, equipment qualification, and regulatory documentation.",
          metrics: [
            { label: "Process Steps Validated", value: "48" },
            { label: "Equipment Qualified", value: "85+" },
            { label: "Time to Approval", value: "14 mo" },
            { label: "Batch Success Rate", value: "99.2%" }
          ]
        },
        {
          title: "Biologics Process Development",
          client: "Emerging Biopharma Company",
          result: "Scale-up Success",
          tags: ["Biologics", "Process Development", "Scale-up", "Tech Transfer"],
          icon: FlaskConical,
          color: '#059669',
          highlight: "Process Development",
          challenge: "Scale manufacturing process from lab to commercial scale while maintaining product quality attributes.",
          solution: "Comprehensive process characterization, DOE studies, and validation strategy development.",
          metrics: [
            { label: "Scale Increase", value: "500x" },
            { label: "Quality Attributes Met", value: "100%" },
            { label: "Batch Failures", value: "0" },
            { label: "Timeline Achieved", value: "On-time" }
          ]
        },
        {
          title: "Viral Vector Facility Validation",
          client: "Gene Therapy Manufacturer",
          result: "Commercial Readiness",
          tags: ["Gene Therapy", "Viral Vector", "Facility Validation", "Aseptic"],
          icon: Microscope,
          color: '#14b8a6',
          highlight: "Viral Vector",
          challenge: "Validate new viral vector manufacturing facility for commercial gene therapy production.",
          solution: "Full facility qualification including HVAC, utilities, cleanrooms, and manufacturing equipment.",
          metrics: [
            { label: "Systems Qualified", value: "120+" },
            { label: "Cleanrooms Validated", value: "18" },
            { label: "Regulatory Inspections", value: "Passed" },
            { label: "Time to First Batch", value: "8 mo" }
          ]
        }
      ]
    },
    "Medical Devices": {
      icon: Cpu,
      color: '#8b5cf6',
      description: '3 Success Stories',
      cases: [
        {
          title: "EU MDR Transition Program",
          client: "Global Medical Device Manufacturer",
          result: "200+ Devices Certified",
          tags: ["EU MDR", "ISO 13485", "Technical Files", "Notified Body"],
          icon: Cpu,
          color: '#8b5cf6',
          highlight: "EU MDR",
          challenge: "Transition legacy device portfolio to EU MDR compliance before regulatory deadline.",
          solution: "Established gap assessment framework and systematic remediation program across product lines.",
          metrics: [
            { label: "Devices Certified", value: "200+" },
            { label: "Technical Files", value: "150+" },
            { label: "On-Time Delivery", value: "100%" },
            { label: "NB Audit Findings", value: "3" }
          ]
        },
        {
          title: "Software as Medical Device (SaMD)",
          client: "Digital Health Startup",
          result: "FDA 510(k) Clearance",
          tags: ["SaMD", "IEC 62304", "Cybersecurity", "FDA 510(k)"],
          icon: CircuitBoard,
          color: '#7c3aed',
          highlight: "SaMD",
          challenge: "Develop compliant software lifecycle documentation for AI-powered diagnostic tool.",
          solution: "Implemented IEC 62304 compliant SDLC with integrated cybersecurity and risk management.",
          metrics: [
            { label: "Documentation Complete", value: "100%" },
            { label: "FDA Questions", value: "0" },
            { label: "Time to Clearance", value: "6 mo" },
            { label: "Cybersecurity Score", value: "A+" }
          ]
        },
        {
          title: "Design Controls Implementation",
          client: "Orthopedic Device Company",
          result: "FDA Audit Success",
          tags: ["Design Controls", "DHF", "Risk Management", "V&V"],
          icon: Settings2,
          color: '#a855f7',
          highlight: "Design Controls",
          challenge: "Remediate design control deficiencies identified in FDA warning letter.",
          solution: "Complete design history file reconstruction and implementation of robust design control procedures.",
          metrics: [
            { label: "DHFs Remediated", value: "45" },
            { label: "Risk Files Updated", value: "60+" },
            { label: "Warning Letter", value: "Closed" },
            { label: "Re-inspection Result", value: "NAI" }
          ]
        }
      ]
    },
    CDMO: {
      icon: Server,
      color: '#ec4899',
      description: '3 Success Stories',
      cases: [
        {
          title: "Enterprise MES Implementation",
          client: "Global Contract Manufacturer",
          result: "8 Sites Digitally Transformed",
          tags: ["MES", "Automation", "Industry 4.0", "CSV"],
          icon: Server,
          color: '#ec4899',
          highlight: "Digital Transformation",
          challenge: "Implement unified MES platform across global manufacturing network with diverse legacy systems.",
          solution: "Phased rollout with comprehensive CSV, data migration, and change management programs.",
          metrics: [
            { label: "Sites Implemented", value: "8" },
            { label: "Batch Records/Day", value: "500+" },
            { label: "Paper Reduction", value: "85%" },
            { label: "OEE Improvement", value: "22%" }
          ]
        },
        {
          title: "Multi-Client Quality System",
          client: "Specialty CDMO",
          result: "Client Audit Excellence",
          tags: ["QMS", "Multi-Client", "Audit Ready", "cGMP"],
          icon: ClipboardCheck,
          color: '#db2777',
          highlight: "Quality System",
          challenge: "Develop scalable QMS capable of supporting diverse client requirements and regulatory standards.",
          solution: "Implemented modular quality system with client-specific overlays and automated compliance tracking.",
          metrics: [
            { label: "Client Audits Passed", value: "50+" },
            { label: "Regulatory Inspections", value: "12" },
            { label: "Major Findings", value: "0" },
            { label: "Client Satisfaction", value: "98%" }
          ]
        },
        {
          title: "Technology Transfer Excellence",
          client: "Pharma-CDMO Partnership",
          result: "Seamless Product Transfer",
          tags: ["Tech Transfer", "Process Validation", "Scale-up", "Knowledge Management"],
          icon: Truck,
          color: '#f472b6',
          highlight: "Tech Transfer",
          challenge: "Execute complex technology transfer of 15 products from sponsor to CDMO within aggressive timeline.",
          solution: "Structured tech transfer protocol with parallel workstreams and integrated risk management.",
          metrics: [
            { label: "Products Transferred", value: "15" },
            { label: "First-Time Success", value: "93%" },
            { label: "Timeline Variance", value: "-5%" },
            { label: "Batch Failures", value: "0" }
          ]
        }
      ]
    }
  };

  const currentIndustry = industryGroups[activeIndustry];
  const currentCase = currentIndustry.cases[activeSubCase];
  const industries = Object.keys(industryGroups);

  // Auto-cycle through industries and their sub-cases
  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setActiveSubCase((prev) => {
          const currentCases = industryGroups[activeIndustry].cases;
          if (prev + 1 < currentCases.length) {
            return prev + 1;
          } else {
            // Move to next industry
            const currentIdx = industries.indexOf(activeIndustry);
            const nextIdx = (currentIdx + 1) % industries.length;
            setActiveIndustry(industries[nextIdx]);
            return 0;
          }
        });
      }, 5000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHovered, activeIndustry, industries]);

  return (
    <section id="case-studies" className="py-28 bg-gradient-to-b from-gray-50 via-white to-gray-50 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-96 h-96 bg-[#0ea5e9]/5 rounded-full blur-[150px] animate-morph-blob"></div>
        <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#10b981]/5 rounded-full blur-[120px] animate-morph-blob" style={{ animationDelay: '5s' }}></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#8b5cf6]/3 rounded-full blur-[180px]"></div>
      </div>

      <div
        className="max-w-7xl mx-auto px-6 relative z-10"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#0ea5e9]/10 to-[#10b981]/10 rounded-full mb-6">
            <Award size={18} className="text-[#10b981]" />
            <span className="text-sm font-bold text-[#10b981] tracking-wider">SUCCESS STORIES</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6">
            Proven{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#8b5cf6]">
              Results
            </span>
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Real-world success stories demonstrating our commitment to delivering measurable outcomes for life sciences organizations worldwide.
          </p>
        </div>

        {/* Industry Navigation Pills */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {industries.map((industry) => {
            const ind = industryGroups[industry];
            const IconComponent = ind.icon;
            const isActive = activeIndustry === industry;
            return (
              <button
                key={industry}
                onClick={() => { setActiveIndustry(industry); setActiveSubCase(0); }}
                className={`group flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${isActive
                  ? 'text-white shadow-lg scale-105'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:shadow-md'
                  }`}
                style={{
                  background: isActive ? `linear-gradient(135deg, ${ind.color}, ${ind.color}cc)` : undefined,
                  boxShadow: isActive ? `0 10px 30px ${ind.color}30` : undefined
                }}
              >
                <IconComponent size={18} />
                <span>{industry}</span>
                {ind.cases.length > 1 && (
                  <span className={`px-2 py-0.5 text-xs rounded-full ${isActive ? 'bg-white/20' : 'bg-gray-100'}`}>
                    {ind.cases.length}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Sub-navigation for industries with multiple cases (like Pharmaceutical) */}
        {currentIndustry.cases.length > 1 && (
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 p-2 bg-white rounded-xl border border-gray-200 shadow-sm">
              {currentIndustry.cases.map((caseItem, idx) => {
                const CaseIcon = caseItem.icon;
                const isActive = activeSubCase === idx;
                return (
                  <button
                    key={idx}
                    onClick={() => setActiveSubCase(idx)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-300 ${isActive
                      ? 'text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    style={{
                      background: isActive ? `linear-gradient(135deg, ${caseItem.color}, ${caseItem.color}cc)` : undefined
                    }}
                  >
                    <CaseIcon size={16} />
                    <span>{caseItem.highlight}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Active Case Study Detail */}
        <div className="relative">
          <div
            className="absolute -inset-4 rounded-3xl blur-2xl opacity-10 transition-colors duration-500"
            style={{ backgroundColor: currentCase.color }}
          />

          <div className="relative bg-white rounded-3xl border border-gray-100 shadow-2xl overflow-hidden">
            {/* Top accent */}
            <div
              className="h-2 w-full"
              style={{ background: `linear-gradient(90deg, ${currentCase.color}, ${currentIndustry.color})` }}
            />

            <div className="p-8 lg:p-12">
              <div className="grid lg:grid-cols-2 gap-12">
                {/* Left - Case Info */}
                <div>
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span
                      className="px-4 py-1.5 rounded-full text-sm font-bold text-white"
                      style={{ backgroundColor: currentIndustry.color }}
                    >
                      {activeIndustry}
                    </span>
                    {currentIndustry.cases.length > 1 && (
                      <span
                        className="px-3 py-1 rounded-full text-xs font-bold text-white"
                        style={{ backgroundColor: currentCase.color }}
                      >
                        {currentCase.highlight}
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-gray-500 text-sm">
                      <Building2 size={14} />
                      {currentCase.client}
                    </span>
                  </div>

                  <h3 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">
                    {currentCase.title}
                  </h3>

                  {/* Result Badge */}
                  <div
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl mb-8"
                    style={{ backgroundColor: `${currentCase.color}15` }}
                  >
                    <CheckCircle2 size={20} style={{ color: currentCase.color }} />
                    <span className="font-bold text-gray-900">{currentCase.result}</span>
                  </div>

                  {/* Challenge & Solution */}
                  <div className="space-y-6">
                    <div className="p-5 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle size={16} className="text-amber-500" />
                        <span className="font-bold text-gray-900">Challenge</span>
                      </div>
                      <p className="text-gray-600">{currentCase.challenge}</p>
                    </div>

                    <div className="p-5 rounded-xl border-2" style={{ borderColor: `${currentCase.color}30`, backgroundColor: `${currentCase.color}05` }}>
                      <div className="flex items-center gap-2 mb-2">
                        <Lightbulb size={16} style={{ color: currentCase.color }} />
                        <span className="font-bold text-gray-900">Solution</span>
                      </div>
                      <p className="text-gray-600">{currentCase.solution}</p>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mt-6">
                    {currentCase.tags.map((tag, tidx) => (
                      <span
                        key={tidx}
                        className="px-3 py-1.5 bg-gray-100 text-gray-700 text-sm font-medium rounded-full hover:bg-gray-200 transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Right - Metrics */}
                <div>
                  <div
                    className="relative w-24 h-24 rounded-2xl flex items-center justify-center mb-8 shadow-xl"
                    style={{
                      background: `linear-gradient(135deg, ${currentCase.color}, ${currentCase.color}cc)`,
                      boxShadow: `0 20px 40px ${currentCase.color}30`
                    }}
                  >
                    {(() => {
                      const CaseIcon = currentCase.icon;
                      return <CaseIcon size={48} className="text-white" />;
                    })()}
                  </div>

                  <h4 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <BarChart3 size={18} style={{ color: currentCase.color }} />
                    Key Results & Metrics
                  </h4>

                  <div className="grid grid-cols-2 gap-4">
                    {currentCase.metrics.map((metric, midx) => (
                      <div
                        key={midx}
                        className="group p-5 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100 hover:shadow-lg hover:border-transparent transition-all duration-300"
                      >
                        <div
                          className="text-4xl font-black mb-1 group-hover:scale-105 transition-transform"
                          style={{ color: currentCase.color }}
                        >
                          {metric.value}
                        </div>
                        <div className="text-sm text-gray-600 font-medium">{metric.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* CTA */}
                  <button
                    onClick={() => window.dispatchEvent(new Event('openHelix'))}
                    className="mt-8 w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold text-white transition-all hover:scale-[1.02] hover:shadow-xl"
                    style={{
                      background: `linear-gradient(135deg, ${currentCase.color}, ${currentCase.color}cc)`,
                      boxShadow: `0 10px 30px ${currentCase.color}30`
                    }}
                  >
                    <Sparkles size={18} />
                    Discuss Similar Project
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>

              {/* Navigation for multiple cases */}
              {currentIndustry.cases.length > 1 && (
                <div className="mt-10 pt-8 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      <span className="font-semibold text-gray-900">{activeSubCase + 1}</span> of <span className="font-semibold text-gray-900">{currentIndustry.cases.length}</span> {activeIndustry} case studies
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setActiveSubCase(prev => prev > 0 ? prev - 1 : currentIndustry.cases.length - 1)}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-semibold"
                      >
                        <ChevronRight size={16} className="rotate-180" />
                        Previous
                      </button>
                      <button
                        onClick={() => setActiveSubCase(prev => (prev + 1) % currentIndustry.cases.length)}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-semibold text-white"
                        style={{ backgroundColor: currentCase.color }}
                      >
                        Next
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Dots indicator */}
                  <div className="flex justify-center mt-6 gap-2">
                    {currentIndustry.cases.map((c, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveSubCase(idx)}
                        className={`h-2.5 rounded-full transition-all duration-300 ${idx === activeSubCase ? 'w-8' : 'w-2.5 hover:scale-125'
                          }`}
                        style={{
                          backgroundColor: idx === activeSubCase ? c.color : '#e5e7eb'
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* All Pharmaceutical Case Studies Overview (when Pharmaceutical is selected) */}
        {activeIndustry === 'Pharmaceutical' && (
          <div className="mt-12">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Pill size={20} className="text-[#0ea5e9]" />
              All Pharmaceutical Success Stories
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {currentIndustry.cases.map((caseItem, idx) => {
                const CaseIcon = caseItem.icon;
                const isActive = activeSubCase === idx;
                return (
                  <button
                    key={idx}
                    onClick={() => setActiveSubCase(idx)}
                    className={`group relative p-5 rounded-xl border-2 text-left transition-all duration-300 overflow-hidden ${isActive
                      ? 'bg-white shadow-xl'
                      : 'bg-white/50 border-gray-100 hover:border-gray-200 hover:shadow-lg'
                      }`}
                    style={{ borderColor: isActive ? `${caseItem.color}50` : undefined }}
                  >
                    {/* Active glow */}
                    {isActive && (
                      <div className="absolute -inset-1 rounded-xl blur-lg opacity-10 -z-10" style={{ backgroundColor: caseItem.color }}></div>
                    )}

                    {/* Top color bar */}
                    <div
                      className={`absolute top-0 left-0 right-0 h-1 rounded-t-xl transition-all ${isActive ? 'opacity-100' : 'opacity-50'}`}
                      style={{ background: `linear-gradient(90deg, ${caseItem.color}, ${caseItem.color}80)` }}
                    />

                    <div className="flex items-start gap-3">
                      <div
                        className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform ${isActive ? 'scale-110' : 'group-hover:scale-105'}`}
                        style={{ backgroundColor: `${caseItem.color}15` }}
                      >
                        <CaseIcon size={24} style={{ color: caseItem.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span
                          className="text-xs font-bold px-2 py-0.5 rounded-full text-white"
                          style={{ backgroundColor: caseItem.color }}
                        >
                          {caseItem.highlight}
                        </span>
                        <h4 className="font-bold text-gray-900 mt-2 text-sm leading-tight truncate">
                          {caseItem.title}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">{caseItem.client}</p>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center gap-1 text-xs font-semibold" style={{ color: caseItem.color }}>
                        <CheckCircle2 size={12} />
                        {caseItem.result}
                      </div>
                      <ChevronRight size={16} className={`text-gray-400 transition-transform ${isActive ? 'translate-x-1' : 'group-hover:translate-x-1'}`} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Stats Banner */}
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Projects Completed', value: '2,800+', icon: CheckCircle2, color: '#0ea5e9' },
            { label: 'Client Satisfaction', value: '98%', icon: Award, color: '#10b981' },
            { label: 'Industries Served', value: '6+', icon: Building2, color: '#8b5cf6' },
            { label: 'Global Locations', value: '12+', icon: Globe2, color: '#ec4899' },
          ].map((stat, idx) => (
            <div
              key={idx}
              className="group p-6 bg-white rounded-2xl border border-gray-100 hover:shadow-lg hover:border-transparent transition-all duration-300"
            >
              <stat.icon size={24} style={{ color: stat.color }} className="mb-3 group-hover:scale-110 transition-transform" />
              <div className="text-3xl font-black text-gray-900 mb-1">{stat.value}</div>
              <div className="text-sm text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// News & Insights Section
// News Page Component
const NewsPage = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedNews, setSelectedNews] = useState(null);

  const categories = ['All', 'Industry News', 'Company Updates', 'Regulatory', 'Technology', 'Insights'];

  const newsItems = [
    {
      id: 1,
      category: 'Industry News',
      date: 'December 20, 2025',
      title: 'FDA Releases Updated Guidance on Computer System Validation',
      excerpt: 'The FDA has published new guidance emphasizing risk-based approaches to CSV, aligning with modern software development practices and GAMP 5 principles.',
      image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=250&fit=crop',
      readTime: '4 min read',
      featured: true,
      author: 'Dr. Sarah Chen'
    },
    {
      id: 2,
      category: 'Company Updates',
      date: 'December 18, 2025',
      title: 'UPT Expands Global Footprint with New European Operations Center',
      excerpt: 'United Pharma Technologies announces strategic expansion into EU markets with new Dublin facility, bringing validation and quality expertise to European manufacturers.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&h=250&fit=crop',
      readTime: '3 min read',
      featured: true,
      author: 'UPT Corporate'
    },
    {
      id: 3,
      category: 'Regulatory',
      date: 'December 15, 2025',
      title: 'EU Annex 1 Implementation: Key Compliance Strategies for 2026',
      excerpt: 'With the EU Annex 1 revision now in effect, pharmaceutical manufacturers must adapt contamination control strategies and validation approaches for continued compliance.',
      image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=400&h=250&fit=crop',
      readTime: '5 min read',
      featured: false,
      author: 'Michael Rodriguez'
    },
    {
      id: 4,
      category: 'Technology',
      date: 'December 12, 2025',
      title: 'Digital Twins Revolutionizing Pharmaceutical Manufacturing Validation',
      excerpt: 'Industry 4.0 technologies continue reshaping pharmaceutical manufacturing with AI-driven analytics, digital twins, and automated quality systems transforming validation approaches.',
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=250&fit=crop',
      readTime: '4 min read',
      featured: false,
      author: 'Jennifer Park'
    },
    {
      id: 5,
      category: 'Company Updates',
      date: 'December 8, 2025',
      title: 'UPT Achieves Record 98.5% Client Satisfaction in Q4 2025',
      excerpt: 'Our commitment to excellence has resulted in record-breaking client satisfaction scores, reflecting our dedication to quality service delivery and partnership approach.',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop',
      readTime: '2 min read',
      featured: false,
      author: 'UPT Corporate'
    },
    {
      id: 6,
      category: 'Regulatory',
      date: 'December 5, 2025',
      title: 'Data Integrity Best Practices: Implementing Robust ALCOA+ Frameworks',
      excerpt: 'Ensuring data integrity remains a top priority for regulatory agencies. Learn about implementing robust ALCOA+ frameworks that withstand inspection scrutiny.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop',
      readTime: '6 min read',
      featured: false,
      author: 'Dr. Emily Watson'
    },
    {
      id: 7,
      category: 'Insights',
      date: 'December 1, 2025',
      title: 'The Future of Cell and Gene Therapy Manufacturing: Validation Challenges',
      excerpt: 'As CGT products advance through clinical trials, manufacturing validation presents unique challenges requiring innovative approaches and specialized expertise.',
      image: 'https://images.unsplash.com/photo-1579154204601-01588f351e67?w=400&h=250&fit=crop',
      readTime: '7 min read',
      featured: false,
      author: 'Dr. James Morrison'
    },
    {
      id: 8,
      category: 'Technology',
      date: 'November 28, 2025',
      title: 'MES Implementation Success: Lessons from 50+ Life Sciences Deployments',
      excerpt: 'Drawing from extensive MES implementation experience, we share key success factors and common pitfalls in manufacturing execution system deployments.',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=250&fit=crop',
      readTime: '5 min read',
      featured: false,
      author: 'Robert Chang'
    },
    {
      id: 9,
      category: 'Industry News',
      date: 'November 22, 2025',
      title: 'Global Pharmaceutical Market Outlook: Validation Services Demand Surges',
      excerpt: 'Industry analysts project 15% annual growth in validation services as pharmaceutical companies accelerate facility expansions and digital transformation initiatives.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=250&fit=crop',
      readTime: '4 min read',
      featured: false,
      author: 'Amanda Foster'
    },
    {
      id: 10,
      category: 'Regulatory',
      date: 'November 18, 2025',
      title: 'FDA 483 Trends 2025: Most Common Observations and Prevention Strategies',
      excerpt: 'Analysis of 2025 FDA inspection data reveals key trends in observations. Learn proactive strategies to prevent common compliance gaps.',
      image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&h=250&fit=crop',
      readTime: '8 min read',
      featured: false,
      author: 'Dr. Patricia Kim'
    },
    {
      id: 11,
      category: 'Insights',
      date: 'November 12, 2025',
      title: 'Building a World-Class Validation Team: Recruitment and Development',
      excerpt: 'In a competitive talent market, attracting and developing validation professionals requires strategic approaches to recruitment, training, and career development.',
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=250&fit=crop',
      readTime: '5 min read',
      featured: false,
      author: 'Lisa Thompson'
    },
    {
      id: 12,
      category: 'Company Updates',
      date: 'November 8, 2025',
      title: 'UPT Launches AI-Powered Validation Document Assistant',
      excerpt: 'Introducing Helix 2.0: Our enhanced AI assistant now provides intelligent protocol generation, deviation analysis, and regulatory guidance for validation professionals.',
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=250&fit=crop',
      readTime: '3 min read',
      featured: false,
      author: 'UPT Innovation Team'
    }
  ];

  const filteredNews = activeCategory === 'All'
    ? newsItems
    : newsItems.filter(item => item.category === activeCategory);

  const featuredNews = newsItems.filter(item => item.featured);
  const regularNews = filteredNews.filter(item => !item.featured);

  const getCategoryColor = (category) => {
    const colors = {
      'Industry News': '#0ea5e9',
      'Company Updates': '#10b981',
      'Regulatory': '#f59e0b',
      'Technology': '#8b5cf6',
      'Insights': '#ec4899'
    };
    return colors[category] || '#6b7280';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header */}
      <div className="bg-gradient-to-r from-[#0a1525] to-[#0d2137] text-white">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <EnterpriseLogo variant="light" size="small" />
            </a>
            <a
              href="/"
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowRight size={16} className="rotate-180" />
              Back to Home
            </a>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#0a1525] via-[#0d2137] to-[#102a3f] py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-[#2d9b83] text-sm font-semibold mb-6">
            <FileText size={16} />
            NEWS & INSIGHTS
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
            Latest from{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] to-[#10b981]">
              UPT & Industry
            </span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Stay informed with the latest regulatory updates, industry trends, and company announcements
          </p>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-16 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#0369a1]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#047857]/5 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-5 py-2.5 rounded-full font-medium transition-all ${activeCategory === category
                  ? 'bg-gradient-to-r from-[#0369a1] to-[#047857] text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Featured News - Only show when 'All' is selected */}
          {activeCategory === 'All' && (
            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {featuredNews.map((news) => (
                <div
                  key={news.id}
                  onClick={() => setSelectedNews(news)}
                  className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer border border-gray-100"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={news.image}
                      alt={news.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <span
                        className="px-3 py-1 rounded-full text-xs font-bold text-white"
                        style={{ backgroundColor: getCategoryColor(news.category) }}
                      >
                        {news.category}
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <span className="text-white/80 text-sm">{news.date}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#0369a1] transition-colors line-clamp-2">
                      {news.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {news.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400 text-sm flex items-center gap-1">
                        <Clock size={14} />
                        {news.readTime}
                      </span>
                      <span className="text-[#0369a1] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read More
                        <ArrowRight size={16} />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Regular News Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(activeCategory === 'All' ? regularNews : filteredNews).map((news) => (
              <div
                key={news.id}
                onClick={() => setSelectedNews(news)}
                className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 hover:border-[#0369a1]/30"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={news.image}
                    alt={news.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3">
                    <span
                      className="px-2.5 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-wide"
                      style={{ backgroundColor: getCategoryColor(news.category) }}
                    >
                      {news.category}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                    <span>{news.date}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {news.readTime}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0369a1] transition-colors line-clamp-2">
                    {news.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                    {news.excerpt}
                  </p>
                  <span className="text-[#047857] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read Article
                    <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* News Detail Modal */}
      {selectedNews && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedNews(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="relative h-64 overflow-hidden rounded-t-2xl">
              <img
                src={selectedNews.image}
                alt={selectedNews.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <button
                onClick={() => setSelectedNews(null)}
                className="absolute top-4 right-4 p-2 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/40 transition-colors"
              >
                <X size={24} className="text-white" />
              </button>
              <div className="absolute bottom-4 left-6 right-6">
                <span
                  className="px-3 py-1 rounded-full text-xs font-bold text-white mb-3 inline-block"
                  style={{ backgroundColor: getCategoryColor(selectedNews.category) }}
                >
                  {selectedNews.category}
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-white">{selectedNews.title}</h2>
              </div>
            </div>
            <div className="p-6 md:p-8">
              <div className="flex items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b border-gray-100">
                <span className="flex items-center gap-1">
                  <Calendar size={16} />
                  {selectedNews.date}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={16} />
                  {selectedNews.readTime}
                </span>
                <span className="flex items-center gap-1">
                  <User size={16} />
                  {selectedNews.author}
                </span>
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  {selectedNews.excerpt}
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  As the life sciences industry continues to evolve, staying ahead of regulatory changes and technological advancements becomes increasingly critical. Organizations must adapt their strategies to maintain compliance while driving operational efficiency.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  United Pharma Technologies remains committed to providing expert guidance and support throughout this journey. Our team of experienced professionals brings decades of combined expertise in validation, quality systems, and regulatory affairs.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  For more information about how UPT can support your organization's compliance and quality initiatives, contact our team of experts today.
                </p>
              </div>
              <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">Share:</span>
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <Linkedin size={18} className="text-[#0077b5]" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <Twitter size={18} className="text-[#1da1f2]" />
                  </button>
                </div>
                <button
                  onClick={() => setSelectedNews(null)}
                  className="px-6 py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-all"
                >
                  Close Article
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

// About Section - White theme with glowing effects
const AboutSection = () => (
  <section id="about" className="py-28 bg-white relative overflow-hidden">
    {/* Subtle background pattern */}
    <div className="absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: `radial-gradient(circle at 1px 1px, #10b981 1px, transparent 0)`,
      backgroundSize: '40px 40px'
    }}></div>

    {/* Animated background blobs */}
    <div className="absolute top-20 left-10 w-96 h-96 bg-[#0ea5e9]/5 rounded-full blur-[150px] animate-morph-blob"></div>
    <div className="absolute bottom-20 right-10 w-80 h-80 bg-[#10b981]/5 rounded-full blur-[120px] animate-morph-blob" style={{ animationDelay: '5s' }}></div>
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#8b5cf6]/3 rounded-full blur-[180px]"></div>

    {/* Floating particles */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(10)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 rounded-full quantum-float"
          style={{
            backgroundColor: ['#0ea5e9', '#10b981', '#8b5cf6'][i % 3],
            left: `${(i * 13) % 100}%`,
            top: `${(i * 17) % 100}%`,
            animationDelay: `${i * 0.4}s`,
            opacity: 0.3
          }}
        />
      ))}
    </div>

    <div className="max-w-7xl mx-auto px-6 relative z-10">
      {/* Section Header */}
      <div className="text-center max-w-3xl mx-auto mb-16">
        <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#0ea5e9]/10 to-[#10b981]/10 rounded-full mb-6">
          <Building2 size={18} className="text-[#10b981]" />
          <span className="text-sm font-bold text-[#10b981] tracking-wider">ABOUT UPT</span>
        </div>
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6">
          Your Trusted{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#8b5cf6]">
            Compliance Partner
          </span>
        </h2>
        <p className="text-xl text-gray-600 leading-relaxed">
          Since 2014, United Pharma Technologies has been the trusted partner for life sciences organizations seeking validation, quality, and regulatory excellence across the globe.
        </p>
      </div>

      <div className="grid lg:grid-cols-1 gap-16 items-center mb-20">
        <div>
          <p className="text-gray-700 text-lg mb-8 leading-relaxed text-center max-w-4xl mx-auto">
            Our team of expert professionals brings deep domain expertise across pharmaceuticals, biotechnology, medical devices, and diagnostics. We are committed to delivering solutions that not only meet regulatory requirements but drive operational excellence.
          </p>

          {/* Core Values */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { icon: Globe2, label: "Global Reach", desc: "Worldwide coverage", color: '#0ea5e9' },
              { icon: Award, label: "Industry Leaders", desc: "Since 2014", color: '#10b981' },
              { icon: Handshake, label: "Trusted Partner", desc: "98% client retention", color: '#8b5cf6' },
              { icon: Target, label: "Results Focused", desc: "2800+ projects", color: '#ec4899' }
            ].map((item, idx) => (
              <div
                key={idx}
                className="group relative p-4 bg-gray-50 border border-gray-100 rounded-xl hover:bg-white hover:shadow-lg hover:border-transparent transition-all duration-300"
              >
                {/* Hover glow */}
                <div
                  className="absolute -inset-1 rounded-xl blur-lg opacity-0 group-hover:opacity-20 transition-opacity -z-10"
                  style={{ backgroundColor: item.color }}
                />
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <item.icon size={20} style={{ color: item.color }} />
                  </div>
                  <span className="font-bold text-gray-900">{item.label}</span>
                </div>
                <p className="text-gray-500 text-sm pl-13">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Certifications */}
          <div className="flex flex-wrap justify-center gap-3">
            {['ISO 13485', 'GAMP 5', 'EU GMP', '21 CFR Part 11'].map((cert, idx) => (
              <span
                key={idx}
                className="px-4 py-2 bg-gray-50 border border-gray-100 rounded-lg text-sm font-semibold text-gray-600 hover:bg-white hover:shadow-md hover:border-transparent transition-all duration-300"
              >
                {cert}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Leadership & Expertise */}
      <div className="relative bg-gradient-to-br from-gray-50 to-white border-2 border-gray-100 rounded-3xl p-8 lg:p-12 overflow-hidden">
        {/* Subtle glow */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#10b981]/5 rounded-full blur-[100px]"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#0ea5e9]/5 rounded-full blur-[100px]"></div>

        <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-3 relative z-10">
          <Sparkles size={24} className="text-[#10b981]" />
          Why Organizations Choose UPT
        </h3>
        <div className="grid md:grid-cols-3 gap-6 relative z-10">
          {[
            {
              title: "Deep Domain Expertise",
              desc: "Our team includes former FDA inspectors, industry veterans, and subject matter experts with hands-on experience across all life sciences sectors.",
              icon: GraduationCap,
              color: '#0ea5e9'
            },
            {
              title: "Scalable Solutions",
              desc: "From single-site projects to global enterprise programs, we scale our services to match your needs while maintaining consistent quality.",
              icon: TrendingUp,
              color: '#10b981'
            },
            {
              title: "Technology-Enabled",
              desc: "We leverage cutting-edge tools including AI-assisted documentation, digital workflows, and real-time project dashboards.",
              icon: CircuitBoard,
              color: '#8b5cf6'
            }
          ].map((item, idx) => (
            <div
              key={idx}
              className="group relative p-6 bg-white rounded-xl border border-gray-100 hover:shadow-xl hover:border-transparent transition-all duration-300"
            >
              {/* Glow effect */}
              <div
                className="absolute -inset-1 rounded-xl blur-xl opacity-0 group-hover:opacity-15 transition-opacity -z-10"
                style={{ backgroundColor: item.color }}
              />

              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                style={{ backgroundColor: `${item.color}15` }}
              >
                <item.icon size={28} style={{ color: item.color }} />
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h4>
              <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Location Banner */}
      <div className="mt-12 text-center">
        <div className="inline-flex flex-wrap justify-center items-center gap-4 p-6 bg-gradient-to-r from-gray-50 via-white to-gray-50 rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center">
              <MapPin size={20} className="text-[#0ea5e9]" />
            </div>
            <div className="text-left">
              <span className="text-gray-900 font-bold block">United States</span>
              <span className="text-gray-500 text-sm">120 Wood Avenue South, Suite 406, Iselin, NJ 08830</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Contact Section - Light with accent border
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", company: "", phone: "", service: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      console.log('Submitting demo request with data:', JSON.stringify(formData, null, 2));
      const response = await axios.post(`${API}/hiring_requests/contact_request_submit`, formData, {
        timeout: 10000
      });
      console.log(response.data);
      setSubmitStatus(response.data === "SUCCESS" ? "success" : "error");
      if (response.data === "SUCCESS") setFormData({ name: "", email: "", company: "", phone: "", service: "", message: "" });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus("error");
      if (error.response?.status === 500 || error.code === 'ECONNABORTED') {
        toast({
          title: "Server Error",
          description: "The server is temporarily unavailable. Please try again later.",
          variant: "destructive"
        });
      }
    }
    setIsSubmitting(false);
    setTimeout(() => setSubmitStatus(null), 5000);
  };

  return (
    <section id="contact" className="py-24 bg-white relative overflow-hidden">
      {/* Top border accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0369a1] via-[#047857] to-[#10b981]"></div>

      {/* Background pattern */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `linear-gradient(135deg, #f8fafc 25%, transparent 25%), linear-gradient(225deg, #f8fafc 25%, transparent 25%), linear-gradient(45deg, #f8fafc 25%, transparent 25%), linear-gradient(315deg, #f8fafc 25%, transparent 25%)`,
        backgroundSize: '40px 40px',
        backgroundPosition: '0 0, 20px 0, 20px -20px, 0px 20px'
      }}></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="text-[#047857] font-semibold tracking-wider text-sm mb-3 flex items-center gap-2">
              <span className="w-8 h-0.5 bg-gradient-to-r from-[#0369a1] to-[#047857]"></span>
              CONTACT
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">Let's Discuss Your Project</h2>
            <p className="text-xl text-gray-600 mb-10">Ready to achieve compliance excellence? Our experts are here to help.</p>

            <div className="space-y-6">
              {[
                { icon: Phone, label: "Phone", value: "+1 732-769-8433", href: "tel:+17327698433" },
                { icon: Mail, label: "Email", value: "info@unitedpharmatechnologies.com", href: "mailto:info@unitedpharmatechnologies.com" },
                { icon: MapPin, label: "Location", value: "New Jersey, United States", href: "#" }
              ].map((item, idx) => (
                <a key={idx} href={item.href} className="flex items-start gap-4 group p-4 rounded-xl hover:bg-gray-50 transition-colors">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#0369a1] to-[#047857] rounded-xl flex items-center justify-center shadow-lg shadow-[#047857]/20">
                    <item.icon className="text-white" size={22} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">{item.label}</div>
                    <div className="font-semibold text-gray-900 group-hover:text-[#047857] transition-colors">{item.value}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-xl shadow-black/5 border-2 border-gray-100">
            {submitStatus == "success" && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 font-medium">
                Thank you! We'll be in touch within 24 hours.
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <input type="text" required placeholder="Name *" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a936f] focus:border-transparent outline-none transition-all" />
                <input type="email" required placeholder="Email *" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a936f] focus:border-transparent outline-none transition-all" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <input type="text" placeholder="Company" value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a936f] focus:border-transparent outline-none transition-all" />
                <input type="tel" placeholder="Phone" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a936f] focus:border-transparent outline-none transition-all" />
              </div>
              <select value={formData.service} onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a936f] focus:border-transparent outline-none text-gray-600 transition-all">
                <option value="">Service Interest</option>
                <option value="validation">Validation Services</option>
                <option value="automation">Automation Solutions</option>
                <option value="quality">Quality Systems</option>
                <option value="regulatory">Regulatory Affairs</option>
              </select>
              <textarea rows={4} required placeholder="Tell us about your project *" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a936f] focus:border-transparent outline-none resize-none transition-all"></textarea>
              <button type="submit" disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-[#1e5f74] to-[#1a936f] text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-[#1a936f]/20 transition-all disabled:opacity-50 hover:-translate-y-0.5">
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer - Enhanced Enterprise Design
const Footer = () => (
  <footer className="relative bg-gradient-to-b from-[#0a1525] via-[#0d1a2d] to-[#060d16] text-white pt-20 pb-8 overflow-hidden">
    {/* Animated background effects */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Gradient orbs */}
      <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-[#0ea5e9]/5 rounded-full blur-[150px]"></div>
      <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-[#10b981]/5 rounded-full blur-[120px]"></div>

      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }}></div>
    </div>

    {/* Top accent gradient */}
    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0ea5e9] via-[#10b981] to-[#8b5cf6]"></div>

    <div className="max-w-7xl mx-auto px-6 relative z-10">
      {/* Main Footer Content */}
      <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-10 mb-16">
        {/* Company Info */}
        <div className="lg:col-span-2">
          {/* Logo Image */}
          <img
            src="/upt-logo-hd.png"
            alt="United Pharma Technologies Logo"
            className="h-14 w-auto"
          />
          <p className="text-gray-400 mt-6 mb-4 text-base leading-relaxed max-w-sm">
            Your trusted partner for life sciences validation, pharma staffing, and regulatory excellence.
          </p>

          {/* Social Links */}
          <div className="flex gap-3 mb-8">
            {[
              { icon: Linkedin, color: '#0077B5', label: 'LinkedIn' },
              { icon: Twitter, color: '#1DA1F2', label: 'Twitter' },
              { icon: Facebook, color: '#4267B2', label: 'Facebook' }
            ].map((social, idx) => (
              <a
                key={idx}
                href="#"
                className="group w-11 h-11 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center hover:scale-110 transition-all duration-300"
                style={{ '--hover-color': social.color }}
              >
                <social.icon size={18} className="text-gray-400 group-hover:text-white transition-colors" />
              </a>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-bold text-lg mb-6 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#0ea5e9]"></span>
            Industries USA
          </h4>
          <ul className="space-y-3">
            {['Pharmaceutical USA', 'Biotechnology USA', 'Medical Devices USA', 'Cell & Gene Therapy', 'CDMO/CMO USA', 'IVD Diagnostics'].map((link, idx) => (
              <li key={idx}>
                <a href="#industries" className="group flex items-center text-gray-400 hover:text-[#0ea5e9] transition-colors text-sm">
                  <ChevronRight size={14} className="mr-1 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                  {link}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-bold text-lg mb-6 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#10b981]"></span>
            Services USA
          </h4>
          <ul className="space-y-3">
            {['GMP Validation USA', 'CQV Services USA', 'CSV Consulting USA', 'Quality Systems USA', 'Automation Services USA', 'Regulatory Affairs USA', 'Pharma Staffing USA'].map((link, idx) => (
              <li key={idx}>
                <a href="#services" className="group flex items-center text-gray-400 hover:text-[#10b981] transition-colors text-sm">
                  <ChevronRight size={14} className="mr-1 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                  {link}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Staffing Section */}
        <div>
          <h4 className="font-bold text-lg mb-6 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#ec4899]"></span>
            Staffing USA
          </h4>
          <ul className="space-y-3">
            {['Validation Engineers', 'CQV Engineers', 'CSV Consultants', 'QA Specialists', 'Automation Engineers', 'Process Engineers'].map((link, idx) => (
              <li key={idx}>
                <a href="/careers" className="group flex items-center text-gray-400 hover:text-[#ec4899] transition-colors text-sm">
                  <ChevronRight size={14} className="mr-1 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                  {link}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact US - Give it more width */}
        <div className="lg:col-span-1">
          <h4 className="font-bold text-lg mb-6 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#8b5cf6]"></span>
            Contact US
          </h4>
          <ul className="space-y-3">
            <li>
              <a href="tel:+17327698433" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group text-sm">
                <Phone size={14} className="text-[#0ea5e9] flex-shrink-0" />
                <span>+1 732-769-8433</span>
              </a>
            </li>
            <li>
              <a href="mailto:info@unitedpharmatechnologies.com" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group text-sm">
                <Mail size={14} className="text-[#10b981] flex-shrink-0" />
                <span>info@unitedpharmatechnologies.com</span>
              </a>
            </li>
            <li>
              <div className="flex items-start gap-2 text-gray-400 text-sm">
                <MapPin size={14} className="text-[#8b5cf6] flex-shrink-0 mt-0.5" />
                <div>
                  <div>120 Wood Ave S, Suite 406</div>
                  <div>Iselin, NJ 08830, USA</div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="mb-12 p-8 bg-gradient-to-r from-white/5 via-white/10 to-white/5 rounded-2xl border border-white/10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <h4 className="text-xl font-bold mb-2">Stay Updated with Industry Insights</h4>
            <p className="text-gray-400">Get the latest compliance news, regulatory updates, and best practices.</p>
          </div>
          <div className="flex gap-3">
            <input
              type="email"
              placeholder="Enter your email"
              className="px-5 py-3 bg-white/5 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#0ea5e9] transition-colors w-64"
            />
            <button className="px-6 py-3 bg-gradient-to-r from-[#0ea5e9] to-[#10b981] rounded-xl font-semibold hover:shadow-lg hover:shadow-[#0ea5e9]/20 transition-all hover:scale-105">
              Subscribe
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 pt-8">
        {/* SEO Keywords Section - Services & Staffing Across USA */}
        <div className="mb-8 text-xs text-gray-600 leading-relaxed">
          <p className="mb-3">
            <strong className="text-gray-500">Pharma Services USA:</strong> United Pharma Technologies (UPT) provides comprehensive pharma services including GMP validation services, CQV services, commissioning qualification validation, equipment qualification, process validation, HVAC validation, utilities qualification, cleaning validation, thermal mapping, computer system validation (CSV), 21 CFR Part 11 compliance, CSA consulting, data integrity consulting, GAMP 5 consulting, quality assurance consulting, FDA compliance consulting, FDA audit readiness, GMP remediation services, and regulatory affairs support.
          </p>
          <p className="mb-3">
            <strong className="text-gray-500">Life Sciences Staffing USA:</strong> Premier life sciences staffing and pharma staffing services including validation engineers staffing, CQV engineers, CSV consultants, GMP QA consultants, quality engineers pharma, automation engineers pharma, process engineers, manufacturing engineers, quality control analysts, regulatory affairs specialists, compliance specialists, project managers. We offer contract staffing, permanent placement, executive search, staff augmentation, and temp-to-perm solutions for pharmaceutical, biotechnology, medical device, and CDMO industries.
          </p>
          <p className="mb-3">
            <strong className="text-gray-500">Automation & Digital Services:</strong> PLC SCADA pharma, DCS programming, batch automation ISA-88, MES consulting, MES implementation, electronic batch records, digital manufacturing, smart manufacturing pharma, Industry 4.0, IoT pharma manufacturing, digital twin, predictive maintenance, paperless manufacturing, and GMP automation consulting services across USA.
          </p>
          <p className="mb-3">
            <strong className="text-gray-500">Medical Device & Biotech Services:</strong> ISO 13485 consulting, FDA medical device compliance, EU MDR consulting, IVDR consulting, design controls, DHF consulting, SaMD validation, software validation medical device, biotech consulting, cell and gene therapy consulting, ATMP consulting, biologics manufacturing, sterile manufacturing, aseptic processing, single use systems validation.
          </p>
          <p className="mb-3">
            <strong className="text-gray-500">Serving All 50 US States:</strong> Alabama, Alaska, Arizona, Arkansas, California, Colorado, Connecticut, Delaware, Florida, Georgia, Hawaii, Idaho, Illinois, Indiana, Iowa, Kansas, Kentucky, Louisiana, Maine, Maryland, Massachusetts, Michigan, Minnesota, Mississippi, Missouri, Montana, Nebraska, Nevada, New Hampshire, New Jersey, New Mexico, New York, North Carolina, North Dakota, Ohio, Oklahoma, Oregon, Pennsylvania, Rhode Island, South Carolina, South Dakota, Tennessee, Texas, Utah, Vermont, Virginia, Washington, West Virginia, Wisconsin, Wyoming, Puerto Rico, Washington DC.
          </p>
          <p>
            <strong className="text-gray-500">Major Pharma Hub Cities:</strong> Boston, Cambridge, San Diego, San Francisco, Los Angeles, New York City, Philadelphia, Chicago, Houston, Dallas, Austin, Research Triangle Park (RTP), Raleigh, Durham, Charlotte, Atlanta, Miami, Tampa, Orlando, Seattle, Denver, Phoenix, Indianapolis, Minneapolis, Baltimore, Hartford, New Haven, Worcester, Providence, Pittsburgh, Cleveland, Cincinnati, Columbus, Detroit, St. Louis, Kansas City, Nashville, Salt Lake City, San Jose, Irvine, and more.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} United Pharma Technologies. All rights reserved.
          </p>

          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
            <a href="/privacy" className="hover:text-[#0ea5e9] transition-colors">Privacy Policy</a>
            <a href="/terms" className="hover:text-[#10b981] transition-colors">Terms of Service</a>
            <a href="/cookies" className="hover:text-[#8b5cf6] transition-colors">Cookie Policy</a>
            <a href="/accessibility" className="hover:text-[#ec4899] transition-colors">Accessibility</a>
          </div>

          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <span>Powered by</span>
            <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] to-[#10b981]">UPT Excellence</span>
          </div>
        </div>
      </div>
    </div>
  </footer>
);

// Helix Dedicated Page Component
const HelixPage = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm Helix, UPT's Compliance & Automation Intelligence. I'm here to provide structured guidance across validation, automation, quality, and regulatory compliance for regulated life-sciences operations.\n\nHow can I assist you today?"
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/helix/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I apologize, but I'm having trouble connecting right now. Please try again or contact UPT directly at +1 732-769-8433."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const suggestedQuestions = [
    "What is GAMP 5 and how does it apply to validation?",
    "Explain 21 CFR Part 11 compliance requirements",
    "What are the key principles of data integrity (ALCOA+)?",
    "How does UPT approach computer system validation?",
    "What is ISA-88 batch control?",
    "Explain IQ, OQ, PQ qualification stages"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1525] via-[#0d2137] to-[#102a3f]">
      {/* Header */}
      <header className="bg-white/5 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <EnterpriseLogo variant="light" size="small" />
          </a>
          <a
            href="/"
            className="text-white/70 hover:text-white transition-colors flex items-center gap-2"
          >
            <ArrowRight size={16} className="rotate-180" />
            Back to Home
          </a>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Sidebar - About Helix */}
          <div className="lg:col-span-1 space-y-6">
            {/* Helix Intro Card */}
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg, #0369a1 0%, #047857 100%)' }}
                >
                  <Sparkles size={28} className="text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Helix</h1>
                  <p className="text-white/60 text-sm">Compliance & Automation Intelligence</p>
                </div>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                Helix is United Pharma Technologies' digital intelligence layer, designed to support
                decision-making across pharmaceutical, biotechnology, and medical device manufacturing environments.
              </p>
            </div>

            {/* Expertise Areas */}
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Target size={18} className="text-[#047857]" />
                Expertise Areas
              </h3>
              <div className="space-y-3">
                {[
                  { icon: ShieldCheck, label: "Validation & Qualification", color: "#0369a1" },
                  { icon: Settings2, label: "Automation & Digital Manufacturing", color: "#047857" },
                  { icon: ClipboardCheck, label: "Quality & Compliance", color: "#0ea5e9" },
                  { icon: Cpu, label: "Medical Device & ISO", color: "#10b981" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-white/80 text-sm">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${item.color}20` }}
                    >
                      <item.icon size={16} style={{ color: item.color }} />
                    </div>
                    {item.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-amber-500/10 backdrop-blur-xl rounded-2xl border border-amber-500/20 p-6">
              <h3 className="text-amber-400 font-semibold mb-3 flex items-center gap-2">
                <FileCheck size={18} />
                Important Note
              </h3>
              <p className="text-amber-200/80 text-sm leading-relaxed">
                Helix supports informed decision-making but does not replace qualified professionals,
                provide regulatory approvals, or generate final validation documents.
                Execution remains with UPT experts.
              </p>
            </div>

            {/* Contact CTA */}
            <div className="bg-gradient-to-br from-[#0369a1]/20 to-[#047857]/20 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
              <h3 className="text-white font-semibold mb-3">Need Expert Assistance?</h3>
              <p className="text-white/60 text-sm mb-4">
                For detailed project discussions, contact UPT directly.
              </p>
              <a
                href="tel:+17327698433"
                className="flex items-center gap-2 text-[#10b981] hover:text-[#34d399] transition-colors text-sm font-medium"
              >
                <Phone size={16} />
                +1 732-769-8433
              </a>
            </div>
          </div>

          {/* Main Chat Area */}
          <div className="lg:col-span-2">
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden h-[calc(100vh-200px)] flex flex-col">
              {/* Chat Header */}
              <div
                className="px-6 py-4 border-b border-white/10 flex items-center justify-between"
                style={{ background: 'linear-gradient(135deg, #0369a1/30 0%, #047857/30 100%)' }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-white font-medium">Chat with Helix</span>
                </div>
                <span className="text-white/50 text-sm">Powered by AI</span>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] px-5 py-4 rounded-2xl ${msg.role === 'user'
                        ? 'bg-gradient-to-r from-[#0369a1] to-[#047857] text-white rounded-br-md'
                        : 'bg-white/10 text-white rounded-bl-md border border-white/10'
                        }`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-white/10">
                          <Sparkles size={16} className="text-[#10b981]" />
                          <span className="text-sm font-semibold text-[#10b981]">Helix</span>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white/10 px-5 py-4 rounded-2xl rounded-bl-md border border-white/10">
                      <div className="flex items-center gap-3">
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-[#0369a1] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-2 h-2 bg-[#047857] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-[#10b981] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        <span className="text-sm text-white/50">Helix is thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Suggested Questions */}
              {messages.length <= 1 && (
                <div className="px-6 pb-4">
                  <p className="text-white/50 text-xs mb-3">Suggested questions:</p>
                  <div className="flex flex-wrap gap-2">
                    {suggestedQuestions.slice(0, 3).map((q, idx) => (
                      <button
                        key={idx}
                        onClick={() => setInputValue(q)}
                        className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-white/70 text-xs transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className="p-4 border-t border-white/10 bg-white/5">
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about validation, compliance, automation..."
                    className="flex-1 px-5 py-4 rounded-xl bg-white/10 border border-white/10 focus:outline-none focus:border-[#047857] focus:ring-2 focus:ring-[#047857]/20 text-white placeholder-white/40 text-sm"
                    disabled={isLoading}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!inputValue.trim() || isLoading}
                    className="w-14 h-14 rounded-xl flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      background: inputValue.trim() ? 'linear-gradient(135deg, #0369a1 0%, #047857 100%)' : 'rgba(255,255,255,0.1)'
                    }}
                  >
                    <Send size={22} className={inputValue.trim() ? 'text-white' : 'text-white/40'} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Jobs Data - Shared between CareersPage and JobDetailPage
const jobsData = [
  // Validation Department (12 roles)
  { id: 1, title: "Senior Validation Engineer", department: "Validation", location: "New Jersey, USA", type: "Full-time", experience: "5+ years", description: "Lead validation projects for pharmaceutical manufacturing systems, including IQ/OQ/PQ protocols and CSV activities.", salary: "$120k - $150k", urgent: true, responsibilities: ["Lead IQ/OQ/PQ protocol development and execution", "Manage CSV activities for computerized systems", "Coordinate with cross-functional teams", "Review and approve validation documentation", "Mentor junior engineers"], requirements: ["Bachelor's degree in Engineering or Science", "5+ years pharmaceutical validation experience", "Strong knowledge of FDA regulations and EU guidelines", "Experience with GAMP 5 methodology", "Excellent communication skills"], benefits: ["Competitive salary with annual bonus", "Comprehensive health insurance", "401(k) with company match", "Professional development opportunities", "Flexible work arrangements"] },
  { id: 2, title: "CSV Specialist", department: "Validation", location: "Remote", type: "Full-time", experience: "3+ years", description: "Execute computer system validation activities per GAMP 5 guidelines for regulated clients.", salary: "$90k - $120k", urgent: false, responsibilities: ["Execute CSV activities per GAMP 5", "Develop validation protocols and reports", "Perform risk assessments", "Support system implementations", "Maintain validation documentation"], requirements: ["Bachelor's degree in relevant field", "3+ years CSV experience", "Knowledge of 21 CFR Part 11", "Experience with LIMS, ERP, or MES systems", "Strong analytical skills"], benefits: ["Remote work flexibility", "Health and dental insurance", "Paid time off", "Training allowance", "Career growth opportunities"] },
  { id: 3, title: "Equipment Qualification Lead", department: "Validation", location: "Boston, USA", type: "Full-time", experience: "5+ years", description: "Manage equipment qualification programs including FAT, SAT, IQ, OQ, and PQ execution.", salary: "$115k - $145k", urgent: true, responsibilities: ["Lead equipment qualification projects", "Develop FAT/SAT/IQ/OQ/PQ protocols", "Coordinate with vendors and stakeholders", "Manage project timelines and budgets", "Ensure regulatory compliance"], requirements: ["Bachelor's degree in Engineering", "5+ years equipment qualification experience", "Knowledge of pharmaceutical manufacturing equipment", "Project management skills", "Strong technical writing abilities"], benefits: ["Competitive compensation", "Health benefits", "401(k) plan", "Professional certifications support", "Relocation assistance"] },
  { id: 4, title: "Cleaning Validation Specialist", department: "Validation", location: "New Jersey, USA", type: "Full-time", experience: "3+ years", description: "Develop and execute cleaning validation protocols for multi-product manufacturing facilities.", salary: "$85k - $110k", urgent: false, responsibilities: ["Develop cleaning validation protocols", "Execute cleaning studies", "Calculate acceptance criteria", "Review analytical methods", "Support regulatory inspections"], requirements: ["Bachelor's degree in Chemistry or Science", "3+ years cleaning validation experience", "Knowledge of FDA cleaning validation guidance", "Analytical chemistry background", "Detail-oriented"], benefits: ["Health insurance", "Dental and vision", "Paid holidays", "Training programs", "Team environment"] },
  { id: 5, title: "Process Validation Engineer", department: "Validation", location: "Philadelphia, USA", type: "Full-time", experience: "4+ years", description: "Lead process validation studies including PPQ protocols and continued process verification programs.", salary: "$100k - $130k", urgent: false, responsibilities: ["Lead process validation studies", "Develop PPQ protocols", "Implement CPV programs", "Analyze process data", "Support tech transfer activities"], requirements: ["Bachelor's in Engineering or Science", "4+ years process validation experience", "Statistical analysis skills", "Knowledge of process capability", "Manufacturing experience preferred"], benefits: ["Competitive salary", "Annual bonus potential", "Comprehensive benefits", "Career advancement", "Work-life balance"] },
  { id: 6, title: "Thermal Mapping Specialist", department: "Validation", location: "San Francisco, USA", type: "Contract", experience: "2+ years", description: "Conduct thermal mapping studies for warehouses, cold rooms, and controlled environments.", salary: "$75k - $95k", urgent: false, responsibilities: ["Conduct thermal mapping studies", "Install and calibrate sensors", "Analyze temperature data", "Prepare mapping reports", "Recommend corrective actions"], requirements: ["Technical degree preferred", "2+ years thermal mapping experience", "Knowledge of GDP requirements", "Experience with data loggers", "Willingness to travel"], benefits: ["Competitive hourly rate", "Travel expenses covered", "Flexible schedule", "Project variety", "Skill development"] },
  { id: 7, title: "Validation Documentation Specialist", department: "Validation", location: "Remote", type: "Full-time", experience: "2+ years", description: "Prepare and review validation documentation including protocols, reports, and SOPs.", salary: "$65k - $85k", urgent: false, responsibilities: ["Prepare validation documentation", "Review protocols and reports", "Maintain document control", "Support audit preparations", "Update SOPs as needed"], requirements: ["Bachelor's degree", "2+ years documentation experience", "Strong technical writing skills", "Attention to detail", "Knowledge of GMP requirements"], benefits: ["Remote work", "Health insurance", "PTO", "Professional development", "Supportive team"] },
  { id: 8, title: "CQV Project Manager", department: "Validation", location: "Research Triangle, NC", type: "Full-time", experience: "7+ years", description: "Manage large-scale CQV projects for pharmaceutical facility startups and expansions.", salary: "$130k - $160k", urgent: true, responsibilities: ["Manage CQV project execution", "Develop project schedules", "Lead project teams", "Interface with clients", "Ensure quality deliverables"], requirements: ["Bachelor's degree in Engineering", "7+ years CQV experience", "PMP certification preferred", "Strong leadership skills", "Capital project experience"], benefits: ["Executive compensation", "Bonus program", "Full benefits package", "Leadership role", "High-impact projects"] },
  { id: 9, title: "Sterilization Validation Engineer", department: "Validation", location: "San Diego, USA", type: "Full-time", experience: "4+ years", description: "Lead sterilization validation programs for medical devices and pharmaceutical products.", salary: "$95k - $125k", urgent: false, responsibilities: ["Lead sterilization validation", "Develop validation protocols", "Coordinate with microbiology", "Support regulatory submissions", "Maintain validation lifecycle"], requirements: ["Bachelor's in Engineering or Science", "4+ years sterilization experience", "Knowledge of ISO 11135, 11137, 11137", "Medical device or pharma background", "Problem-solving skills"], benefits: ["Competitive salary", "Health benefits", "401(k)", "Growth opportunities", "Innovative environment"] },
  { id: 10, title: "Packaging Validation Specialist", department: "Validation", location: "Chicago, USA", type: "Full-time", experience: "3+ years", description: "Execute packaging validation studies including seal integrity and stability testing.", salary: "$80k - $105k", urgent: false, responsibilities: ["Execute packaging validation", "Perform seal integrity tests", "Support stability studies", "Develop validation protocols", "Review packaging specifications"], requirements: ["Bachelor's degree", "3+ years packaging experience", "Knowledge of ASTM standards", "Laboratory skills", "Quality mindset"], benefits: ["Health insurance", "Retirement plan", "Paid time off", "Training opportunities", "Team collaboration"] },
  { id: 11, title: "Method Validation Scientist", department: "Validation", location: "Boston, USA", type: "Full-time", experience: "4+ years", description: "Perform analytical method validation and transfer activities for pharmaceutical products.", salary: "$90k - $120k", urgent: true, responsibilities: ["Perform method validation", "Execute method transfers", "Develop validation protocols", "Analyze validation data", "Support regulatory submissions"], requirements: ["Bachelor's or Master's in Chemistry", "4+ years method validation experience", "HPLC and dissolution expertise", "ICH guidelines knowledge", "Strong analytical skills"], benefits: ["Competitive compensation", "Scientific environment", "Career growth", "Benefits package", "Research opportunities"] },
  { id: 12, title: "Utilities Qualification Engineer", department: "Validation", location: "Houston, USA", type: "Full-time", experience: "3+ years", description: "Qualify critical utility systems including WFI, purified water, and clean steam.", salary: "$85k - $115k", urgent: false, responsibilities: ["Qualify utility systems", "Develop IQ/OQ/PQ protocols", "Monitor utility performance", "Support system maintenance", "Ensure compliance"], requirements: ["Bachelor's in Engineering", "3+ years utilities experience", "Knowledge of USP requirements", "Experience with water systems", "Technical aptitude"], benefits: ["Health benefits", "401(k) match", "PTO", "Professional development", "Team environment"] },

  // Automation Department (10 roles)
  { id: 13, title: "Automation Engineer", department: "Automation", location: "Boston, USA", type: "Full-time", experience: "4+ years", description: "Design and implement PLC/DCS control systems for pharmaceutical manufacturing facilities.", salary: "$100k - $130k", urgent: false, responsibilities: ["Design control systems", "Program PLC/DCS systems", "Support system integration", "Troubleshoot issues", "Document designs"], requirements: ["Bachelor's in Engineering", "4+ years automation experience", "PLC programming skills", "DCS experience preferred", "Problem-solving abilities"], benefits: ["Competitive salary", "Health insurance", "Technical training", "Career advancement", "Innovative projects"] },
  { id: 14, title: "MES Implementation Lead", department: "Automation", location: "New Jersey, USA", type: "Full-time", experience: "5+ years", description: "Lead MES implementation projects from design through validation for life sciences clients.", salary: "$130k - $160k", urgent: true, responsibilities: ["Lead MES implementations", "Gather requirements", "Configure MES modules", "Coordinate validation", "Train end users"], requirements: ["Bachelor's degree", "5+ years MES experience", "Knowledge of batch processing", "Project leadership skills", "Client-facing experience"], benefits: ["Executive compensation", "Bonus potential", "Full benefits", "Leadership opportunities", "Industry recognition"] },
  { id: 15, title: "SCADA Systems Engineer", department: "Automation", location: "San Francisco, USA", type: "Full-time", experience: "4+ years", description: "Develop and maintain SCADA systems for batch processing and continuous manufacturing.", salary: "$110k - $140k", urgent: false, responsibilities: ["Develop SCADA applications", "Configure HMI screens", "Implement alarming systems", "Support batch operations", "Maintain documentation"], requirements: ["Bachelor's in Engineering", "4+ years SCADA experience", "WonderWare or Ignition preferred", "Database skills", "GMP knowledge"], benefits: ["Competitive pay", "Tech-forward environment", "Benefits package", "Professional growth", "Team collaboration"] },
  { id: 16, title: "Batch Record Specialist", department: "Automation", location: "Philadelphia, USA", type: "Full-time", experience: "3+ years", description: "Design and implement electronic batch record systems for pharmaceutical manufacturing.", salary: "$90k - $115k", urgent: false, responsibilities: ["Design eBR systems", "Configure master batch records", "Support validation", "Train users", "Improve processes"], requirements: ["Bachelor's degree", "3+ years eBR experience", "Knowledge of batch manufacturing", "System configuration skills", "Attention to detail"], benefits: ["Health insurance", "401(k)", "PTO", "Training", "Career path"] },
  { id: 17, title: "Industrial IoT Engineer", department: "Automation", location: "San Diego, USA", type: "Full-time", experience: "4+ years", description: "Implement IoT solutions for real-time monitoring and predictive maintenance in manufacturing.", salary: "$105k - $135k", urgent: false, responsibilities: ["Implement IoT solutions", "Design sensor networks", "Develop dashboards", "Enable predictive analytics", "Support Industry 4.0"], requirements: ["Bachelor's in Engineering or IT", "4+ years IoT experience", "Cloud platform knowledge", "Data analytics skills", "Innovation mindset"], benefits: ["Cutting-edge technology", "Competitive salary", "Benefits", "Innovation culture", "Growth opportunities"] },
  { id: 18, title: "DCS Configuration Engineer", department: "Automation", location: "New Jersey, USA", type: "Contract", experience: "3+ years", description: "Configure and validate DCS systems including DeltaV, Honeywell, and ABB platforms.", salary: "$95k - $120k", urgent: false, responsibilities: ["Configure DCS systems", "Develop control strategies", "Support FAT/SAT", "Troubleshoot issues", "Document configurations"], requirements: ["Technical degree", "3+ years DCS experience", "DeltaV or Honeywell preferred", "Control theory knowledge", "Pharma experience"], benefits: ["Competitive rate", "Project variety", "Skill development", "Industry exposure", "Flexible engagement"] },
  { id: 19, title: "Serialization Specialist", department: "Automation", location: "Remote", type: "Full-time", experience: "3+ years", description: "Implement track-and-trace and serialization systems for pharmaceutical packaging lines.", salary: "$85k - $110k", urgent: false, responsibilities: ["Implement serialization systems", "Configure aggregation", "Support DSCSA compliance", "Train operators", "Maintain documentation"], requirements: ["Bachelor's degree", "3+ years serialization experience", "Knowledge of DSCSA/FMD", "Packaging line experience", "Technical aptitude"], benefits: ["Remote flexibility", "Health benefits", "Career growth", "Industry expertise", "Training provided"] },
  { id: 20, title: "Control Systems Architect", department: "Automation", location: "Houston, USA", type: "Full-time", experience: "6+ years", description: "Design control system architectures for new pharmaceutical manufacturing facilities.", salary: "$125k - $155k", urgent: true, responsibilities: ["Design system architectures", "Develop specifications", "Lead technical teams", "Review designs", "Ensure standards compliance"], requirements: ["Bachelor's or Master's in Engineering", "6+ years control systems experience", "Architecture design skills", "Multi-platform knowledge", "Leadership abilities"], benefits: ["Senior compensation", "Leadership role", "Full benefits", "High-impact projects", "Professional recognition"] },
  { id: 21, title: "Robotics Integration Engineer", department: "Automation", location: "Boston, USA", type: "Full-time", experience: "4+ years", description: "Integrate robotic systems for aseptic filling and packaging operations.", salary: "$110k - $140k", urgent: false, responsibilities: ["Integrate robotic systems", "Program robot sequences", "Support aseptic operations", "Validate systems", "Train operators"], requirements: ["Bachelor's in Engineering", "4+ years robotics experience", "Fanuc or ABB preferred", "Aseptic environment knowledge", "Problem-solving skills"], benefits: ["Competitive salary", "Advanced technology", "Benefits package", "Career advancement", "Innovative work"] },
  { id: 22, title: "Vision Systems Engineer", department: "Automation", location: "Chicago, USA", type: "Full-time", experience: "3+ years", description: "Implement and validate machine vision systems for pharmaceutical inspection applications.", salary: "$95k - $120k", urgent: false, responsibilities: ["Implement vision systems", "Develop inspection recipes", "Validate systems", "Troubleshoot issues", "Support production"], requirements: ["Bachelor's in Engineering", "3+ years vision experience", "Cognex or Keyence preferred", "Image processing knowledge", "Quality mindset"], benefits: ["Health insurance", "401(k)", "Training", "Technology exposure", "Team environment"] },

  // Quality Department (10 roles)
  { id: 23, title: "Quality Systems Manager", department: "Quality", location: "New Jersey, USA", type: "Full-time", experience: "8+ years", description: "Oversee quality management system implementations and maintain compliance with GMP regulations.", salary: "$140k - $170k", urgent: false, responsibilities: ["Oversee QMS implementation", "Maintain GMP compliance", "Lead quality initiatives", "Manage quality team", "Interface with regulators"], requirements: ["Bachelor's or Master's degree", "8+ years quality experience", "QMS implementation expertise", "Leadership skills", "Regulatory inspection experience"], benefits: ["Executive compensation", "Bonus program", "Full benefits", "Leadership role", "Strategic impact"] },
  { id: 24, title: "Data Integrity Specialist", department: "Quality", location: "Remote", type: "Full-time", experience: "3+ years", description: "Implement ALCOA+ principles and data integrity programs for pharmaceutical clients.", salary: "$85k - $110k", urgent: true, responsibilities: ["Implement DI programs", "Conduct assessments", "Develop remediation plans", "Train staff", "Support audits"], requirements: ["Bachelor's degree", "3+ years DI experience", "ALCOA+ knowledge", "Assessment skills", "Communication abilities"], benefits: ["Remote work", "Health benefits", "Career growth", "Industry expertise", "Training opportunities"] },
  { id: 25, title: "QA Compliance Analyst", department: "Quality", location: "Boston, USA", type: "Full-time", experience: "2+ years", description: "Support quality assurance activities including deviation investigations and CAPA management.", salary: "$70k - $90k", urgent: false, responsibilities: ["Investigate deviations", "Manage CAPAs", "Review documentation", "Support audits", "Track quality metrics"], requirements: ["Bachelor's degree", "2+ years QA experience", "Root cause analysis skills", "GMP knowledge", "Detail-oriented"], benefits: ["Health insurance", "Entry to mid-level role", "Training provided", "Growth path", "Team environment"] },
  { id: 26, title: "Supplier Quality Engineer", department: "Quality", location: "Philadelphia, USA", type: "Full-time", experience: "4+ years", description: "Manage supplier qualification and audit programs for pharmaceutical raw materials and components.", salary: "$95k - $125k", urgent: false, responsibilities: ["Manage supplier programs", "Conduct supplier audits", "Qualify new suppliers", "Review COAs", "Handle supplier issues"], requirements: ["Bachelor's degree", "4+ years supplier quality experience", "Auditing skills", "GMP knowledge", "Negotiation abilities"], benefits: ["Competitive salary", "Travel opportunities", "Benefits package", "Professional development", "Supplier relationships"] },
  { id: 27, title: "Quality Risk Manager", department: "Quality", location: "New Jersey, USA", type: "Full-time", experience: "6+ years", description: "Lead quality risk management activities including FMEA, HACCP, and ICH Q9 implementations.", salary: "$115k - $145k", urgent: false, responsibilities: ["Lead QRM activities", "Facilitate FMEA sessions", "Implement ICH Q9", "Train staff", "Drive risk-based decisions"], requirements: ["Bachelor's or Master's degree", "6+ years QRM experience", "ICH Q9 expertise", "Facilitation skills", "Strategic thinking"], benefits: ["Senior compensation", "Thought leadership", "Benefits", "Industry influence", "Career advancement"] },
  { id: 28, title: "Internal Audit Lead", department: "Quality", location: "San Francisco, USA", type: "Full-time", experience: "5+ years", description: "Develop and execute internal audit programs across quality systems and manufacturing operations.", salary: "$100k - $130k", urgent: false, responsibilities: ["Lead audit program", "Conduct internal audits", "Train auditors", "Track findings", "Report to management"], requirements: ["Bachelor's degree", "5+ years audit experience", "Lead auditor certification", "GMP expertise", "Communication skills"], benefits: ["Competitive pay", "Audit leadership", "Benefits", "Professional certifications", "Travel opportunities"] },
  { id: 29, title: "Batch Record Review Specialist", department: "Quality", location: "Research Triangle, NC", type: "Full-time", experience: "2+ years", description: "Review and approve batch production records ensuring GMP compliance.", salary: "$65k - $85k", urgent: false, responsibilities: ["Review batch records", "Identify discrepancies", "Ensure compliance", "Support release", "Maintain records"], requirements: ["Bachelor's degree", "2+ years review experience", "GMP knowledge", "Attention to detail", "Documentation skills"], benefits: ["Health insurance", "Entry-level opportunity", "Training", "Career path", "Team support"] },
  { id: 30, title: "Quality Control Scientist", department: "Quality", location: "San Diego, USA", type: "Full-time", experience: "3+ years", description: "Perform QC testing and method validation for pharmaceutical products.", salary: "$75k - $100k", urgent: false, responsibilities: ["Perform QC testing", "Validate methods", "Analyze data", "Write reports", "Maintain equipment"], requirements: ["Bachelor's in Chemistry", "3+ years QC experience", "HPLC expertise", "GLP knowledge", "Laboratory skills"], benefits: ["Scientific environment", "Benefits package", "Research opportunities", "Career growth", "Modern lab"] },
  { id: 31, title: "Complaints Investigation Specialist", department: "Quality", location: "Chicago, USA", type: "Full-time", experience: "3+ years", description: "Investigate product complaints and implement corrective actions.", salary: "$80k - $105k", urgent: true, responsibilities: ["Investigate complaints", "Perform root cause analysis", "Implement CAPAs", "Report findings", "Interface with customers"], requirements: ["Bachelor's degree", "3+ years complaint handling experience", "Investigation skills", "Customer focus", "Communication abilities"], benefits: ["Health insurance", "Problem-solving role", "Benefits", "Customer interaction", "Impact on quality"] },
  { id: 32, title: "Quality Metrics Analyst", department: "Quality", location: "Remote", type: "Full-time", experience: "2+ years", description: "Develop and maintain quality metrics dashboards and trending analysis.", salary: "$70k - $90k", urgent: false, responsibilities: ["Develop metrics dashboards", "Analyze trends", "Report KPIs", "Support decisions", "Automate reporting"], requirements: ["Bachelor's degree", "2+ years analytics experience", "Excel/Power BI skills", "Quality metrics knowledge", "Analytical mindset"], benefits: ["Remote work", "Data-driven role", "Health benefits", "Skill development", "Career growth"] },

  // Regulatory Department (8 roles)
  { id: 33, title: "Regulatory Affairs Manager", department: "Regulatory", location: "New Jersey, USA", type: "Full-time", experience: "7+ years", description: "Manage regulatory submissions and ensure compliance with FDA, EMA, and global health authorities.", salary: "$135k - $165k", urgent: false, responsibilities: ["Manage regulatory submissions", "Ensure global compliance", "Lead regulatory team", "Interface with agencies", "Develop strategies"], requirements: ["Bachelor's or advanced degree", "7+ years RA experience", "FDA and EMA expertise", "Leadership skills", "Strategic planning"], benefits: ["Executive compensation", "Leadership role", "Full benefits", "Global exposure", "Career advancement"] },
  { id: 34, title: "CMC Regulatory Specialist", department: "Regulatory", location: "Remote", type: "Full-time", experience: "4+ years", description: "Prepare and review CMC documentation for regulatory submissions across global markets.", salary: "$95k - $125k", urgent: false, responsibilities: ["Prepare CMC sections", "Review documentation", "Support submissions", "Coordinate with teams", "Track requirements"], requirements: ["Bachelor's in Science", "4+ years CMC experience", "Technical writing skills", "Global regulatory knowledge", "Attention to detail"], benefits: ["Remote flexibility", "Health benefits", "Professional growth", "Global projects", "Industry expertise"] },
  { id: 35, title: "Technical Writer - Regulatory", department: "Regulatory", location: "Remote", type: "Full-time", experience: "2+ years", description: "Create regulatory documentation including SOPs, protocols, and submission dossiers.", salary: "$65k - $85k", urgent: false, responsibilities: ["Write technical documents", "Create SOPs", "Support submissions", "Review documents", "Maintain templates"], requirements: ["Bachelor's degree", "2+ years technical writing experience", "Regulatory knowledge", "Strong writing skills", "Detail-oriented"], benefits: ["Remote work", "Writing-focused role", "Health insurance", "Career development", "Training provided"] },
  { id: 36, title: "FDA Submissions Specialist", department: "Regulatory", location: "Boston, USA", type: "Full-time", experience: "4+ years", description: "Prepare and submit FDA regulatory filings including NDAs, ANDAs, and 510(k)s.", salary: "$100k - $130k", urgent: true, responsibilities: ["Prepare FDA submissions", "Compile dossiers", "Track submissions", "Respond to queries", "Maintain records"], requirements: ["Bachelor's degree", "4+ years FDA submission experience", "eCTD knowledge", "Regulatory database skills", "Deadline-driven"], benefits: ["Competitive salary", "FDA expertise", "Benefits package", "Career growth", "Industry recognition"] },
  { id: 37, title: "Labeling Compliance Specialist", department: "Regulatory", location: "Philadelphia, USA", type: "Full-time", experience: "3+ years", description: "Ensure labeling compliance with global regulatory requirements for pharmaceutical products.", salary: "$80k - $105k", urgent: false, responsibilities: ["Review labeling content", "Ensure compliance", "Coordinate artwork", "Track changes", "Support submissions"], requirements: ["Bachelor's degree", "3+ years labeling experience", "Global labeling knowledge", "Attention to detail", "Project coordination"], benefits: ["Health insurance", "Specialized role", "Benefits", "Regulatory expertise", "Team collaboration"] },
  { id: 38, title: "Medical Device Regulatory Specialist", department: "Regulatory", location: "San Diego, USA", type: "Full-time", experience: "4+ years", description: "Support medical device regulatory compliance including 510(k) and PMA submissions.", salary: "$95k - $125k", urgent: false, responsibilities: ["Support device submissions", "Prepare 510(k)s", "Ensure compliance", "Track requirements", "Interface with FDA"], requirements: ["Bachelor's in Engineering or Science", "4+ years device RA experience", "510(k) expertise", "FDA knowledge", "Technical background"], benefits: ["Competitive pay", "Device industry", "Benefits", "Career growth", "Innovative products"] },
  { id: 39, title: "Post-Market Surveillance Specialist", department: "Regulatory", location: "Chicago, USA", type: "Full-time", experience: "3+ years", description: "Manage post-market surveillance and adverse event reporting programs.", salary: "$85k - $110k", urgent: false, responsibilities: ["Manage PMS programs", "Report adverse events", "Analyze safety data", "Ensure compliance", "Support recalls"], requirements: ["Bachelor's degree", "3+ years PMS experience", "Safety reporting knowledge", "Database skills", "Analytical abilities"], benefits: ["Health insurance", "Safety-focused role", "Benefits", "Regulatory impact", "Team environment"] },
  { id: 40, title: "Regulatory Intelligence Analyst", department: "Regulatory", location: "Remote", type: "Full-time", experience: "3+ years", description: "Monitor and analyze regulatory changes impacting pharmaceutical and biotech industries.", salary: "$80k - $105k", urgent: false, responsibilities: ["Monitor regulations", "Analyze changes", "Brief stakeholders", "Track trends", "Support planning"], requirements: ["Bachelor's degree", "3+ years regulatory intelligence experience", "Research skills", "Communication abilities", "Industry knowledge"], benefits: ["Remote work", "Intelligence role", "Health benefits", "Industry insight", "Career development"] },

  // Engineering Department (8 roles)
  { id: 41, title: "Process Engineer", department: "Engineering", location: "San Francisco, USA", type: "Full-time", experience: "3+ years", description: "Design and optimize pharmaceutical manufacturing processes for scale-up and commercialization.", salary: "$95k - $120k", urgent: false, responsibilities: ["Design processes", "Optimize operations", "Support scale-up", "Analyze data", "Document processes"], requirements: ["Bachelor's in ChemE or related", "3+ years process experience", "Manufacturing knowledge", "Problem-solving skills", "Analytical abilities"], benefits: ["Competitive salary", "Engineering role", "Benefits", "Career growth", "Technical challenges"] },
  { id: 42, title: "Commissioning Engineer", department: "Engineering", location: "Houston, USA", type: "Full-time", experience: "4+ years", description: "Lead commissioning activities for new pharmaceutical facilities and equipment installations.", salary: "$100k - $130k", urgent: false, responsibilities: ["Lead commissioning", "Execute protocols", "Coordinate teams", "Manage schedules", "Ensure readiness"], requirements: ["Bachelor's in Engineering", "4+ years commissioning experience", "Project coordination skills", "GMP knowledge", "Leadership abilities"], benefits: ["Competitive pay", "Project leadership", "Benefits", "Career advancement", "Travel opportunities"] },
  { id: 43, title: "Utilities Engineer", department: "Engineering", location: "New Jersey, USA", type: "Full-time", experience: "4+ years", description: "Design and qualify utility systems including WFI, clean steam, HVAC, and compressed gases.", salary: "$95k - $125k", urgent: false, responsibilities: ["Design utility systems", "Qualify installations", "Support operations", "Troubleshoot issues", "Ensure compliance"], requirements: ["Bachelor's in Engineering", "4+ years utilities experience", "WFI system knowledge", "GMP background", "Technical aptitude"], benefits: ["Health insurance", "Technical role", "401(k)", "Professional growth", "Team collaboration"] },
  { id: 44, title: "Packaging Engineer", department: "Engineering", location: "Philadelphia, USA", type: "Full-time", experience: "3+ years", description: "Develop and validate packaging processes for pharmaceutical and medical device products.", salary: "$85k - $110k", urgent: false, responsibilities: ["Develop packaging processes", "Validate systems", "Support operations", "Improve efficiency", "Ensure compliance"], requirements: ["Bachelor's in Engineering", "3+ years packaging experience", "Validation knowledge", "Problem-solving skills", "Detail-oriented"], benefits: ["Competitive salary", "Engineering role", "Benefits", "Career development", "Team environment"] },
  { id: 45, title: "Facility Engineer", department: "Engineering", location: "Research Triangle, NC", type: "Full-time", experience: "5+ years", description: "Support facility maintenance and improvement projects in GMP manufacturing environments.", salary: "$100k - $130k", urgent: false, responsibilities: ["Support facility operations", "Lead improvement projects", "Manage maintenance", "Ensure compliance", "Coordinate contractors"], requirements: ["Bachelor's in Engineering", "5+ years facility experience", "GMP knowledge", "Project management skills", "Budget management"], benefits: ["Senior role", "Health benefits", "401(k)", "Leadership opportunities", "Facility impact"] },
  { id: 46, title: "HVAC Systems Engineer", department: "Engineering", location: "Boston, USA", type: "Full-time", experience: "4+ years", description: "Design and qualify HVAC systems for cleanroom and controlled environments.", salary: "$95k - $125k", urgent: true, responsibilities: ["Design HVAC systems", "Qualify installations", "Support cleanrooms", "Troubleshoot issues", "Ensure compliance"], requirements: ["Bachelor's in ME or related", "4+ years HVAC experience", "Cleanroom knowledge", "BMS experience", "Technical skills"], benefits: ["Competitive pay", "Specialized role", "Benefits", "Career growth", "Technical challenges"] },
  { id: 47, title: "Piping Design Engineer", department: "Engineering", location: "Houston, USA", type: "Full-time", experience: "3+ years", description: "Design high-purity piping systems for pharmaceutical manufacturing facilities.", salary: "$90k - $115k", urgent: false, responsibilities: ["Design piping systems", "Develop P&IDs", "Support installations", "Review designs", "Ensure standards"], requirements: ["Bachelor's in Engineering", "3+ years piping experience", "AutoCAD skills", "High-purity knowledge", "Detail-oriented"], benefits: ["Health insurance", "Design role", "401(k)", "Professional development", "Team collaboration"] },
  { id: 48, title: "Cleanroom Design Specialist", department: "Engineering", location: "San Diego, USA", type: "Full-time", experience: "5+ years", description: "Design cleanroom facilities meeting ISO and GMP classification requirements.", salary: "$110k - $140k", urgent: false, responsibilities: ["Design cleanrooms", "Specify classifications", "Coordinate disciplines", "Support construction", "Ensure compliance"], requirements: ["Bachelor's in Engineering", "5+ years cleanroom experience", "ISO standards knowledge", "Design skills", "GMP expertise"], benefits: ["Senior compensation", "Design leadership", "Benefits", "High-impact projects", "Industry recognition"] },

  // IT Department (6 roles)
  { id: 49, title: "IT Infrastructure Specialist", department: "IT", location: "Remote", type: "Full-time", experience: "4+ years", description: "Manage IT infrastructure supporting GxP systems with focus on security and compliance.", salary: "$100k - $130k", urgent: false, responsibilities: ["Manage IT infrastructure", "Ensure GxP compliance", "Support systems", "Implement security", "Document controls"], requirements: ["Bachelor's in IT or related", "4+ years infrastructure experience", "GxP knowledge", "Security awareness", "Problem-solving skills"], benefits: ["Remote work", "IT role", "Health benefits", "Career growth", "Technical challenges"] },
  { id: 50, title: "Cybersecurity Analyst - Life Sciences", department: "IT", location: "New Jersey, USA", type: "Full-time", experience: "3+ years", description: "Implement cybersecurity controls for pharmaceutical manufacturing and laboratory systems.", salary: "$95k - $125k", urgent: true, responsibilities: ["Implement security controls", "Assess vulnerabilities", "Monitor threats", "Support incidents", "Ensure compliance"], requirements: ["Bachelor's in IT or Cybersecurity", "3+ years security experience", "Life sciences knowledge", "Security certifications", "Analytical skills"], benefits: ["Competitive pay", "Security focus", "Benefits", "Certifications support", "Industry impact"] },
  { id: 51, title: "GxP Systems Administrator", department: "IT", location: "Boston, USA", type: "Full-time", experience: "3+ years", description: "Administer and maintain validated computer systems including LIMS, ERP, and document management.", salary: "$85k - $110k", urgent: false, responsibilities: ["Administer GxP systems", "Maintain validation", "Support users", "Manage changes", "Ensure compliance"], requirements: ["Bachelor's in IT", "3+ years admin experience", "LIMS/ERP knowledge", "GxP understanding", "Problem-solving abilities"], benefits: ["Health insurance", "IT role", "401(k)", "Training", "Career path"] },
  { id: 52, title: "Cloud Solutions Architect", department: "IT", location: "Remote", type: "Full-time", experience: "5+ years", description: "Design and implement cloud-based solutions for life sciences applications with compliance focus.", salary: "$130k - $160k", urgent: false, responsibilities: ["Design cloud solutions", "Implement architectures", "Ensure compliance", "Lead migrations", "Optimize costs"], requirements: ["Bachelor's or Master's in IT", "5+ years cloud experience", "AWS/Azure expertise", "GxP knowledge", "Architecture skills"], benefits: ["Senior compensation", "Remote flexibility", "Benefits", "Technology leadership", "Innovation role"] },
  { id: 53, title: "Database Administrator - Pharma", department: "IT", location: "Philadelphia, USA", type: "Full-time", experience: "4+ years", description: "Manage databases supporting manufacturing and quality systems in validated environments.", salary: "$95k - $120k", urgent: false, responsibilities: ["Manage databases", "Ensure performance", "Support validation", "Implement backups", "Maintain security"], requirements: ["Bachelor's in IT", "4+ years DBA experience", "SQL Server/Oracle expertise", "GxP knowledge", "Performance tuning"], benefits: ["Competitive salary", "DBA role", "Benefits", "Technical growth", "Team environment"] },
  { id: 54, title: "IT Compliance Analyst", department: "IT", location: "Chicago, USA", type: "Full-time", experience: "3+ years", description: "Ensure IT systems compliance with 21 CFR Part 11 and EU Annex 11 requirements.", salary: "$85k - $110k", urgent: false, responsibilities: ["Ensure Part 11 compliance", "Conduct assessments", "Review systems", "Support audits", "Document controls"], requirements: ["Bachelor's degree", "3+ years compliance experience", "Part 11/Annex 11 expertise", "IT background", "Audit skills"], benefits: ["Health insurance", "Compliance role", "401(k)", "Regulatory expertise", "Career development"] },

  // Management (6 roles)
  { id: 55, title: "Project Manager - Validation", department: "Management", location: "Boston, USA", type: "Full-time", experience: "6+ years", description: "Lead cross-functional validation projects from initiation through completion for global clients.", salary: "$125k - $155k", urgent: false, responsibilities: ["Lead validation projects", "Manage teams", "Control budgets", "Interface with clients", "Ensure delivery"], requirements: ["Bachelor's degree", "6+ years PM experience", "PMP certification preferred", "Validation background", "Leadership skills"], benefits: ["Competitive compensation", "Leadership role", "Benefits", "Client exposure", "Career advancement"] },
  { id: 56, title: "Program Director", department: "Management", location: "New Jersey, USA", type: "Full-time", experience: "10+ years", description: "Oversee multiple client programs and drive strategic initiatives for business growth.", salary: "$160k - $200k", urgent: false, responsibilities: ["Oversee programs", "Drive strategy", "Manage relationships", "Lead teams", "Grow business"], requirements: ["Bachelor's or Master's degree", "10+ years leadership experience", "Strategic mindset", "Business development skills", "Industry expertise"], benefits: ["Executive compensation", "Senior leadership", "Full benefits", "Strategic impact", "Equity potential"] },
  { id: 57, title: "Client Success Manager", department: "Management", location: "Remote", type: "Full-time", experience: "5+ years", description: "Build and maintain strategic client relationships, ensuring project success and satisfaction.", salary: "$110k - $140k", urgent: false, responsibilities: ["Manage client relationships", "Ensure satisfaction", "Identify opportunities", "Resolve issues", "Drive renewals"], requirements: ["Bachelor's degree", "5+ years client success experience", "Relationship skills", "Problem-solving abilities", "Industry knowledge"], benefits: ["Remote flexibility", "Client-facing role", "Health benefits", "Career growth", "Impact on success"] },
  { id: 58, title: "Practice Lead - Automation", department: "Management", location: "San Francisco, USA", type: "Full-time", experience: "8+ years", description: "Lead automation practice including team development, methodology, and business development.", salary: "$150k - $180k", urgent: true, responsibilities: ["Lead automation practice", "Develop team", "Define methodology", "Drive business development", "Ensure excellence"], requirements: ["Bachelor's or Master's degree", "8+ years automation experience", "Leadership skills", "Business acumen", "Technical expertise"], benefits: ["Senior compensation", "Practice leadership", "Benefits", "Strategic role", "Industry influence"] },
  { id: 59, title: "Operations Manager", department: "Management", location: "New Jersey, USA", type: "Full-time", experience: "7+ years", description: "Manage day-to-day operations and resource allocation for consulting engagements.", salary: "$130k - $160k", urgent: false, responsibilities: ["Manage operations", "Allocate resources", "Optimize utilization", "Support projects", "Drive efficiency"], requirements: ["Bachelor's degree", "7+ years operations experience", "Resource management skills", "Process improvement mindset", "Leadership abilities"], benefits: ["Competitive salary", "Operations leadership", "Benefits", "Impact on efficiency", "Career advancement"] },
  { id: 60, title: "Business Development Director", department: "Management", location: "Chicago, USA", type: "Full-time", experience: "8+ years", description: "Drive business growth through strategic partnerships and new client acquisition.", salary: "$140k - $180k", urgent: true, responsibilities: ["Drive business growth", "Develop partnerships", "Acquire clients", "Lead proposals", "Achieve targets"], requirements: ["Bachelor's or Master's degree", "8+ years BD experience", "Sales skills", "Industry network", "Strategic thinking"], benefits: ["Executive compensation", "Commission potential", "Benefits", "Growth role", "Industry recognition"] },

  // Training Department (5 roles)
  { id: 61, title: "Training Manager - GxP", department: "Training", location: "New Jersey, USA", type: "Full-time", experience: "6+ years", description: "Develop and deliver GxP training programs for pharmaceutical and biotech professionals.", salary: "$110k - $140k", urgent: false, responsibilities: ["Develop training programs", "Deliver GxP training", "Manage training team", "Assess effectiveness", "Update curricula"], requirements: ["Bachelor's degree", "6+ years training experience", "GxP expertise", "Instructional design skills", "Presentation abilities"], benefits: ["Competitive salary", "Training leadership", "Benefits", "Subject matter expertise", "Career growth"] },
  { id: 62, title: "E-Learning Developer", department: "Training", location: "Remote", type: "Full-time", experience: "3+ years", description: "Create engaging e-learning content for validation and quality training programs.", salary: "$75k - $95k", urgent: false, responsibilities: ["Develop e-learning content", "Design interactive modules", "Use authoring tools", "Support LMS", "Track effectiveness"], requirements: ["Bachelor's degree", "3+ years e-learning experience", "Articulate/Captivate skills", "Instructional design knowledge", "Creativity"], benefits: ["Remote work", "Creative role", "Health benefits", "Skill development", "Work-life balance"] },
  { id: 63, title: "Technical Trainer - Automation", department: "Training", location: "Boston, USA", type: "Contract", experience: "4+ years", description: "Deliver hands-on training for automation systems including PLC, SCADA, and MES platforms.", salary: "$90k - $115k", urgent: false, responsibilities: ["Deliver automation training", "Develop materials", "Conduct hands-on sessions", "Assess competency", "Update content"], requirements: ["Technical degree", "4+ years training experience", "PLC/SCADA expertise", "Presentation skills", "Teaching abilities"], benefits: ["Competitive rate", "Hands-on role", "Skill sharing", "Industry exposure", "Flexible engagement"] },
  { id: 64, title: "Curriculum Developer - Quality Systems", department: "Training", location: "Remote", type: "Full-time", experience: "4+ years", description: "Design comprehensive training curricula for quality management systems.", salary: "$85k - $110k", urgent: false, responsibilities: ["Design curricula", "Develop training materials", "Align with standards", "Evaluate programs", "Update content"], requirements: ["Bachelor's degree", "4+ years curriculum development experience", "Quality systems knowledge", "Instructional design skills", "Writing abilities"], benefits: ["Remote flexibility", "Curriculum leadership", "Health benefits", "Professional development", "Impact on learning"] },
  { id: 65, title: "Training Coordinator", department: "Training", location: "Philadelphia, USA", type: "Full-time", experience: "2+ years", description: "Coordinate training schedules, track certifications, and manage training records.", salary: "$55k - $70k", urgent: false, responsibilities: ["Coordinate training", "Track certifications", "Manage records", "Support logistics", "Generate reports"], requirements: ["Bachelor's degree", "2+ years coordination experience", "Organizational skills", "LMS knowledge", "Attention to detail"], benefits: ["Entry-level opportunity", "Health insurance", "Training", "Career path", "Team environment"] },

  // Consulting (5 roles)
  { id: 66, title: "Senior Consultant - Quality Systems", department: "Consulting", location: "Boston, USA", type: "Full-time", experience: "8+ years", description: "Provide strategic consulting on quality systems design, implementation, and remediation.", salary: "$140k - $175k", urgent: false, responsibilities: ["Provide strategic consulting", "Design quality systems", "Lead implementations", "Remediate issues", "Advise clients"], requirements: ["Bachelor's or Master's degree", "8+ years consulting experience", "Quality systems expertise", "Client management skills", "Strategic thinking"], benefits: ["Senior compensation", "Consulting leadership", "Benefits", "Client variety", "Industry influence"] },
  { id: 67, title: "Consultant - Digital Transformation", department: "Consulting", location: "New Jersey, USA", type: "Full-time", experience: "5+ years", description: "Guide clients through digital transformation initiatives including Industry 4.0 and paperless manufacturing.", salary: "$120k - $150k", urgent: true, responsibilities: ["Guide digital transformation", "Implement Industry 4.0", "Enable paperless operations", "Advise on technology", "Drive change"], requirements: ["Bachelor's degree", "5+ years digital transformation experience", "Industry 4.0 knowledge", "Change management skills", "Technical aptitude"], benefits: ["Competitive salary", "Innovation focus", "Benefits", "Cutting-edge projects", "Career growth"] },
  { id: 68, title: "Associate Consultant", department: "Consulting", location: "Remote", type: "Full-time", experience: "2+ years", description: "Support consulting engagements across validation, quality, and regulatory projects.", salary: "$70k - $90k", urgent: false, responsibilities: ["Support engagements", "Conduct analyses", "Prepare deliverables", "Assist senior consultants", "Build expertise"], requirements: ["Bachelor's degree", "2+ years relevant experience", "Consulting aptitude", "Analytical skills", "Communication abilities"], benefits: ["Remote work", "Consulting career", "Health benefits", "Mentorship", "Growth opportunities"] },
  { id: 69, title: "Principal Consultant - Regulatory", department: "Consulting", location: "San Francisco, USA", type: "Full-time", experience: "10+ years", description: "Lead complex regulatory consulting projects and develop client solutions.", salary: "$160k - $200k", urgent: false, responsibilities: ["Lead regulatory consulting", "Develop solutions", "Manage key clients", "Drive thought leadership", "Mentor consultants"], requirements: ["Advanced degree preferred", "10+ years regulatory experience", "Executive presence", "Business development skills", "Industry reputation"], benefits: ["Executive compensation", "Principal role", "Benefits", "Strategic leadership", "Industry recognition"] },
  { id: 70, title: "Consultant - Data Integrity", department: "Consulting", location: "Research Triangle, NC", type: "Full-time", experience: "5+ years", description: "Advise clients on data integrity programs, remediation, and compliance strategies.", salary: "$110k - $140k", urgent: true, responsibilities: ["Advise on DI programs", "Lead remediations", "Develop strategies", "Conduct assessments", "Train clients"], requirements: ["Bachelor's degree", "5+ years DI experience", "Assessment skills", "Remediation expertise", "Communication abilities"], benefits: ["Competitive salary", "DI expertise", "Benefits", "High-demand skills", "Career advancement"] },
];

// Job Detail Page Component
const JobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [formData, setFormData] = useState({
    jobs_id: jobId || '',
    name: '',
    email: '',
    phone_number: '',
    years_of_experience: '',
    current_role: '',
    preferred_location: '',
    expected_salary_range: '',
    availability: '',
    key_skills_expertise: '',
    linkedIn_profile: '',
    resume: null,
    career_goals_additiona_information: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [resumeFilename, setResumeFilename] = useState('');
  const [resumeFile, setResumeFile] = useState(null);

  // Update formData when jobId changes
  useEffect(() => {
    if (jobId) {
      setFormData(prev => ({ ...prev, jobs_id: jobId }));
    }
  }, [jobId]);

  // Initialize job from location state if available
  const [job, setJob] = useState(location.state?.job || null);
  const [loading, setLoading] = useState(!location.state?.job);

  // Effect to fetch job if not in state (fallback for direct linking)
  useEffect(() => {
    if (!job && jobId) {
      const fetchJob = async () => {
        try {
          // Try to find in static data first as fallback
          const staticJob = jobsData.find(j => j.id === parseInt(jobId));
          if (staticJob) {
            setJob(staticJob);
            setLoading(false);
            return;
          }

          // If not in static, try API
          try {
            const response = await axios.get(`${API}/jobs/${jobId}`);
            if (response.data && (response.data.data || response.data)) {
              setJob(response.data.data || response.data);
            }
          } catch (apiError) {
            console.error("Error fetching job details:", apiError);
          }
        } catch (err) {
          console.error(err);
        } finally {
          setLoading(false);
        }
      };

      // If we have jobsData coming from props or context in future we could use it, 
      // but here we check local static array first then API
      fetchJob();
    } else {
      setLoading(false);
    }
  }, [jobId, job]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#047857] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Job Not Found</h1>
          <a href="/careers" className="text-[#047857] hover:underline">← Back to Careers</a>
        </div>
      </div>
    );
  }

  const getDepartmentColor = (dept) => {
    const colors = {
      'Validation': '#0369a1',
      'Automation': '#047857',
      'Quality': '#7c3aed',
      'Regulatory': '#dc2626',
      'Engineering': '#ea580c',
      'IT': '#0891b2',
      'Management': '#4f46e5',
      'Training': '#059669',
      'Consulting': '#7c3aed'
    };
    return colors[dept] || '#6b7280';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Form validation
    if (!formData.name.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!formData.jobs_id || formData.jobs_id === '') {
      toast.error('Please select a position');
      return;
    }
    if (!formData.resume) {
      toast.error('Please upload your resume');
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = {
        jobs_id: formData.jobs_id,
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone_number: formData.phone_number.trim(),
        years_of_experience: formData.years_of_experience,
        current_role: formData.current_role.trim(),
        preferred_location: formData.preferred_location.trim(),
        expected_salary_range: formData.expected_salary_range.trim(),
        availability: formData.availability.trim(),
        key_skills_expertise: formData.key_skills_expertise.trim(),
        linkedIn_profile: formData.linkedIn_profile.trim(),
        resume: formData.resume,
        career_goals_additiona_information: formData.career_goals_additiona_information.trim()
      };

      const response = await axios.post(`${API}/job_seekers/create`, submitData, {
        timeout: 10000
      });

      if (response.data.err_code === 'valid') {
        setShowApplyModal(false);
        toast({
          title: 'Application submitted',
          description: 'Your application has been submitted successfully.'
        });
        // Reset form
        setFormData({
          jobs_id: jobId || '',
          name: '',
          email: '',
          phone_number: '',
          years_of_experience: '',
          current_role: '',
          preferred_location: '',
          expected_salary_range: '',
          availability: '',
          key_skills_expertise: '',
          linkedIn_profile: '',
          resume: null,
          career_goals_additiona_information: ''
        });
        setResumeFilename('');
        setResumeFile(null);
      } else {
        toast({
          title: 'Submission failed',
          description: response.data?.message || 'Failed to submit application. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      if (error.response?.status === 500 || error.code === 'ECONNABORTED') {
        // Show user-friendly error message
        console.warn('Server error while submitting application');
      }
      toast({
        title: 'Submission failed',
        description: 'Failed to submit application. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setResumeFile(file);
      setUploading(true);
      setUploadError("");
      try {
        const formDataUpload = new FormData();
        formDataUpload.append("post_image", file);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

        const response = await fetch(`${API}/upload_image`, {
          method: "POST",
          body: formDataUpload,
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}`);
        }

        const data = await response.json();
        if (data.err_code === "valid" && data.data && data.data.uploaded_pic_filename) {
          setResumeFilename(data.data.uploaded_pic_filename);
          setFormData(prev => ({ ...prev, resume: data.data.uploaded_pic_filename }));
          // toast.success('Resume uploaded successfully!');
        } else {
          setUploadError("Failed to upload resume. Please try again.");
          setResumeFilename("");
          // toast.error('Failed to upload resume');
        }
      } catch (err) {
        console.error('File upload error:', err);
        if (err.name === 'AbortError') {
          setUploadError("Upload took too long. Please try again.");
        } else if (err.message.includes('500')) {
          setUploadError("Server error. Please try again later.");
        } else {
          setUploadError("Failed to upload resume. Please try again.");
        }
        setResumeFilename("");
        // toast.error('Failed to upload resume');
      } finally {
        setUploading(false);
      }
    }
  };

  const shareJob = () => {
    if (navigator.share) {
      navigator.share({
        title: `${job.title} - United Pharma Technologies`,
        text: `Check out this job opportunity: ${job.title} at United Pharma Technologies`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Job link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/upt-logo-hd.png" alt="UPT Logo" className="h-10 w-auto" />
          </a>
          <div className="flex items-center gap-4">
            <a href="/careers" className="text-gray-600 hover:text-gray-900 flex items-center gap-2">
              <ArrowLeft size={18} />
              All Jobs
            </a>
          </div>
        </div>
      </header>

      {/* Job Header Banner */}
      <div className="bg-gradient-to-r from-[#0d2137] via-[#163352] to-[#0d2137] text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border border-white/20 rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-48 h-48 border border-white/20 rounded-full"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#047857]/20 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto px-6 relative">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span
              className="px-4 py-1.5 rounded-full text-sm font-semibold text-white"
              style={{ backgroundColor: getDepartmentColor(job.department) }}
            >
              {job.department}
            </span>
            {job.urgent && (
              <span className="px-3 py-1 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                URGENT HIRE
              </span>
            )}
            <span className="px-3 py-1 bg-white/20 text-white text-sm rounded-full">
              Job ID: {job.job_id || `UPT-${(job.id || '').toString().padStart(4, '0')}`}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">{job.title}</h1>
          <div className="flex flex-wrap items-center gap-6 text-white/80">
            <div className="flex items-center gap-2">
              <MapPin size={18} />
              <span>{job.location}</span>
            </div>
            <div className="flex items-center gap-2">
              <Briefcase size={18} />
              <span>{job.type}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={18} />
              <span>{job.experience}</span>
            </div>
            <div className="flex items-center gap-2 text-[#10b981] font-semibold">
              <span>{job.salary}</span>
            </div>
          </div>

          {job.tags && (
            <div className="mt-8 pt-6 border-t border-white/10">
              <p className="text-white/60 text-sm mb-3 font-medium uppercase tracking-wider">Required Skills</p>
              <div className="flex flex-wrap gap-2">
                {job.tags.split(',').map((tag, i) => (
                  <span key={i} className="px-3 py-1 bg-white/5 hover:bg-white/10 rounded-lg text-sm font-medium text-white/80 border border-white/10 transition-colors cursor-default">
                    {tag.trim()}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Job Details */}
          <div className="lg:col-span-2 space-y-8">
            {/* About the Role */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#047857]/10 rounded-xl flex items-center justify-center">
                  <FileText size={20} className="text-[#047857]" />
                </div>
                About the Role
              </h2>
              <div className="text-gray-600 leading-relaxed text-lg prose max-w-none">
                {job.summary ? (
                  <div dangerouslySetInnerHTML={{ __html: job.summary }} />
                ) : (
                  <p>{job.description}</p>
                )}
              </div>
            </div>

            {/* Key Responsibilities */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0369a1]/10 rounded-xl flex items-center justify-center">
                  <ClipboardCheck size={20} className="text-[#0369a1]" />
                </div>
                Key Responsibilities
              </h2>
              {typeof job.responsibilities === 'string' ? (
                <div className="prose max-w-none text-gray-600 space-y-3 [&>ul]:space-y-2 [&>ul]:list-disc [&>ul]:pl-5" dangerouslySetInnerHTML={{ __html: job.responsibilities }} />
              ) : (
                <ul className="space-y-3">
                  {(job.responsibilities || []).map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-gray-600">
                      <CheckCircle2 size={20} className="text-[#047857] flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Requirements */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#7c3aed]/10 rounded-xl flex items-center justify-center">
                  <Award size={20} className="text-[#7c3aed]" />
                </div>
                Requirements
              </h2>
              {typeof job.requirements === 'string' ? (
                <div className="prose max-w-none text-gray-600 space-y-3 [&>ul]:space-y-2 [&>ul]:list-disc [&>ul]:pl-5" dangerouslySetInnerHTML={{ __html: job.requirements }} />
              ) : (
                <ul className="space-y-3">
                  {(job.requirements || []).map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-gray-600">
                      <ChevronRight size={20} className="text-[#047857] flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Benefits */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#10b981]/10 rounded-xl flex items-center justify-center">
                  <Star size={20} className="text-[#10b981]" />
                </div>
                Benefits & Perks
              </h2>
              <ul className="grid md:grid-cols-2 gap-3">
                {(job.benefits || [
                  "Competitive salary package",
                  "Comprehensive health insurance",
                  "401(k) with company match",
                  "Professional development",
                  "Flexible work arrangements"
                ]).map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-600 bg-gray-50 px-4 py-3 rounded-xl">
                    <CheckCircle2 size={18} className="text-[#10b981] flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right Column - Apply Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#0369a1] to-[#047857] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Briefcase size={28} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Ready to Apply?</h3>
                <p className="text-gray-500 text-sm mt-2">Join our team of experts</p>
              </div>

              <button
                onClick={() => setShowApplyModal(true)}
                className="w-full py-4 bg-gradient-to-r from-[#047857] to-[#10b981] text-white font-bold rounded-xl hover:shadow-lg hover:shadow-[#047857]/30 transition-all duration-300 flex items-center justify-center gap-2 mb-4"
              >
                <Send size={18} />
                Apply Now
              </button>

              <button
                onClick={shareJob}
                className="w-full py-3 border-2 border-gray-200 text-gray-700 font-semibold rounded-xl hover:border-[#047857] hover:text-[#047857] transition-all flex items-center justify-center gap-2"
              >
                <ExternalLink size={18} />
                Share This Job
              </button>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <h4 className="font-semibold text-gray-900 mb-3">Quick Info</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Posted</span>
                    <span className="font-medium text-gray-900">
                      {job.created_at ? new Date(job.created_at).toLocaleDateString() : 'Recently'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Job ID</span>
                    <span className="font-medium text-gray-900">UPT-{job.id.toString().padStart(4, '0')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Department</span>
                    <span className="font-medium" style={{ color: getDepartmentColor(job.department) }}>{job.department}</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-xs text-gray-400 text-center">
                  Questions? Email us at<br />
                  <a href="mailto:careers@unitedpharmatechnologies.com" className="text-[#047857] hover:underline">
                    careers@unitedpharmatechnologies.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Apply Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowApplyModal(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Apply for Position</h2>
                <button
                  onClick={() => setShowApplyModal(false)}
                  className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <X size={20} className="text-gray-600" />
                </button>
              </div>
              <p className="text-gray-600 mt-2">
                Applying for: {job.title}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#047857] focus:border-transparent outline-none"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#047857] focus:border-transparent outline-none"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#047857] focus:border-transparent outline-none"
                    placeholder="Enter your phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Years of Experience</label>
                  <select
                    value={formData.years_of_experience}
                    onChange={(e) => setFormData({ ...formData, years_of_experience: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#047857] focus:border-transparent outline-none"
                  >
                    <option value="">Select experience</option>
                    <option value="0-1">0-1 years</option>
                    <option value="1-3">1-3 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="5-10">5-10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Current Role</label>
                  <input
                    type="text"
                    value={formData.current_role}
                    onChange={(e) => setFormData({ ...formData, current_role: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#047857] focus:border-transparent outline-none"
                    placeholder="Enter your current role"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Preferred Location</label>
                  <input
                    type="text"
                    value={formData.preferred_location}
                    onChange={(e) => setFormData({ ...formData, preferred_location: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#047857] focus:border-transparent outline-none"
                    placeholder="Enter preferred location"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Salary Range</label>
                  <input
                    type="text"
                    value={formData.expected_salary_range}
                    onChange={(e) => setFormData({ ...formData, expected_salary_range: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#047857] focus:border-transparent outline-none"
                    placeholder="e.g., $80,000 - $100,000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Availability</label>
                  <select
                    value={formData.availability}
                    onChange={(e) => setFormData({ ...formData, availability: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#047857] focus:border-transparent outline-none"
                  >
                    <option value="">Select availability</option>
                    <option value="immediately">Immediately</option>
                    <option value="2-weeks">2 weeks</option>
                    <option value="1-month">1 month</option>
                    <option value="2-months">2 months</option>
                    <option value="3-months">3 months</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Key Skills & Expertise</label>
                <input
                  type="text"
                  value={formData.key_skills_expertise}
                  onChange={(e) => setFormData({ ...formData, key_skills_expertise: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#047857] focus:border-transparent outline-none"
                  placeholder="Enter your key skills and expertise"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">LinkedIn Profile</label>
                <input
                  type="url"
                  value={formData.linkedIn_profile}
                  onChange={(e) => setFormData({ ...formData, linkedIn_profile: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#047857] focus:border-transparent outline-none"
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Resume *</label>
                <div className="space-y-3">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileUpload}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#047857] focus:border-transparent outline-none file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#047857] file:text-white hover:file:bg-[#0369a1]"
                  />
                  {uploading && (
                    <div className="flex items-center gap-2 text-blue-600">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      Uploading resume...
                    </div>
                  )}
                  {uploadError && (
                    <p className="text-red-600 text-sm">{uploadError}</p>
                  )}
                  {resumeFilename && (
                    <p className="text-green-600 text-sm">Resume uploaded: {resumeFilename}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Career Goals & Additional Information</label>
                <textarea
                  rows={4}
                  value={formData.career_goals_additiona_information}
                  onChange={(e) => setFormData({ ...formData, career_goals_additiona_information: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#047857] focus:border-transparent outline-none resize-none"
                  placeholder="Tell us about your career goals and any additional information..."
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowApplyModal(false)}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-[#0369a1] to-[#047857] text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-[#0a1525] py-12">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <a href="/" className="inline-block mb-4">
            <img src="/upt-logo-hd.png" alt="UPT Logo" className="h-10 w-auto mx-auto" />
          </a>
          <p className="text-white/50 text-sm">
            © 2014-{new Date().getFullYear()} United Pharma Technologies. All rights reserved.
          </p>
          <div className="flex justify-center gap-6 mt-4 text-white/40 text-sm">
            <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
            <a href="/terms" className="hover:text-white transition-colors">Terms of Service</a>
            <a href="/careers" className="hover:text-white transition-colors">All Jobs</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Careers Page Component
const CareersPage = () => {
  useEffect(() => {
    const scriptSrc = 'https://jobsapi.ceipal.com/APISource/widget.js';
    const existing = document.querySelector(`script[src="${scriptSrc}"]`);
    if (existing) existing.remove();

    const container = document.getElementById('example-widget-container');
    if (container) container.innerHTML = '';

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.type = 'text/javascript';
    script.async = true;
    script.setAttribute('data-ceipal-api-key', 'cTVkU3hIeGxKYy8ybzhsdWJ3bDNMZz09');
    script.setAttribute('data-ceipal-career-portal-id', 'S3dUMVNKYkRseEdmNHZxNTRPN0VwUT09');
    document.body.appendChild(script);

    return () => {
      script.remove();
      const widgetContainer = document.getElementById('example-widget-container');
      if (widgetContainer) widgetContainer.innerHTML = '';
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <EnterpriseLogo variant="dark" size="small" />
          </a>
          <a
            href="/"
            className="text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
          >
            <ArrowRight size={16} className="rotate-180" />
            Back to Home
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0a1525] via-[#0d2137] to-[#102a3f] py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#0369a1]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#047857]/20 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <p className="text-[#10b981] font-semibold tracking-wider text-sm mb-4 flex items-center gap-2">
              <Briefcase size={16} />
              CAREERS AT UPT
            </p>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6">
              Shape the Future of <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0ea5e9] to-[#10b981]">Life Sciences</span>
            </h1>
            <p className="text-xl text-white/70 mb-8">
              Join our team of experts driving innovation in pharmaceutical validation, automation, and regulatory compliance. Build your career with industry leaders.
            </p>
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2 text-white/80">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                  <Users size={20} className="text-[#10b981]" />
                </div>
                <span>200+ Employees</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                  <Globe2 size={20} className="text-[#10b981]" />
                </div>
                <span>Global Presence</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                  <Award size={20} className="text-[#10b981]" />
                </div>
                <span>Industry Leaders</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ceipal Job Listings Widget */}
      <section className="w-full">
        <div className="w-full" id="example-widget-container"></div>
      </section>

      {/* Why Join Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Why Join UPT?</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { icon: TrendingUp, title: "Career Growth", desc: "Clear advancement paths and continuous learning opportunities" },
              { icon: Handshake, title: "Work-Life Balance", desc: "Flexible arrangements and comprehensive benefits package" },
              { icon: Lightbulb, title: "Innovation", desc: "Work on cutting-edge projects with latest technologies" },
              { icon: Globe2, title: "Global Impact", desc: "Contribute to healthcare improvements worldwide" }
            ].map((item, idx) => (
              <div key={idx} className="p-6 rounded-xl border-2 border-gray-100 hover:border-[#047857]/30 hover:shadow-lg transition-all text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-[#0369a1]/10 to-[#047857]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon size={28} className="text-[#047857]" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#0369a1] to-[#047857]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Don't see the right role?</h2>
          <p className="text-white/80 text-lg mb-8">
            We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
          </p>
          <a
            href="mailto:careers@unitedpharmatechnologies.com"
            className="inline-flex items-center px-8 py-4 bg-white text-[#047857] font-bold rounded-lg hover:shadow-xl transition-all gap-2"
          >
            <Mail size={20} />
            Send Your Resume
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a1525] py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-white/50 text-sm">
            © 2024 United Pharma Technologies. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

// Legal Pages Component
const LegalPage = ({ type }) => {
  const pages = {
    privacy: {
      title: "Privacy Policy",
      lastUpdated: "December 2024",
      content: [
        {
          heading: "Introduction",
          text: "United Pharma Technologies ('UPT', 'we', 'our', or 'us') respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services."
        },
        {
          heading: "Information We Collect",
          text: "We may collect personal information that you voluntarily provide to us when you express interest in our services, participate in activities on our website, or contact us. This may include: name, email address, phone number, company name, job title, and any other information you choose to provide."
        },
        {
          heading: "How We Use Your Information",
          text: "We use the information we collect to: provide, operate, and maintain our services; improve and personalize your experience; communicate with you about our services; send you marketing and promotional communications (with your consent); respond to your inquiries and provide customer support; and comply with legal obligations."
        },
        {
          heading: "Information Sharing",
          text: "We do not sell, trade, or rent your personal information to third parties. We may share your information with trusted service providers who assist us in operating our website and conducting our business, provided they agree to keep this information confidential."
        },
        {
          heading: "Data Security",
          text: "We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet is 100% secure."
        },
        {
          heading: "Your Rights",
          text: "You have the right to access, correct, or delete your personal information. You may also object to or restrict certain processing of your data. To exercise these rights, please contact us at privacy@unitedpharmatech.com."
        },
        {
          heading: "Contact Us",
          text: "If you have questions about this Privacy Policy, please contact us at: United Pharma Technologies, 120 Wood Avenue South, Suite 406, Iselin, NJ 08830, USA. Email: privacy@unitedpharmatech.com"
        }
      ]
    },
    terms: {
      title: "Terms of Service",
      lastUpdated: "December 2024",
      content: [
        {
          heading: "Acceptance of Terms",
          text: "By accessing and using the United Pharma Technologies website and services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services."
        },
        {
          heading: "Services Description",
          text: "United Pharma Technologies provides pharmaceutical consulting, validation services, quality systems support, regulatory affairs assistance, automation solutions, and staffing services to the life sciences industry across the United States."
        },
        {
          heading: "User Responsibilities",
          text: "You agree to use our services only for lawful purposes and in accordance with these Terms. You are responsible for maintaining the confidentiality of any account information and for all activities that occur under your account."
        },
        {
          heading: "Intellectual Property",
          text: "All content on this website, including text, graphics, logos, images, and software, is the property of United Pharma Technologies or its content suppliers and is protected by intellectual property laws. You may not reproduce, distribute, or create derivative works without our express written permission."
        },
        {
          heading: "Limitation of Liability",
          text: "United Pharma Technologies shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of our services. Our total liability shall not exceed the amount paid by you, if any, for accessing our services."
        },
        {
          heading: "Governing Law",
          text: "These Terms shall be governed by and construed in accordance with the laws of the State of New Jersey, United States, without regard to its conflict of law provisions."
        },
        {
          heading: "Changes to Terms",
          text: "We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the new Terms on this page. Your continued use of our services after such modifications constitutes acceptance of the updated Terms."
        }
      ]
    },
    cookies: {
      title: "Cookie Policy",
      lastUpdated: "December 2024",
      content: [
        {
          heading: "What Are Cookies",
          text: "Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners."
        },
        {
          heading: "How We Use Cookies",
          text: "United Pharma Technologies uses cookies to: ensure the proper functioning of our website; analyze website traffic and usage patterns; remember your preferences and settings; and improve your browsing experience."
        },
        {
          heading: "Types of Cookies We Use",
          text: "Essential Cookies: Required for the website to function properly. Analytics Cookies: Help us understand how visitors interact with our website. Preference Cookies: Remember your settings and preferences. Marketing Cookies: Used to deliver relevant advertisements (with your consent)."
        },
        {
          heading: "Managing Cookies",
          text: "Most web browsers allow you to control cookies through their settings. You can set your browser to refuse cookies or delete cookies that have already been set. However, blocking cookies may impact your experience on our website."
        },
        {
          heading: "Third-Party Cookies",
          text: "Some cookies are placed by third-party services that appear on our pages. We do not control these third-party cookies and recommend reviewing their respective privacy policies."
        },
        {
          heading: "Updates to This Policy",
          text: "We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated revision date."
        }
      ]
    },
    accessibility: {
      title: "Accessibility Statement",
      lastUpdated: "December 2024",
      content: [
        {
          heading: "Our Commitment",
          text: "United Pharma Technologies is committed to ensuring digital accessibility for people with disabilities. We continually improve the user experience for everyone and apply relevant accessibility standards."
        },
        {
          heading: "Conformance Status",
          text: "We strive to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards. These guidelines explain how to make web content more accessible for people with disabilities."
        },
        {
          heading: "Accessibility Features",
          text: "Our website includes the following accessibility features: keyboard navigation support; alt text for images; consistent navigation structure; sufficient color contrast; resizable text; clear and simple language; and descriptive link text."
        },
        {
          heading: "Known Limitations",
          text: "While we strive for full accessibility, some content may not yet be fully accessible. We are continuously working to improve accessibility and welcome your feedback."
        },
        {
          heading: "Feedback",
          text: "We welcome your feedback on the accessibility of our website. If you encounter accessibility barriers or have suggestions for improvement, please contact us at accessibility@unitedpharmatech.com or call +1 732-769-8433."
        },
        {
          heading: "Assistive Technologies",
          text: "Our website is designed to be compatible with assistive technologies including screen readers, magnification software, and speech recognition software. We test with common assistive technologies to ensure compatibility."
        }
      ]
    }
  };

  const page = pages[type] || pages.privacy;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#0d2137] py-6">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#0ea5e9] to-[#10b981] rounded-xl flex items-center justify-center">
              <span className="text-white font-black text-lg">U</span>
            </div>
            <span className="text-white font-bold text-xl">UPT</span>
          </a>
          <a href="/" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
            <ArrowLeft size={18} />
            Back to Home
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{page.title}</h1>
            <p className="text-gray-500 mb-8">Last Updated: {page.lastUpdated}</p>

            <div className="space-y-8">
              {page.content.map((section, idx) => (
                <div key={idx}>
                  <h2 className="text-xl font-bold text-gray-900 mb-3">{section.heading}</h2>
                  <p className="text-gray-600 leading-relaxed">{section.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0a1525] py-8">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-white/50 text-sm">
            © 2014-{new Date().getFullYear()} United Pharma Technologies. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};
const HeaderHelmetCode = () => {
  const { siteSettings } = useSiteSettings();
  const logoUrl = siteSettings?.logo || "";
  const faviconUrl = siteSettings?.favicon && siteSettings.favicon.trim() ? siteSettings.favicon : "/favicon.ico";

  return (
    <Helmet>
      <title>{siteSettings?.site_name || 'United Pharma Technologies'}</title>
      <meta name="description" content={siteSettings?.meta_description || 'United Pharma Technologies'} />
      <meta name="keywords" content={siteSettings?.meta_keywords || 'United Pharma Technologies'} />
      <meta property="og:title" content={siteSettings?.site_name || 'United Pharma Technologies'} />
      <meta property="og:description" content={siteSettings?.meta_description || 'United Pharma Technologies'} />
      <meta property="og:image" content={logoUrl} />
      <meta property="og:url" content={`${window.location.origin}`} />
      <meta property="og:type" content="website" />
      {/* favicon - with fallback to default if invalid */}
      <link rel="icon" href={faviconUrl} />
    </Helmet>
  );
}
// App
function App() {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <>
      <SiteSettingsProvider>
        <BrowserRouter>
          <HeaderHelmetCode />
          {/* Scroll to top on route change */}
          <ScrollToTop />

          {/* Page Loader */}
          {isLoading && <PageLoader onComplete={() => setIsLoading(false)} />}

          {/* Custom Cursor */}
          <CustomCursor />

          {/* Helix AI Chatbot - Only show on main pages, not on /helix, /careers, /news, or legal pages */}
          <Routes>
            <Route path="/helix" element={null} />
            <Route path="/careers" element={null} />
            <Route path="/careers/job/:jobId" element={null} />
            <Route path="/news" element={null} />
            <Route path="/privacy" element={null} />
            <Route path="/terms" element={null} />
            <Route path="/cookies" element={null} />
            <Route path="/accessibility" element={null} />
            <Route path="*" element={<HelixChatbot />} />
          </Routes>

          {/* Smooth scroll wrapper */}
          <div className={`${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-500`}>
            <Routes>
              <Route path="/helix" element={<HelixPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/careers/job/:jobId" element={<JobDetailPage />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/privacy" element={<LegalPage type="privacy" />} />
              <Route path="/terms" element={<LegalPage type="terms" />} />
              <Route path="/cookies" element={<LegalPage type="cookies" />} />
              <Route path="/accessibility" element={<LegalPage type="accessibility" />} />
              <Route path="*" element={
                <>
                  <Header />
                  <HeroSection />
                  <IndustriesSection />
                  <EcosystemSection />
                  <ComplianceTransformationSection />
                  <ApproachSection />
                  <CapabilitiesSection />
                  <ServicesSection />
                  <CaseStudiesSection />
                  <AboutSection />
                  <ContactSection />
                  <Footer />
                </>
              } />
            </Routes>
          </div>
        </BrowserRouter>
      </SiteSettingsProvider>
      <Toaster />
    </>
  );
}

export default App;
